import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLayout from '../UI/Layout/AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faChevronLeft, faChevronRight, faHistory,
    faSyncAlt, faChevronDown, faChevronUp, faMapMarkerAlt, faEnvelope,
    faAngleDoubleLeft, faAngleDoubleRight, faLayerGroup,
    faClock, faListUl, faSms, faFileAlt, faIdCard, faBan, faUserShield,
    faPhoneVolume, faComments, faFileInvoiceDollar, faUserCog, faHeadset,
    faPlug, faBook, faCalendarAlt, faUserPlus, faExclamationTriangle,
    faEye, faTimes, faHandshake, faTags, faRoute,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import {
    ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, Cell,
} from 'recharts';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Axios from '../../axios/Axios';
import { userloginDetails } from '../Utils/AdminAuth';

const ACTIVITY_LOG_URL = 'https://napi.authkey.io/api/v1/activityLog';
// Source of truth for "did this signup actually turn into a real account" — the
// activity log's own success/failure text (isCompletedSignupText) is sometimes
// wrong (e.g. the verify-success event never got logged even though the account
// was created), so rows are cross-checked against the real registered-user list
// from this endpoint before being called "Not Completed".
const ADMIN_USER_LIST_URL = 'https://console.authkey.io/api/admin_user_list.php';

// Order matters: entries are matched top-down and the first hit wins, so
// module values that could match more than one pattern (e.g. "whatsapp_optin_optout"
// also containing "optout", or "sms_template_management" also containing "template")
// resolve to the more specific / earlier group instead of the generic one.
// `queryValue` is the lowercase key sent to the API's `module` filter param.
const MODULE_STYLES = [
    { queryValue: 'whatsapp', label: 'WhatsApp', match: /whatsapp|whastapp/i, bg: '#ecfdf5', color: '#0d9488', border: '#99f6e4', icon: faWhatsapp },
    { queryValue: 'sms', label: 'SMS', match: /sms/i, bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', icon: faSms },
    { queryValue: 'voice', label: 'Voice & TTS', match: /voice|flash_call|tts/i, bg: '#eef2ff', color: '#4f46e5', border: '#c7d2fe', icon: faPhoneVolume },
    { queryValue: 'rcs', label: 'RCS', match: /rcs/i, bg: '#fdf4ff', color: '#c026d3', border: '#f5d0fe', icon: faComments },
    { queryValue: 'email', label: 'Email', match: /email|^aws|aws_/i, bg: '#ecfeff', color: '#0891b2', border: '#a5f3fc', icon: faEnvelope },
    { queryValue: 'entity', label: 'Entity Management', match: /entity/i, bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', icon: faIdCard },
    { queryValue: 'template', label: 'Template Management', match: /template/i, bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe', icon: faFileAlt },
    { queryValue: 'billing', label: 'Billing & Payments', match: /billing|transaction/i, bg: '#fffbeb', color: '#d97706', border: '#fde68a', icon: faFileInvoiceDollar },
    { queryValue: 'signup', label: 'Signup', match: /signup/i, bg: '#fdf2f8', color: '#db2777', border: '#fbcfe8', icon: faUserPlus },
    { queryValue: 'login', label: 'Login & Security', match: /login|two_factor|change_password/i, bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', icon: faUserShield },
    { queryValue: 'profile', label: 'Profile & Notifications', match: /profile|notification/i, bg: '#f8fafc', color: '#334155', border: '#e2e8f0', icon: faUserCog },
    { queryValue: 'ticket', label: 'Support', match: /ticket/i, bg: '#fff1f2', color: '#e11d48', border: '#fecdd3', icon: faHeadset },
    { queryValue: 'integration', label: 'Integrations & Widgets', match: /integration|widget|qr/i, bg: '#f0f9ff', color: '#0284c7', border: '#bae6fd', icon: faPlug },
    { queryValue: 'api_docs', label: 'API Docs', match: /api_docs/i, bg: '#fafaf9', color: '#57534e', border: '#e7e5e4', icon: faBook },
    { queryValue: 'optout', label: 'Optout', match: /optout/i, bg: '#fef2f2', color: '#dc2626', border: '#fecaca', icon: faBan },
];

const DEFAULT_MODULE_STYLE = { label: 'Other', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', icon: faLayerGroup };

const getModuleStyle = (module) => {
    const found = MODULE_STYLES.find((m) => m.match.test(module || ''));
    return found || DEFAULT_MODULE_STYLE;
};

// The raw activity log module for every message-send action (SMS, WhatsApp, Voice, Email,
// RCS) is the literal string "message" — it doesn't match any of the resolved MODULE_STYLES
// categories above (those are keyed off channel-specific module strings like "sms"/"whatsapp",
// which the send-action rows don't use), so it's checked directly against the raw module here.
const isMessagingRow = (row) => (row.module || '').toString().trim().toLowerCase() === 'message';

// A "message" module row is NOT the same thing as a message actually going out — e.g. a
// send_email row can log message: "Nothing To Do" (nothing was actually sent) right next
// to others logging "Submitted Successfully" (SMS rows even carry it as a literal
// quoted string, "\"Submitted Successfully\"", which this substring match still catches).
// Same convention as isCompletedSignupText/isSuccessfulLoginText elsewhere in this file:
// success/failure lives in the message text, not a separate flag.
const isMessageSentText = (row) => /submitted successfully|success/i.test(row.message || '');

// A Billing-module row's own message is one of three literal texts the API sends for a
// payment attempt — "Payment Success", "Payment Try", "Payment Failed" — same message-
// text convention as isCompletedSignupText/isSuccessfulLoginText elsewhere in this file,
// but here matched against exact known strings rather than a generic keyword set (unlike
// signup/login, whose free-text messages vary). Used by both the New Users and All Users
// Funnel Stage Breakdown tabs' Payment stage. Each of the three subrows counts ONLY rows
// matching its own exact text — a user whose Billing row is neither Success, Try, nor
// Failed (including a user with no Billing row at all) isn't counted in any of the three,
// rather than being lumped into "Tried Payment" as a catch-all.
const isPaymentSuccessText = (row) => /payment\s*success/i.test(row.message || '');
const isPaymentTriedText = (row) => /payment\s*try/i.test(row.message || '');
const isPaymentFailedText = (row) => /payment\s*failed/i.test(row.message || '');

// Every channel logs the same generic event (test_message_button) under the "message"
// module, so `page` (e.g. "send_sms") is the only field that says which channel a given
// message actually went out on.
const MESSAGE_CHANNEL_PATTERNS = [
    { match: /send_whatsapp|whatsapp/i, label: 'WhatsApp' },
    { match: /send_sms|sms/i, label: 'SMS' },
    { match: /send_voice|voice/i, label: 'Voice' },
    { match: /send_email|email/i, label: 'Email' },
    { match: /send_rcs|rcs/i, label: 'RCS' },
];
const getMessageChannel = (page) => {
    const found = MESSAGE_CHANNEL_PATTERNS.find((p) => p.match.test(page || ''));
    return found ? found.label : 'Other';
};

// Zero-filled per-channel counts (so a channel with no messages still shows "0" instead of
// being missing entirely) for the Messaging stage's Detailed Preview channel breakdown.
const buildChannelBreakdown = (rows) => {
    const counts = new Map(MESSAGE_CHANNEL_PATTERNS.map((p) => [p.label, 0]));
    rows.forEach((r) => counts.set(r.channel, (counts.get(r.channel) || 0) + 1));
    return Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
};

// "All Modules" plus one entry per resolved category, used to drive both the
// module filter dropdown and the `module` param sent to the API.
const MODULE_FILTER_OPTIONS = [
    { value: 'all', label: 'All Modules' },
    ...MODULE_STYLES.map((m) => ({ value: m.queryValue, label: m.label })),
];

// Curated per-module event catalog — the API has no "list distinct events"
// endpoint, so this mirrors the known tracked actions per module/feature area.
// Values are what gets sent as the `event` filter param.
const MODULE_EVENT_MAP = {
    whatsapp: [
        { value: 'whatsapp_onboarding', label: 'WhatsApp Onboarding' },
        { value: 'whatsapp_campaign', label: 'WhatsApp Campaign' },
        { value: 'whatsapp_optin_optout', label: 'WhatsApp Opt-in / Opt-out' },
        { value: 'whatsapp_template', label: 'WhatsApp Template' },
    ],
    sms: [
        { value: 'sms_send', label: 'SMS Send' },
        { value: 'sms_campaign', label: 'SMS Campaign' },
        { value: 'sms_template_management', label: 'SMS Template Management' },
        { value: 'sms_optout', label: 'SMS Optout' },
    ],
    voice: [
        { value: 'voice_call', label: 'Voice Call' },
        { value: 'flash_call', label: 'Flash Call' },
        { value: 'tts_call', label: 'Text-to-Speech Call' },
    ],
    rcs: [
        { value: 'rcs_apply', label: 'RCS Apply' },
        { value: 'rcs_onboarding', label: 'RCS Onboarding' },
        { value: 'rcs_campaign', label: 'RCS Campaign' },
        { value: 'rcs_template', label: 'RCS Template' },
    ],
    email: [
        { value: 'email_send', label: 'Email Send' },
        { value: 'email_campaign', label: 'Email Campaign' },
        { value: 'email_template', label: 'Email Template' },
        { value: 'email_verification', label: 'Email Verification' },
        { value: 'email_detail_report', label: 'Email Detail Report' },
        { value: 'email_report', label: 'Email Report' },
        { value: 'aws_setup', label: 'AWS Setup' },
        { value: 'aws_from_email', label: 'AWS From Email' },
    ],
    entity: [
        { value: 'entity_id', label: 'Entity ID' },
        { value: 'entity_management', label: 'Entity Management' },
    ],
    template: [
        { value: 'template_management', label: 'Template Management' },
        { value: 'dlt_template', label: 'DLT Template' },
    ],
    billing: [
        { value: 'billing_history', label: 'Billing History' },
        { value: 'transaction_history', label: 'Transaction History' },
        { value: 'transaction_payment', label: 'Transaction Payment' },
    ],
    signup: [
        { value: 'signup', label: 'Signup' },
    ],
    login: [
        { value: 'login', label: 'Login' },
        { value: 'two_factor_auth', label: 'Two-Factor Auth' },
        { value: 'change_password', label: 'Change Password' },
    ],
    profile: [
        { value: 'profile_update', label: 'Profile Update' },
        { value: 'notification_status', label: 'Notification Status' },
        { value: 'view_all_notifications', label: 'View All Notifications' },
    ],
    ticket: [
        { value: 'support_ticket', label: 'Support Ticket' },
    ],
    integration: [
        { value: 'integration_configure', label: 'Configure Integration' },
        { value: 'widget', label: 'Widget' },
        { value: 'qr_code', label: 'QR Code' },
    ],
    api_docs: [
        { value: 'api_docs_view', label: 'API Docs View' },
    ],
    optout: [
        { value: 'optout', label: 'Optout' },
    ],
};

const ALL_EVENT_OPTIONS = Object.values(MODULE_EVENT_MAP).flat();

// Event options narrow to the selected module's known events; "All Modules"
// (or no module) shows the full catalog.
const getEventOptions = (moduleValue) => {
    const base = !moduleValue || moduleValue === 'all' ? ALL_EVENT_OPTIONS : (MODULE_EVENT_MAP[moduleValue] || []);
    return [{ value: 'all', label: 'All Events' }, ...base];
};

const emptyState = () => ({ data: [], currentPage: 1, totalPages: 1, totalRecords: 0, pageSize: 0, hasNextPage: false, hasPrevPage: false });

// The API returns data pre-grouped by its own raw `module` string, each with a
// nested `events` array. Flatten that into one row per event — the row's own
// `module` still carries the raw group label, which getModuleStyle() then maps
// onto our resolved categories (WhatsApp/SMS/etc.) for the side-by-side tables.
const flattenLogGroups = (groups) => {
    if (!Array.isArray(groups)) return [];
    return groups.flatMap((group) =>
        (group.events || []).map((evt) => ({
            module: group.module,
            event: evt.event,
            message: evt.message,
            ip: evt.ip,
            page: evt.page,
            created_at: evt.created_at,
            user_id: evt.user_id,
            username: evt.username,
            added_by: evt.added_by,
            email: evt.email,
            user_type: evt.user_type,
            // Signup rows additionally carry a nested `data` object (mobile/email/name/
            // source captured at signup time) plus plan/country/city/parent/parent_type
            // — no other module populates these, so they only ever show up in the Signup
            // stage's Detailed Records modal (signupCompletionSplit below).
            data: evt.data,
            plan: evt.plan,
            country: evt.country,
            city: evt.city,
            parent: evt.parent,
            parent_type: evt.parent_type,
        }))
    );
};

// Fetching the full date-range result set (across pages) so "how many X happened
// today" is an accurate count, not just whatever fits on the current table page.
// Capped so a huge, unbounded range can't trigger hundreds of parallel requests.
const MAX_STAT_PAGES = 20;

// Rows-per-page for the Funnel Stage Breakdown "Preview" modal's detail table.
const STAGE_PREVIEW_PAGE_SIZE = 20;
const STAGE_PREVIEW_PAGE_SIZE_OPTIONS = [20, 50, 100];

// Rows-per-page choices for the per-module cards grid (Activity by Module section),
// which is fed by its own dedicated fetchLogs call rather than the main table's page data.
const MODULE_CARDS_LIMIT_OPTIONS = [10, 25, 50, 100];
const DEFAULT_MODULE_CARDS_LIMIT = 25;

const emptyModuleCardsState = () => ({ data: [], currentPage: 1, totalPages: 1, totalRecords: 0, loading: false });

// Shared "1 ... 4 5 6 ... 20" page-number list builder, used by both the main table's
// pagination bar and the module cards grid's pagination bar.
const buildPageList = (currentPage, totalPages) => {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
        .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
            acc.push(p);
            return acc;
        }, []);
};

const formatDateParam = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const DATE_PRESETS = [
    { key: 'today', label: 'Today', range: () => { const t = startOfDay(new Date()); return [t, t]; } },
    { key: 'yesterday', label: 'Yesterday', range: () => { const t = startOfDay(new Date()); const y = new Date(t); y.setDate(y.getDate() - 1); return [y, y]; } },
    { key: 'last7', label: 'Last 7 Days', range: () => { const t = startOfDay(new Date()); const s = new Date(t); s.setDate(s.getDate() - 6); return [s, t]; } },
    { key: 'last30', label: 'Last 30 Days', range: () => { const t = startOfDay(new Date()); const s = new Date(t); s.setDate(s.getDate() - 29); return [s, t]; } },
    { key: 'all', label: 'All Time', range: () => [null, null] },
];

// Threshold buckets for "how fast did this signup turn into a login" — each bucket's
// `ms` is the max allowed gap between signup_at and first login_at for a conversion to
// count under it. 'all' has no ceiling, so it always matches every converted row. This
// filter layers on top of whatever date range is already selected above (it only ever
// looks at rows already present in conversionStats, which itself is built from
// filteredRangeData), so it stays "dynamic for the date" automatically rather than
// pinning to a fixed date.
const SIGNUP_LOGIN_TIME_BUCKETS = [
    { key: 'all', label: 'All Time', ms: null },
    { key: '5m', label: 'Within 5 Min', ms: 5 * 60 * 1000 },
    { key: '15m', label: 'Within 15 Min', ms: 15 * 60 * 1000 },
    { key: '30m', label: 'Within 30 Min', ms: 30 * 60 * 1000 },
    { key: '1h', label: 'Within 1 Hr', ms: 60 * 60 * 1000 },
    { key: '3h', label: 'Within 3 Hr', ms: 3 * 60 * 60 * 1000 },
    { key: '6h', label: 'Within 6 Hr', ms: 6 * 60 * 60 * 1000 },
    { key: '12h', label: 'Within 12 Hr', ms: 12 * 60 * 60 * 1000 },
    { key: '1d', label: 'Within 1 Day', ms: 24 * 60 * 60 * 1000 },
    { key: '3d', label: 'Within 3 Days', ms: 3 * 24 * 60 * 60 * 1000 },
    { key: '4d', label: 'Within 4 Days', ms: 4 * 24 * 60 * 60 * 1000 },
];

// Human-readable "2 hr 5 min" / "3 days" style label for a single row's exact
// signup→login gap, shown in the Time to Convert column (coarser than the bucket
// filter above, which only needs a ceiling to compare against).
const formatExactDuration = (ms) => {
    if (ms == null || ms < 0) return '—';
    const minutes = Math.round(ms / 60000);
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    if (hours < 24) return remMinutes ? `${hours} hr ${remMinutes} min` : `${hours} hr`;
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return remHours ? `${days}d ${remHours}h` : `${days}d`;
};

// Rows are keyed by user_id when present (the reliable identifier once an account
// exists); rows with no user_id fall back to email (`username`), since some
// early-funnel rows (e.g. signup, before an account is fully created) are logged
// without a user_id.
const funnelRowKey = (row) => {
    if (row.user_id) return `uid:${row.user_id}`;
    const email = (row.username || '').toString().trim().toLowerCase();
    return email ? `email:${email}` : null;
};

// Generic "did the same user show up in step A and later in step B" funnel, shared
// by Signup→Login and Login→WhatsApp Onboarding. `isStepA`/`isStepB` decide which
// rows count as each step; matching a whole funnel *step* rather than one exact
// event string, since raw event text varies a lot in this data.
const buildFunnelStats = (rows, isStepA, isStepB) => {
    const stepAUsers = new Map(); // key -> earliest step-A row
    const stepBUsers = new Map(); // key -> earliest step-B row

    rows.forEach((row) => {
        const key = funnelRowKey(row);
        if (!key) return;

        if (isStepA(row)) {
            const existing = stepAUsers.get(key);
            if (!existing || (row.created_at || '') < (existing.created_at || '')) {
                stepAUsers.set(key, row);
            }
        }
        if (isStepB(row)) {
            const existing = stepBUsers.get(key);
            if (!existing || (row.created_at || '') < (existing.created_at || '')) {
                stepBUsers.set(key, row);
            }
        }
    });

    const converted = [];
    stepAUsers.forEach((aRow, key) => {
        const bRow = stepBUsers.get(key);
        if (bRow) {
            converted.push({
                key,
                username: aRow.username || bRow.username,
                a_user_id: aRow.user_id,
                b_user_id: bRow.user_id,
                a_at: aRow.created_at,
                b_at: bRow.created_at,
                a_ip: aRow.ip,
                b_ip: bRow.ip,
            });
        }
    });
    converted.sort((a, b) => (a.a_at || '').localeCompare(b.a_at || ''));

    const notConverted = [];
    stepAUsers.forEach((aRow, key) => {
        if (stepBUsers.has(key)) return;
        notConverted.push({
            key,
            username: aRow.username,
            a_user_id: aRow.user_id,
            a_at: aRow.created_at,
            a_ip: aRow.ip,
        });
    });
    notConverted.sort((a, b) => (b.a_at || '').localeCompare(a.a_at || ''));

    const totalStepA = stepAUsers.size;
    const totalConverted = converted.length;

    return {
        totalStepA,
        totalConverted,
        conversionRate: totalStepA > 0 ? (totalConverted / totalStepA) * 100 : 0,
        conversions: converted,
        notConverted,
    };
};

// Generic "count of rows per distinct value" grouping, used by the Employee/Reseller/
// Location/Page/Plan signup breakdown cards — keyFn returning a falsy value drops that
// row instead of grouping it under an empty-string bucket. Sorted highest-count first.
const buildCountBreakdown = (rows, keyFn) => {
    const counts = new Map();
    rows.forEach((row) => {
        const key = keyFn(row);
        if (!key) return;
        counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);
};

// ── Sequential "happy path" funnel (Signup → Login → WhatsApp Onboarding →
// Messaging → Payment). Unlike buildFunnelStats above (which measures each
// pair independently against the full signup base), each stage here narrows
// down the PREVIOUS stage's surviving cohort, so counts are strictly
// non-increasing left to right — a classic funnel chart.
const parseLogDate = (str) => {
    if (!str) return null;
    const d = new Date(String(str).replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
};

const buildInitialCohort = (rows, isStepA) => {
    const map = new Map();
    rows.forEach((row) => {
        if (!isStepA(row)) return;
        const key = funnelRowKey(row);
        if (!key) return;
        const existing = map.get(key);
        if (!existing || (row.created_at || '') < (existing.at || '')) {
            // `email` falls back to `username` since the two are the same address on
            // most rows — but on a failed/dropped attempt (exactly what the Reason for
            // Failure view shows) `username` is often null while `email` still has the
            // address, so carrying the raw `row.email` through is what makes that view
            // show anything at all for those rows.
            map.set(key, { key, username: row.username, email: row.email || row.username, user_id: row.user_id, ip: row.ip, at: row.created_at });
        }
    });
    return Array.from(map.values());
};

// Narrows `cohortEntries` down to the subset that also has a row matching
// `isStepB`, keeping the previous stage's timestamp (`prevAt`) alongside the
// new one so a duration-to-convert can be computed per stage.
const narrowCohort = (rows, cohortEntries, isStepB) => {
    const stepBRows = new Map();
    rows.forEach((row) => {
        if (!isStepB(row)) return;
        const key = funnelRowKey(row);
        if (!key) return;
        const existing = stepBRows.get(key);
        if (!existing || (row.created_at || '') < (existing.created_at || '')) {
            stepBRows.set(key, row);
        }
    });

    const survivors = [];
    const dropped = [];
    cohortEntries.forEach((entry) => {
        const match = stepBRows.get(entry.key);
        if (match) {
            survivors.push({
                key: entry.key, username: entry.username || match.username,
                email: entry.email || match.email || match.username,
                user_id: match.user_id ?? entry.user_id, ip: match.ip,
                prevAt: entry.at, at: match.created_at,
            });
        } else {
            dropped.push(entry);
        }
    });
    return { survivors, dropped };
};

// Buckets a stage's median time-to-convert into a human-readable range —
// mirrors the "0 Hrs / < 5 Min / 1-3 Days" style duration column.
const formatDurationBucket = (hours) => {
    if (hours == null) return '—';
    if (hours < 1) return '< 1 Hour';
    if (hours < 24) return `${Math.max(1, Math.round(hours))} Hrs`;
    const days = hours / 24;
    if (days < 3) return '1-3 Days';
    if (days < 7) return '3-7 Days';
    if (days < 14) return '7-14 Days';
    return '14+ Days';
};

const medianDurationHours = (survivors) => {
    const diffs = survivors
        .map((s) => {
            const a = parseLogDate(s.prevAt);
            const b = parseLogDate(s.at);
            if (!a || !b) return null;
            return (b - a) / 3600000;
        })
        .filter((v) => v != null && v >= 0)
        .sort((a, b) => a - b);
    if (!diffs.length) return null;
    const mid = Math.floor(diffs.length / 2);
    return diffs.length % 2 ? diffs[mid] : (diffs[mid - 1] + diffs[mid]) / 2;
};

const STAGE_STATUS_COLOR = { success: '#16a34a', tried: '#2563eb', dropoff: '#dc2626', total: '#64748b' };
const STAGE_STATUS_LABEL = { success: 'Success', tried: 'Tried', dropoff: 'Drop-off', total: 'Total' };
// Light pill backgrounds paired with STAGE_STATUS_COLOR — same palette the
// Funnel Stage Breakdown legend swatches already use for each status.
const STAGE_STATUS_BG = { success: '#f0fdf4', tried: '#eff6ff', dropoff: '#fef2f2', total: '#f1f5f9' };

// A row landing in the "Signup" module is NOT the same thing as a completed signup —
// the raw log captures every step of the flow, including ones that never produce an
// account: weak/mismatched password on "create Account For captcha", invalid
// name/mobile on "user Signup", and a rejected/forbidden verify-account attempt. Only
// a message reading as complete — the word "signup" followed by "successful"/
// "successfull"/"complete" (e.g. "Signup Complete", "signup successfull"), or the exact
// "User Created Successfully" text — means the account actually exists — every other
// signup-module row, whatever its message says, is a real signup that did NOT complete.
// A bare "success"/"complete" anywhere in the message used to be enough, which was too
// loose and could match unrelated text; requiring "signup" alongside it (or the specific
// "User Created Successfully" message) narrows this back down.
// Classification is on MESSAGE TEXT ONLY, not the event name: this used to also require
// row.event to contain "verify" (matching the "Verify Account" event Signup.js logs in
// the frontend source) — but the activityLog API's own `event` value for these rows
// doesn't reliably come back as that exact string, so real completions (message =
// "Signup Complete") were still failing the combined check and falling into Not
// Completed. The message text alone is the reliable signal here.
const isCompletedSignupText = (row) => {
    const message = row.message || '';
    return /signup.*(successfull?|complete)/i.test(message) || /user created successfully/i.test(message);
};

// Same convention as isCompletedSignupText above: a row landing in the WhatsApp
// module with an onboarding-flavored EVENT NAME (raw catalog value
// "whatsapp_onboarding") only means the flow was entered, not finished — every
// other stage of that flow logs under the same module/event too. Only a message
// that actually reads as complete ("WhatsApp Onboarding Complete") confirms the
// onboarding itself went through.
const isCompletedWhatsappOnboardingText = (row) => {
    const message = row.message || '';
    return /whatsapp onboarding complete/i.test(message);
};

// Domain portion of a row's email, lowercased — used by lostSignupsByDomain (see
// below) to split "Potential Customers Lost" into Gmail vs. every other domain.
const emailDomainOf = (row) => {
    const raw = (row.email || row.username || '').toString().trim().toLowerCase();
    const at = raw.lastIndexOf('@');
    return at === -1 ? '' : raw.slice(at + 1);
};

// One line per milestone in the "Funnel Activity Trend" chart (see funnelTrendData
// below) — `key` doubles as the dataKey/series name and the Legend/Tooltip label.
const FUNNEL_TREND_SERIES = [
    { key: 'Signup', color: '#db2777' },
    { key: 'Login', color: '#16a34a' },
    { key: 'WA Onboarding', color: '#0d9488' },
    { key: 'Messaging', color: '#4f46e5' },
    { key: 'Payment', color: '#d97706' },
];

// Custom multi-series tooltip for the Funnel Activity Trend chart — a bolded date
// header followed by one colored-dot row per series, matching the rest of the
// page's modal/table styling instead of recharts' plain default tooltip box.
const FunnelTrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const dt = parseLogDate(label);
    const dateLabel = dt ? dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : label;
    return (
        <div style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px',
            padding: '10px 14px', minWidth: '180px',
            boxShadow: '0 8px 24px rgba(15,23,42,0.14)', fontFamily: 'Inter, system-ui, sans-serif',
        }}>
            <div style={{
                fontSize: '11px', fontWeight: '700', color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px',
            }}>
                {dateLabel}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {payload.map((p) => (
                    <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                        <span style={{ color: '#334155', flex: 1 }}>{p.dataKey}</span>
                        <span style={{ fontWeight: '700', color: '#1a1a2e' }}>{Number(p.value).toLocaleString()}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const Useractivitylog = () => {
    const history = useHistory();
    const lStorage = userloginDetails();
    const adminToken = lStorage?.token;
    const userid = lStorage?.user?.userId

    const [dateRange, setDateRange] = useState(DATE_PRESETS[0].range()); // defaults to Today
    const [startDate, endDate] = dateRange;
    const [rangeStats, setRangeStats] = useState({ loading: false, rows: [], truncated: false });

    const [selectedModule, setSelectedModule] = useState(null); // null = All Modules
    const [selectedEvent, setSelectedEvent] = useState(null); // null = All Events

    // Event options narrow down as soon as a module is picked.
    const eventOptions = useMemo(() => getEventOptions(selectedModule?.value), [selectedModule]);

    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState({ status: false, message: '' });
    const [expandedRow, setExpandedRow] = useState(null);
    const [stagePreview, setStagePreview] = useState(null); // { stageLabel, stageIcon, subrows } | null
    const [stagePreviewTab, setStagePreviewTab] = useState(0);
    const [stagePreviewPage, setStagePreviewPage] = useState(1);
    const [stagePreviewPageSize, setStagePreviewPageSize] = useState(STAGE_PREVIEW_PAGE_SIZE);
    const [stagePreviewChannelFilter, setStagePreviewChannelFilter] = useState(null); // channel label | null = all — "By Channel" chips in the Messaging preview
    const [reasonPreview, setReasonPreview] = useState(null); // { stageLabel, stageIcon, subrowLabel, status, rows } | null
    const [userAttemptsPreview, setUserAttemptsPreview] = useState(null); // { username, user_id, stageLabel, detailColumns, rows } | null
    // "Preview" button on each Overall Stats card (Overall Stats itself, or any of the
    // Employee/Reseller/Location/Page/Plan breakdown cards) opens this SAME modal — every
    // card's button is just a different entry point into the one filterable detail view,
    // not a separately pre-scoped one.
    const [signupDetailOpen, setSignupDetailOpen] = useState(false);
    const [signupDetailFilters, setSignupDetailFilters] = useState({ employee: '', reseller: '', country: '', page: '', plan: '' });
    const [signupDetailPage, setSignupDetailPage] = useState(1);
    const [signupDetailPageSize, setSignupDetailPageSize] = useState(STAGE_PREVIEW_PAGE_SIZE);
    const [logState, setLogState] = useState(emptyState());
    const [funnelBreakdownTab, setFunnelBreakdownTab] = useState('new'); // 'new' | 'all' — Funnel Stage Breakdown tab
    const [lostCustomersTab, setLostCustomersTab] = useState('personal'); // 'personal' | 'gmail' — Potential Customers Lost card tab
    const [signupLoginTimeBucket, setSignupLoginTimeBucket] = useState('all'); // key into SIGNUP_LOGIN_TIME_BUCKETS

    // Dedicated data source for the per-module cards grid (Voice & TTS / RCS / Billing &
    // Payments / ...) — fetched via its own fetchLogs call (always module: 'all', event:
    // 'all') rather than sharing the main table's single-page fetchLog data, so the cards
    // can be paginated/limited independently of the Module & Event filters above.
    const [moduleCardsState, setModuleCardsState] = useState(emptyModuleCardsState());
    const [moduleCardsLimit, setModuleCardsLimit] = useState(DEFAULT_MODULE_CARDS_LIMIT);

    // Ground-truth registered-user lookup (by email and by user id), fetched once
    // from admin_user_list.php, used to verify whether a "signup" log row that
    // looks incomplete actually has a real account behind it.
    const [registeredUsers, setRegisteredUsers] = useState({ loading: false, byEmail: new Map(), byId: new Map() });

    // True while ANY of the page's data fetches are in flight — the main table
    // (loading), the full-range dashboard stats (rangeStats.loading), or the
    // per-module cards grid (moduleCardsState.loading). Drives one unified
    // full-page loading overlay instead of only the scattered inline spinners
    // each individual section already shows.
    const isPageLoading = loading || rangeStats.loading || moduleCardsState.loading;

    const customStyles = {
        control: (provided) => ({ ...provided, cursor: 'pointer' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (style) => ({ ...style, fontSize: '12px' }),
    };

    const showError = (msg) => {
        setError({ status: true, message: msg });
        setTimeout(() => setError({ status: false, message: '' }), 3500);
    };

    // Opens the dedicated per-user journey page (UserJourney.js) for whichever user
    // was clicked, carrying over this page's currently selected date range so the
    // journey opens scoped to the same window the admin was already looking at —
    // falling back to today when no range is set (e.g. the "All Time" preset).
    const openUserJourney = (targetUserId) => {
        if (!targetUserId) return;
        const today = startOfDay(new Date());
        const rangeStart = startDate || today;
        const rangeEnd = endDate || today;
        const params = new URLSearchParams({
            fromdate: formatDateParam(rangeStart),
            todate: formatDateParam(rangeEnd),
        });
        history.push(`/admin/user-journey/${targetUserId}?${params.toString()}`);
    };

    // Small action button dropped into every table/modal on this page that lists
    // users — renders nothing when a row has no real user_id (e.g. a pre-account
    // signup attempt), since there's no account to show a journey for.
    const renderJourneyButton = (targetUserId) => {
        if (!targetUserId) return null;
        return (
            <button
                type="button"
                onClick={() => openUserJourney(targetUserId)}
                title="View Full Journey"
                style={{
                    border: '1px solid #c7d2fe', cursor: 'pointer', background: '#eef2ff',
                    color: '#4338ca', fontSize: '11px', fontWeight: '700',
                    borderRadius: '8px', padding: '5px 10px', whiteSpace: 'nowrap',
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                }}
            >
                <FontAwesomeIcon icon={faRoute} style={{ fontSize: '10px' }} />
                View Full Journey
            </button>
        );
    };

    const fetchLogs = async (page = 1, overrides = {}) => {
        const start = overrides.start !== undefined ? overrides.start : startDate;
        const end = overrides.end !== undefined ? overrides.end : endDate;
        const moduleFilter = overrides.module !== undefined ? overrides.module : selectedModule;
        const eventFilter = overrides.event !== undefined ? overrides.event : selectedEvent;

        setLoading(true);
        setSearched(true);
        setExpandedRow(null);

        try {
            const payload = {
                user_id: userid, 
                token: adminToken,
                method: 'fetchLog',
                page,
            };
            if (start && end) {
                payload.fromdate = formatDateParam(start);
                payload.todate = formatDateParam(end);
            }
            payload.module = moduleFilter && moduleFilter.value !== 'all' ? moduleFilter.value : 'all';
            payload.event = eventFilter && eventFilter.value !== 'all' ? eventFilter.value : 'all';
            const { data } = await Axios.post(ACTIVITY_LOG_URL, payload);

            if (data.success) {
                const rows = flattenLogGroups(data.data);
                const pg = data.pagination || {};
                const currentPage = pg.currentPage || 1;
                const totalPages = pg.totalPages || 1;

                setLogState((prev) => ({
                    data: rows,
                    currentPage,
                    totalPages,
                    totalRecords: pg.totalRecords || rows.length,

                    // Page limit is stable across pages; only the last page can be short,
                    // so keep the largest row count we've seen instead of trusting this page alone.
                    pageSize: Math.max(prev.pageSize, rows.length),

                    // Fall back to deriving from page numbers if the API omits these flags,
                    // otherwise they default to undefined/falsy and permanently disable nav.
                    hasNextPage: pg.hasNextPage ?? currentPage < totalPages,
                    hasPrevPage: pg.hasPrevPage ?? currentPage > 1,
                }));
            } else {
                showError(data.message || 'Failed to fetch activity log.');
                setLogState(emptyState());
            }
        } catch (err) {
            showError(err.response?.data?.message || err.message);
            setLogState(emptyState());
        } finally {
            setLoading(false);
        }
    };

    // Pulls every page (up to MAX_STAT_PAGES) for the selected user + date range so
    // dashboard counts ("how many logins today") reflect the full range, not just
    // whatever one page of the table happens to show.
    const fetchRangeStats = async (overrides = {}) => {
        const start = overrides.start !== undefined ? overrides.start : startDate;
        const end = overrides.end !== undefined ? overrides.end : endDate;
        const moduleFilter = overrides.module !== undefined ? overrides.module : selectedModule;
        const eventFilter = overrides.event !== undefined ? overrides.event : selectedEvent;

        setRangeStats((prev) => ({ ...prev, loading: true }));

        try {
            const basePayload = { user_id: userid, token: adminToken, method: 'fetchLog' };
            if (start && end) {
                basePayload.fromdate = formatDateParam(start);
                basePayload.todate = formatDateParam(end);
            }
            basePayload.module = moduleFilter && moduleFilter.value !== 'all' ? moduleFilter.value : 'all';
            basePayload.event = eventFilter && eventFilter.value !== 'all' ? eventFilter.value : 'all';

            const first = await Axios.post(ACTIVITY_LOG_URL, { ...basePayload, page: 1 });
            if (!first.data.success) {
                setRangeStats({ loading: false, rows: [], truncated: false });
                return;
            }

            let rows = flattenLogGroups(first.data.data);
            const totalPages = first.data.pagination?.totalPages || 1;
            const pagesToFetch = Math.min(totalPages, MAX_STAT_PAGES);

            if (pagesToFetch > 1) {
                const rest = await Promise.all(
                    Array.from({ length: pagesToFetch - 1 }, (_, idx) => idx + 2)
                        .map((p) => Axios.post(ACTIVITY_LOG_URL, { ...basePayload, page: p }))
                );
                rest.forEach((r) => {
                    if (r.data.success) rows = rows.concat(flattenLogGroups(r.data.data));
                });
            }

            setRangeStats({ loading: false, rows, truncated: totalPages > MAX_STAT_PAGES });
        } catch (err) {
            setRangeStats({ loading: false, rows: [], truncated: false });
        }
    };

    // Powers the per-module cards grid via its own API call — always module: 'all',
    // event: 'all' (the cards show every module regardless of the Module/Event filters
    // above), scoped to the shared date range from the top filters, with its own
    // page/limit so a huge single day (e.g. 1,000+ Billing rows) doesn't dump every
    // row into one unbounded scroll box.
    const fetchModuleCards = async (page = 1, overrides = {}) => {
        const start = overrides.start !== undefined ? overrides.start : startDate;
        const end = overrides.end !== undefined ? overrides.end : endDate;
        const limit = overrides.limit !== undefined ? overrides.limit : moduleCardsLimit;

        setModuleCardsState((prev) => ({ ...prev, loading: true }));

        try {
            const payload = {
                user_id: userid,
                token: adminToken,
                method: 'fetchLogs',
                module: 'all',
                event: 'all',
                page,
                limit,
            };
            if (start && end) {
                payload.fromdate = formatDateParam(start);
                payload.todate = formatDateParam(end);
            }
            const { data } = await Axios.post(ACTIVITY_LOG_URL, payload);

            if (data.success) {
                const rows = flattenLogGroups(data.data);
                const pg = data.pagination || {};
                const currentPage = pg.currentPage || page;
                const totalPages = pg.totalPages || 1;

                setModuleCardsState({
                    data: rows,
                    currentPage,
                    totalPages,
                    totalRecords: pg.totalRecords || rows.length,
                    loading: false,
                });
            } else {
                setModuleCardsState(emptyModuleCardsState());
            }
        } catch (err) {
            setModuleCardsState(emptyModuleCardsState());
        }
    };

    const handleModuleCardsPageChange = (page) => {
        if (page < 1 || page > moduleCardsState.totalPages) return;
        fetchModuleCards(page);
    };

    const handleModuleCardsLimitChange = (newLimit) => {
        setModuleCardsLimit(newLimit);
        fetchModuleCards(1, { limit: newLimit });
    };

    const handleSearch = (overrides = {}) => {
        fetchLogs(1, overrides);
        fetchRangeStats(overrides);
    };

    const applyDatePreset = (preset) => {
        const [start, end] = preset.range();
        setDateRange([start, end]);
        handleSearch({ start, end });
        fetchModuleCards(1, { start, end });
    };

    const handleFromDateChange = (date) => {
        setDateRange([date, endDate]);
        if (date && endDate) {
            handleSearch({ start: date, end: endDate });
            fetchModuleCards(1, { start: date, end: endDate });
        }
    };

    const handleToDateChange = (date) => {
        setDateRange([startDate, date]);
        if (startDate && date) {
            handleSearch({ start: startDate, end: date });
            fetchModuleCards(1, { start: startDate, end: date });
        }
    };

    const handleModuleChange = (option) => {
        const normalized = option && option.value !== 'all' ? option : null;
        setSelectedModule(normalized);
        setSelectedEvent(null); // event catalog depends on the module, so reset it
        handleSearch({ module: normalized, event: null });
    };

    const handleEventChange = (option) => {
        const normalized = option && option.value !== 'all' ? option : null;
        setSelectedEvent(normalized);
        handleSearch({ event: normalized });
    };

    const activePresetKey = (() => {
        const s = formatDateParam(startDate);
        const e = formatDateParam(endDate);
        const match = DATE_PRESETS.find((p) => {
            const [ps, pe] = p.range();
            return formatDateParam(ps) === s && formatDateParam(pe) === e;
        });
        return match ? match.key : 'custom';
    })();

    useEffect(() => {
        handleSearch();
        fetchModuleCards(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Loads the full registered-user list once, used as ground truth for whether
    // a signup log row that reads as incomplete actually has a real account.
    const fetchRegisteredUsers = async () => {
        setRegisteredUsers((prev) => ({ ...prev, loading: true }));
        try {
            const { data } = await Axios.post(ADMIN_USER_LIST_URL, {
                user_id: userid,
                token: adminToken,
                method: 'retrieveall',
                type: lStorage?.user?.isAdmin || 'admin',
            });

            if (data.success && Array.isArray(data.data)) {
                const byEmail = new Map();
                const byId = new Map();
                data.data.forEach((u) => {
                    const email = (u.email || '').toString().trim().toLowerCase();
                    if (email) byEmail.set(email, u);
                    if (u.id != null) byId.set(String(u.id), u);
                });
                setRegisteredUsers({ loading: false, byEmail, byId });
            } else {
                setRegisteredUsers({ loading: false, byEmail: new Map(), byId: new Map() });
            }
        } catch (err) {
            setRegisteredUsers({ loading: false, byEmail: new Map(), byId: new Map() });
        }
    };

    useEffect(() => {
        fetchRegisteredUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A signup row counts as a real account either by the log's own success text
    // (isCompletedSignupText) or — since that event sometimes never got logged —
    // by actually existing in the registered-user list, matched by email first
    // (the reliable key pre-account, per funnelRowKey's convention) then by user id.
    const isVerifiedSignup = (row) => {
        const email = (row.username || '').toString().trim().toLowerCase();
        if (email && registeredUsers.byEmail.has(email)) return true;
        if (row.user_id != null && registeredUsers.byId.has(String(row.user_id))) return true;
        return false;
    };

    // Single "did this signup actually complete" check, shared by every funnel
    // computation below (KPIs, Signup→Login conversion, the happy-path cohort,
    // and the Funnel Stage Breakdown) so they all agree with one another.
    const isCompletedSignup = (row) => isCompletedSignupText(row) || isVerifiedSignup(row);

    // Same "Login & Security" module bucket the raw per-module table below (and its
    // "112 records"-style badge) is built from — kept as one match condition so the
    // Login Successful/Failed split always partitions exactly the rows that table shows.
    const isLoginAttempt = (row) => getModuleStyle(row.module).queryValue === 'login';

    // Same convention as isCompletedSignupText: Login.js/MyAccount.js log the API's
    // own resp.message verbatim as the activity log's `message` on BOTH a successful
    // and a failed login attempt (e.g. "User Login Successfully" vs "Invalid Password"),
    // so success/failure lives in the message text itself, not a separate flag.
    // Previously any row under the Login & Security module counted as "logged in" —
    // including failed password attempts — which is the bug this fixes.
    const isSuccessfulLoginText = (row) => /success|complete/i.test(row.message || '');

    const handlePageChange = (page) => {
        if (page < 1) return;
        if (page > logState.totalPages) return;

        fetchLogs(page);
    };

    // Client-side safety net: filters to the selected date range even if the backend
    // silently ignores from_date/to_date, so counts are never wrong, just possibly
    // capped by MAX_STAT_PAGES if a huge amount of unfiltered data comes back.
    const withinDateRange = (row) => {
        if (!startDate || !endDate) return true;
        const day = (row.created_at || '').split(' ')[0];
        if (!day) return false;
        return day >= formatDateParam(startDate) && day <= formatDateParam(endDate);
    };

    // Client-side safety net: filters to the selected module category even if the
    // backend silently ignores the `module` param, so the side-by-side tables only
    // ever show the category the admin picked.
    const withinModuleFilter = (row) => {
        if (!selectedModule) return true;
        return getModuleStyle(row.module).queryValue === selectedModule.value;
    };

    // Same safety net for the event filter — matches exactly or as a substring,
    // since raw `event` values in the data aren't guaranteed to match the
    // curated catalog verbatim.
    const withinEventFilter = (row) => {
        if (!selectedEvent) return true;
        const rowEvent = (row.event || '').toString().toLowerCase();
        const needle = selectedEvent.value.toLowerCase();
        return rowEvent === needle || rowEvent.includes(needle);
    };

    const filteredData = logState.data.filter((row) => {
        if (!withinDateRange(row)) return false;
        if (!withinModuleFilter(row)) return false;
        if (!withinEventFilter(row)) return false;
        if (!searchText) return true;
        const needle = searchText.toLowerCase();
        return ['module', 'event', 'message', 'page', 'user_id', 'username', 'ip', 'added_by']
            .some((key) => (row[key] || '').toString().toLowerCase().includes(needle));
    });

    // The paginated table only ever holds one page of rows, so counts for "how many
    // logins happened today" have to come from the full range fetch instead.
    const filteredRangeData = useMemo(() => {
        const needle = searchText.toLowerCase();
        return rangeStats.rows.filter((row) => {
            if (!withinDateRange(row)) return false;
            if (!withinModuleFilter(row)) return false;
            if (!withinEventFilter(row)) return false;
            if (!searchText) return true;
            return ['module', 'event', 'message', 'page', 'user_id', 'username', 'ip']
                .some((key) => (row[key] || '').toString().toLowerCase().includes(needle));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeStats.rows, searchText, startDate, endDate, selectedModule, selectedEvent]);

    const dashboardStats = useMemo(() => {
        const moduleCounts = new Map();
        const rawModuleCounts = new Map();
        const ips = new Set();
        const dayCounts = new Map();
        let lastActivity = null;

        filteredRangeData.forEach((row) => {
            // Group by resolved module category, not the raw event-tracking string
            // (e.g. "email_detail_report" / "email_verification" both roll up to "Email").
            const style = getModuleStyle(row.module);
            moduleCounts.set(style.label, (moduleCounts.get(style.label) || 0) + 1);

            // The API logs the same module under casing variants (e.g. both "signup"
            // and "Signup" arrive as separate groups) — key case-insensitively so they
            // merge into one row, preferring the capitalized variant for display.
            const rawKey = (row.module || 'unknown').toLowerCase();
            if (!rawModuleCounts.has(rawKey)) {
                rawModuleCounts.set(rawKey, { style, count: 0, display: row.module || 'unknown' });
            }
            const rawEntry = rawModuleCounts.get(rawKey);
            rawEntry.count += 1;
            if (row.module && /^[A-Z]/.test(row.module)) rawEntry.display = row.module;

            if (row.ip) ips.add(row.ip);

            const day = (row.created_at || '').split(' ')[0];
            if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);

            if (row.created_at && (!lastActivity || row.created_at > lastActivity)) {
                lastActivity = row.created_at;
            }
        });

        const moduleBreakdown = Array.from(moduleCounts.entries())
            .map(([name, count]) => {
                const style = MODULE_STYLES.find((m) => m.label === name) || DEFAULT_MODULE_STYLE;
                return { name, count, color: style.color, bg: style.bg, border: style.border, icon: style.icon };
            })
            .sort((a, b) => b.count - a.count)
            .slice(0, 8);

        const rawModuleBreakdown = Array.from(rawModuleCounts.values())
            .map(({ style, count, display }) => ({ rawModule: display, count, style }))
            .sort((a, b) => b.count - a.count);

        const trend = Array.from(dayCounts.entries())
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => a.date.localeCompare(b.date));

        return {
            moduleBreakdown,
            rawModuleBreakdown,
            trend,
            uniqueModules: moduleCounts.size,
            uniqueIps: ips.size,
            lastActivity,
            totalInRange: filteredRangeData.length,
        };
    }, [filteredRangeData]);

    // Matches users who show up under the Signup module against users who actually
    // succeeded at a login attempt (isLoginAttempt + isSuccessfulLoginText) — a
    // failed password attempt under the Login & Security module doesn't count as
    // "converted", it counts as a login that didn't succeed.
    const conversionStats = useMemo(() => {
        const stats = buildFunnelStats(
            filteredRangeData,
            (row) => getModuleStyle(row.module).queryValue === 'signup' && isCompletedSignup(row),
            (row) => isLoginAttempt(row) && isSuccessfulLoginText(row)
        );
        return {
            totalSignups: stats.totalStepA,
            totalConverted: stats.totalConverted,
            conversionRate: stats.conversionRate,
            conversions: stats.conversions.map((c) => ({
                key: c.key, username: c.username,
                signup_user_id: c.a_user_id, login_user_id: c.b_user_id,
                signup_at: c.a_at, login_at: c.b_at,
                signup_ip: c.a_ip, login_ip: c.b_ip,
            })),
            notConverted: stats.notConverted.map((n) => ({
                key: n.key, username: n.username,
                signup_user_id: n.a_user_id, signup_at: n.a_at, signup_ip: n.a_ip,
            })),
        };
        // isCompletedSignup is re-created each render but only closes over registeredUsers,
        // which is already listed — including the function itself would just defeat memoization.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredRangeData, registeredUsers]);

    // Same conversionStats.conversions rows, each carrying the exact signup→login gap
    // (ms) alongside them — the source list for the Time to Convert card's bucket filter
    // below. Kept separate from conversionStats itself so adding a duration column here
    // doesn't touch the plain Signup → Login Conversion table above.
    const conversionsWithDuration = useMemo(() => {
        return conversionStats.conversions.map((c) => {
            const signupTime = parseLogDate(c.signup_at);
            const loginTime = parseLogDate(c.login_at);
            const durationMs = (signupTime && loginTime) ? (loginTime - signupTime) : null;
            return { ...c, durationMs };
        });
    }, [conversionStats.conversions]);

    // Rows from conversionsWithDuration that converted within the currently selected
    // SIGNUP_LOGIN_TIME_BUCKETS threshold — recomputed against filteredRangeData (via
    // conversionStats), so a new date range picked above automatically re-scopes this
    // filter instead of it pinning to whatever range was active when a bucket was chosen.
    const signupLoginTimeFiltered = useMemo(() => {
        const bucket = SIGNUP_LOGIN_TIME_BUCKETS.find((b) => b.key === signupLoginTimeBucket) || SIGNUP_LOGIN_TIME_BUCKETS[0];
        if (bucket.ms == null) return conversionsWithDuration;
        return conversionsWithDuration.filter((c) => c.durationMs != null && c.durationMs <= bucket.ms);
    }, [conversionsWithDuration, signupLoginTimeBucket]);

    // Matches users who show up under the Signup module against users who show up
    // under the WhatsApp module with a message confirming onboarding actually
    // completed (isCompletedWhatsappOnboardingText — same convention as
    // isCompletedSignup below). Measured from Login rather than Signup, since
    // onboarding can only happen after a user has actually logged into the
    // dashboard.
    const whatsappOnboardingStats = useMemo(() => {
        const stats = buildFunnelStats(
            filteredRangeData,
            (row) => isLoginAttempt(row) && isSuccessfulLoginText(row),
            (row) => getModuleStyle(row.module).queryValue === 'whatsapp' && isCompletedWhatsappOnboardingText(row)
        );
        return {
            totalLogins: stats.totalStepA,
            totalOnboarded: stats.totalConverted,
            onboardingRate: stats.conversionRate,
            onboarded: stats.conversions.map((c) => ({
                key: c.key, username: c.username,
                login_user_id: c.a_user_id, onboarding_user_id: c.b_user_id,
                login_at: c.a_at, onboarded_at: c.b_at,
                login_ip: c.a_ip, onboarding_ip: c.b_ip,
            })),
            notOnboarded: stats.notConverted.map((n) => ({
                key: n.key, username: n.username,
                login_user_id: n.a_user_id, login_at: n.a_at, login_ip: n.a_ip,
            })),
        };
    }, [filteredRangeData]);

    // Users who have real activity under Signup (a completed signup) or WhatsApp
    // Onboarding, but no successful Login row at all — both of those activities are
    // only supposed to be reachable after a login, so a hit here means the login
    // event itself simply never got recorded (a data-recording gap), not that the
    // user skipped login. Surfaced as its own "Error in Recording Data" card so these
    // mismatches are visible even though they'd otherwise look like broken funnels.
    const dataMismatchStats = useMemo(() => {
        const moduleOf = (row) => getModuleStyle(row.module).queryValue;
        const isSignupActivity = (row) => moduleOf(row) === 'signup' && isCompletedSignup(row);
        const isOnboardingActivity = (row) => moduleOf(row) === 'whatsapp' && isCompletedWhatsappOnboardingText(row);
        // Any Login & Security row — success or failed attempt — proves a login event
        // was actually recorded for this person, which is all this card cares about. A
        // failed *attempt* is still a recorded login; requiring isSuccessfulLoginText
        // here would flag people as "missing" a login the system did capture, just not
        // as a success.
        const isLoginRecorded = (row) => isLoginAttempt(row);

        // Keyed by email only, not funnelRowKey's uid-first convention — a pre-account
        // Signup row logs user_id as 0/empty and only picks up the real id on the final
        // "Signup Complete" row, while WhatsApp Onboarding rows always carry the real id.
        // Keying by uid would put those on different keys for the same person; email is
        // the one identifier both modules log consistently, so it's what ties them together.
        const emailKey = (row) => {
            const email = (row.username || row.email || '').toString().trim().toLowerCase();
            return email || null;
        };

        const activityByKey = new Map();
        const loginKeys = new Set();

        filteredRangeData.forEach((row) => {
            const key = emailKey(row);
            if (!key) return;

            if (isLoginRecorded(row)) loginKeys.add(key);

            const signupHit = isSignupActivity(row);
            const onboardingHit = isOnboardingActivity(row);
            if (!signupHit && !onboardingHit) return;

            const source = signupHit ? 'Signup' : 'WhatsApp Onboarding';
            const existing = activityByKey.get(key);
            if (!existing) {
                activityByKey.set(key, {
                    key, username: row.username, user_id: row.user_id, ip: row.ip,
                    first_at: row.created_at, last_at: row.created_at,
                    sources: new Set([source]),
                });
            } else {
                existing.sources.add(source);
                if (row.user_id != null) existing.user_id = row.user_id;
                if ((row.created_at || '') < (existing.first_at || '')) existing.first_at = row.created_at;
                if ((row.created_at || '') >= (existing.last_at || '')) {
                    existing.last_at = row.created_at;
                    existing.ip = row.ip || existing.ip;
                }
            }
        });

        const mismatched = [];
        activityByKey.forEach((entry) => {
            if (loginKeys.has(entry.key)) return;
            // Only surface customers with BOTH Signup and WhatsApp Onboarding activity —
            // a mismatch on just one of the two is excluded from this card.
            if (entry.sources.size < 2) return;
            mismatched.push({
                key: entry.key, username: entry.username, user_id: entry.user_id, ip: entry.ip,
                first_at: entry.first_at, last_at: entry.last_at,
                source: Array.from(entry.sources).join(' + '),
            });
        });
        mismatched.sort((a, b) => (b.last_at || '').localeCompare(a.last_at || ''));

        return { totalActivity: activityByKey.size, mismatched };
        // isCompletedSignup only closes over registeredUsers, which is already listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredRangeData, registeredUsers]);

    // Matches users who show up under the WhatsApp module with an onboarding-flavored
    // event against users who show up under the Billing module with a completed
    // transaction_payment event — i.e. who actually recharged/paid vs who onboarded
    // but hasn't paid yet. Measured from WhatsApp Onboarding rather than Login/Signup,
    // since a recharge only makes sense once a user is already onboarded onto a channel.
    const rechargeStats = useMemo(() => {
        const stats = buildFunnelStats(
            filteredRangeData,
            (row) => getModuleStyle(row.module).queryValue === 'whatsapp' && isCompletedWhatsappOnboardingText(row),
            (row) => getModuleStyle(row.module).queryValue === 'billing' && /transaction_payment/i.test(row.event || '')
        );
        return {
            totalOnboarded: stats.totalStepA,
            totalRecharged: stats.totalConverted,
            rechargeRate: stats.conversionRate,
            recharged: stats.conversions.map((c) => ({
                key: c.key, username: c.username,
                onboarding_user_id: c.a_user_id, payment_user_id: c.b_user_id,
                onboarded_at: c.a_at, recharged_at: c.b_at,
                onboarding_ip: c.a_ip, payment_ip: c.b_ip,
            })),
            notRecharged: stats.notConverted.map((n) => ({
                key: n.key, username: n.username,
                onboarding_user_id: n.a_user_id, onboarded_at: n.a_at, onboarding_ip: n.a_ip,
            })),
        };
    }, [filteredRangeData]);

    // Strict sequential path: Signup → Login → WhatsApp Onboarding → Messaging → Payment.
    // "Messaging" = the raw "message" module — every channel's send action (SMS, WhatsApp,
    // Voice, Email, RCS) logs through this one shared module/event (test_message_button),
    // with `page` (e.g. "send_sms") as the only signal for which channel was actually used.
    // This used to check the resolved per-channel modules (whatsapp/sms/rcs/voice/email)
    // instead, which never matched real data (send actions aren't logged under those raw
    // module strings) — so this stage, and everything narrowed from it below (Payment),
    // was silently always empty. "Payment" splits into Tried (visited billing/transactions
    // but no completed transaction_payment event) vs Successful.
    const funnelPath = useMemo(() => {
        const moduleOf = (row) => getModuleStyle(row.module).queryValue;
        const isSignup = (row) => moduleOf(row) === 'signup' && isCompletedSignup(row);
        const isLogin = (row) => isLoginAttempt(row) && isSuccessfulLoginText(row);
        const isOnboarding = (row) => moduleOf(row) === 'whatsapp' && isCompletedWhatsappOnboardingText(row);
        const isMessaging = (row) => isMessagingRow(row) && isMessageSentText(row);
        const isBilling = (row) => moduleOf(row) === 'billing';
        const isPaid = (row) => isBilling(row) && /transaction_payment/i.test(row.event || '');
        const isTriedPayment = (row) => isBilling(row) && !isPaid(row);

        const signup = buildInitialCohort(filteredRangeData, isSignup);

        // "Logged In" is every login within the selected range, not only logins by users
        // whose signup ALSO happens to fall in this exact range — narrowCohort(signup, isLogin)
        // only counts a login if that same user's signup row is also present in this range,
        // so an existing account (signed up weeks ago) logging in today was being dropped
        // entirely, showing 0 even though the raw Login-module table has rows for today.
        const signupByKey = new Map(signup.map((s) => [s.key, s]));
        const loggedIn = buildInitialCohort(filteredRangeData, isLogin).map((entry) => {
            const signupEntry = signupByKey.get(entry.key);
            // Only set prevAt (used for the Signup→Login duration bucket) when this same
            // user's signup also happened in-range — otherwise there's no in-range signup
            // timestamp to measure from, and the duration correctly falls back to "—".
            return signupEntry ? { ...entry, prevAt: signupEntry.at } : entry;
        });
        // "No Login" stays scoped to this range's new signups who haven't logged in at all —
        // that comparison (fresh signup, zero logins) is still meaningful on its own.
        const loggedInKeys = new Set(loggedIn.map((l) => l.key));
        const notLoggedIn = signup.filter((entry) => !loggedInKeys.has(entry.key));
        const login = { survivors: loggedIn, dropped: notLoggedIn };

        const onboarding = narrowCohort(filteredRangeData, login.survivors, isOnboarding);
        const messaging = narrowCohort(filteredRangeData, onboarding.survivors, isMessaging);
        const paid = narrowCohort(filteredRangeData, messaging.survivors, isPaid);
        const tried = narrowCohort(filteredRangeData, paid.dropped, isTriedPayment);

        return {
            signup, login, onboarding, messaging,
            payment: { paid: paid.survivors, tried: tried.survivors, none: tried.dropped },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredRangeData, registeredUsers]);

    // Splits EVERY raw signup-module row into Completed vs Not Completed. Only a row
    // whose own message IS the real completion text (isCompletedSignupText — API sends
    // "signup successfull" on "Verify Account") counts as Completed for this preview;
    // isVerifiedSignup (cross-checked against the real admin_user_list.php registered-user
    // list) is kept only as an informational "verified" column, NOT used to decide the
    // bucket — since it checks by email/user id, using it for bucketing would promote
    // EVERY other signup-module row for that same (now-registered) user, failed attempts
    // included, into "Completed" too, even though their own message isn't the completion
    // one. Completed rows are still deduped per user (keeping the most recent) in case the
    // same completion got logged more than once.
    const signupCompletionSplit = useMemo(() => {
        const completedByKey = new Map();
        const notCompletedRows = [];
        filteredRangeData.forEach((row) => {
            if (getModuleStyle(row.module).queryValue !== 'signup') return;
            const verified = isVerifiedSignup(row);
            const entry = {
                // A pre-account signup attempt (weak password, invalid mobile, etc.) is
                // logged with `username: null` but `email` still populated — this is the
                // exact case the Reason for Failure view exists to show, so `email` has
                // to be carried through explicitly rather than assumed equal to username.
                username: row.username, email: row.email || row.username, user_id: row.user_id, ip: row.ip, at: row.created_at,
                // `message` carries the reason (e.g. "Invalid Mobile", "Forbidden", or the
                // success text) and `added_by` carries the signup source (e.g. which
                // page/channel the signup came in through) — neither is tracked on other
                // modules, so these only get populated/shown for the Signup stage.
                reason: row.message, source: row.added_by, verified: verified ? 'Yes' : 'No',
                // Signup-only fields carried in the API's nested `data` object (name/mobile
                // as entered on the signup form) plus plan/country/city/parent/parent_type —
                // same "only ever populated for Signup" scoping as reason/source above.
                name: row.data?.name || '-', mobile: row.data?.mobile || '-', plan: row.plan || '-',
                location: [row.city, row.country].filter(Boolean).join(', ') || '-',
                referredBy: row.parent ? `${row.parent}${row.parent_type ? ` (${row.parent_type})` : ''}` : '-',
                // Raw page/parent/parent_type (as opposed to the display-formatted
                // location/referredBy above) — kept for the Employee/Reseller/Page
                // breakdown cards, which group and count on these directly.
                page: row.page || '', parent: row.parent, parent_type: row.parent_type, country: row.country,
            };

            if (isCompletedSignupText(row)) {
                const key = funnelRowKey(row) || `${(row.username || '').toLowerCase()}|${row.user_id || ''}`;
                const existing = completedByKey.get(key);
                if (!existing || (row.created_at || '') > (existing.at || '')) {
                    completedByKey.set(key, entry);
                }
            } else {
                notCompletedRows.push(entry);
            }
        });

        const completedRows = Array.from(completedByKey.values());
        // Most recent first in both buckets — freshest completions/drop-offs matter most.
        completedRows.sort((a, b) => (b.at || '').localeCompare(a.at || ''));
        notCompletedRows.sort((a, b) => (b.at || '').localeCompare(a.at || ''));

        // notCompletedRows itself stays un-deduped (every raw attempt — "Forbidden",
        // "Mobile OTP Can Not be Blank" x2, etc. — is kept so the Detailed Records modal
        // can still group them per user and show the full attempt history behind the
        // "Preview" button). But the *count* used everywhere else — this stage's badge,
        // the Total Signup Attempts baseline, every other stage's conversion % denominator
        // — should count distinct users, not raw rows, same as completedRows already does;
        // otherwise one user retrying several times inflates "Signup Not Completed" (and so
        // "Total Signup Attempts") well past the number of people who actually tried.
        const notCompletedUserKeys = new Set();
        notCompletedRows.forEach((row) => {
            const key = row.user_id ? `uid:${row.user_id}` : `u:${(row.username || row.email || '').toString().trim().toLowerCase()}`;
            notCompletedUserKeys.add(key);
        });
        const notCompletedUserCount = notCompletedUserKeys.size;

        return {
            completed: completedRows.length, notCompleted: notCompletedUserCount,
            total: completedRows.length + notCompletedUserCount,
            completedRows, notCompletedRows,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredRangeData, registeredUsers]);

    // Employee/Reseller/Location/Page/Plan breakdowns for the Overall Stats grid — scoped
    // to signupCompletionSplit.completedRows (one row per user, actually-completed signups
    // only), same universe as the "Signup Completed" count shown elsewhere, so these counts
    // reconcile with it rather than including failed/dropped attempts.
    const signupBreakdowns = useMemo(() => {
        const rows = signupCompletionSplit.completedRows;
        return {
            employee: buildCountBreakdown(rows.filter((r) => r.parent_type === 'emp'), (r) => r.parent),
            reseller: buildCountBreakdown(rows.filter((r) => r.parent_type === 'reseller'), (r) => r.parent),
            location: buildCountBreakdown(rows, (r) => r.country || null),
            page: buildCountBreakdown(rows, (r) => r.page || '(direct)'),
            plan: buildCountBreakdown(rows, (r) => (r.plan !== '-' ? r.plan : null)),
        };
    }, [signupCompletionSplit]);

    // Underlying rows behind the Overall Stats grid's "Preview" modal — same
    // signupCompletionSplit.completedRows universe the breakdown cards above count from,
    // narrowed by whichever of the modal's own Employee/Reseller/Country/Page/Plan filters
    // are set (empty string = that filter is off). All five filters AND together, so
    // picking both an Employee and a Reseller at once (a combination that can't really
    // occur, since parent_type is either 'emp' or 'reseller' for a given row) legitimately
    // produces zero rows rather than silently ignoring one of the two.
    const signupDetailRows = useMemo(() => {
        const { employee, reseller, country, page, plan } = signupDetailFilters;
        return signupCompletionSplit.completedRows.filter((r) => {
            if (employee && r.parent !== employee) return false;
            if (reseller && r.parent !== reseller) return false;
            if (country && r.country !== country) return false;
            if (page && (r.page || '(direct)') !== page) return false;
            if (plan && r.plan !== plan) return false;
            return true;
        });
    }, [signupCompletionSplit, signupDetailFilters]);

    const openSignupDetail = () => {
        setSignupDetailFilters({ employee: '', reseller: '', country: '', page: '', plan: '' });
        setSignupDetailPage(1);
        setSignupDetailOpen(true);
    };

    // Drill-in from a single row of an Employee/Reseller/Location/Page/Plan breakdown
    // card — same shared modal as openSignupDetail, just pre-set to that one row's
    // filter instead of opening blank, so clicking "ritu@datagenit.com  13" jumps
    // straight to that employee's signups instead of making the admin re-pick it.
    const openSignupDetailFiltered = (filterKey, value) => {
        setSignupDetailFilters({ employee: '', reseller: '', country: '', page: '', plan: '', [filterKey]: value });
        setSignupDetailPage(1);
        setSignupDetailOpen(true);
    };

    // Login-attempt records for the Login row's Preview modal, built from
    // `filteredRangeData` (the full selected date range, same scope as every other
    // funnel/KPI number on this page), split PURELY on the row's own message text
    // (isSuccessfulLoginText: contains "success"/"complete" → Successful, anything else →
    // Failed). successRows/failedRows themselves stay un-deduped (every raw attempt is
    // kept so the Detailed Records modal can still group them per user and show the full
    // attempt history behind the "Preview" button) — but successUserCount/failedUserCount,
    // the numbers actually shown on the stage badges, count distinct users, not raw rows,
    // same reasoning as signupCompletionSplit's notCompletedUserCount: otherwise one user
    // retrying several times inflates "Login Failed" well past the number of people who
    // actually tried. This used to read from `filteredData` (only the current table PAGE,
    // ~20-50 rows) while the rest of the funnel used the full range — that mismatch is
    // what produced nonsensical percentages like "608% conversion" for the Login stage.
    const loginAttemptRows = useMemo(() => {
        const successRows = [];
        const failedRows = [];
        filteredRangeData.forEach((row) => {
            if (!isLoginAttempt(row)) return;
            const entry = {
                username: row.username, email: row.email || row.username, user_id: row.user_id, ip: row.ip, at: row.created_at,
                // `reason` carries the API's own response message — the success text
                // (e.g. "User Login Successfully") or the failure reason (e.g. "Invalid Password").
                reason: row.message,
            };
            if (isSuccessfulLoginText(row)) successRows.push(entry);
            else failedRows.push(entry);
        });
        successRows.sort((a, b) => (b.at || '').localeCompare(a.at || ''));
        failedRows.sort((a, b) => (b.at || '').localeCompare(a.at || ''));

        const userKey = (r) => (r.user_id ? `uid:${r.user_id}` : `u:${(r.email || r.username || '').toString().trim().toLowerCase()}`);
        const successUserCount = new Set(successRows.map(userKey)).size;
        const failedUserCount = new Set(failedRows.map(userKey)).size;

        return { successRows, failedRows, successUserCount, failedUserCount };
    }, [filteredRangeData]);

    // Pure email-id cross-check between today's (in-range) completed signups and
    // today's successful logins — deliberately matched by email alone (not the
    // user_id-preferred funnelRowKey every other stage uses), since a fresh signup
    // often hasn't been assigned a user_id yet at the moment its "Signup Completed"
    // row is logged, while the login row right after it already has one; keying by
    // user_id there would miss the very same-day match this section exists to show.
    // Every email that shows up in BOTH buckets is a brand-new user who signed up
    // and logged in inside this same range — not an old account just logging back in.
    const signupLoginEmailMatch = useMemo(() => {
        const emailOf = (row) => (row.username || '').toString().trim().toLowerCase();

        const signupByEmail = new Map();
        signupCompletionSplit.completedRows.forEach((row) => {
            const email = emailOf(row);
            if (!email) return;
            const existing = signupByEmail.get(email);
            if (!existing || (row.at || '') < (existing.at || '')) signupByEmail.set(email, row);
        });

        const loginByEmail = new Map();
        loginAttemptRows.successRows.forEach((row) => {
            const email = emailOf(row);
            if (!email) return;
            const existing = loginByEmail.get(email);
            if (!existing || (row.at || '') < (existing.at || '')) loginByEmail.set(email, row);
        });

        const matched = [];
        const notMatched = [];
        signupByEmail.forEach((signupRow, email) => {
            const loginRow = loginByEmail.get(email);
            if (loginRow) {
                matched.push({
                    key: `email:${email}`, username: email,
                    user_id: loginRow.user_id ?? signupRow.user_id,
                    ip: loginRow.ip || signupRow.ip, message: loginRow.reason,
                    prevAt: signupRow.at, at: loginRow.at,
                    signup_at: signupRow.at, login_at: loginRow.at,
                });
            } else {
                notMatched.push({
                    key: `email:${email}`, username: email, email,
                    user_id: signupRow.user_id, ip: signupRow.ip,
                    at: signupRow.at, signup_at: signupRow.at,
                });
            }
        });
        matched.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        notMatched.sort((a, b) => (b.signup_at || '').localeCompare(a.signup_at || ''));
        return { matched, notMatched };
    }, [signupCompletionSplit, loginAttemptRows]);

    // "Potential Customers Lost" card — NOT signup failures; this is
    // signupLoginEmailMatch.notMatched, i.e. users who DID complete signup but haven't
    // logged in yet, split by their email's domain into "Gmail" (gmail.com) and
    // everything else ("Potential Customer Who Lost" — every other domain, personal
    // provider or company/business, as long as an email/domain is present at all).
    const lostSignupsByDomain = useMemo(() => {
        const personalDomain = [];
        const gmail = [];
        signupLoginEmailMatch.notMatched.forEach((row) => {
            const domain = emailDomainOf(row);
            if (!domain) return;
            if (domain === 'gmail.com') gmail.push(row);
            else personalDomain.push(row);
        });
        return { personalDomain, gmail };
    }, [signupLoginEmailMatch]);

    // Shared match key for the Login → First Payment and Login → Onboarding cross-checks
    // below — prefers user_id like funnelRowKey elsewhere, but falls back to the row's own
    // `email` field before `username`. That matters here specifically: a failed/invalid
    // login row can carry a populated `email` while `username` is null (e.g. "Invalid
    // Username"), and matching on user_id-or-username alone (funnelRowKey's convention)
    // would silently drop it.
    const idOrEmailKey = (row) => {
        if (row.user_id) return `uid:${row.user_id}`;
        const email = (row.email || row.username || '').toString().trim().toLowerCase();
        return email ? `email:${email}` : null;
    };

    // Every successful login in range against that same user's EARLIEST ever billing
    // row whose message is the literal "Payment Success" text — i.e. did they go on to
    // make their first payment, not just any payment. Independent of the sequential
    // funnelPath.payment stage below (which requires WhatsApp Onboarding + Messaging in
    // between); this is the direct Login → Payment pair, backing the "All Users" Funnel
    // Stage Breakdown tab, re-keyed by user_id/email (instead of funnelRowKey) per how
    // this section's matching was asked to work.
    const loginFirstPaymentMatch = useMemo(() => {
        const paidByKey = new Map();
        // Each of the three Billing outcomes is its own exact-text map — a Billing row
        // only lands in triedByKey if its message is literally "Payment Try", not as a
        // catch-all for anything that isn't Success/Failed. A row whose message matches
        // none of the three (or a user with no Billing row at all) ends up in none of
        // the maps below and so isn't counted in any of the three subrows.
        const triedByKey = new Map();
        const failedByKey = new Map();
        filteredRangeData.forEach((row) => {
            if (getModuleStyle(row.module).queryValue !== 'billing') return;
            const key = idOrEmailKey(row);
            if (!key) return;
            if (isPaymentSuccessText(row)) {
                const existing = paidByKey.get(key);
                if (!existing || (row.created_at || '') < (existing.created_at || '')) paidByKey.set(key, row);
            } else if (isPaymentFailedText(row)) {
                const existing = failedByKey.get(key);
                if (!existing || (row.created_at || '') < (existing.created_at || '')) failedByKey.set(key, row);
            } else if (isPaymentTriedText(row)) {
                const existing = triedByKey.get(key);
                if (!existing || (row.created_at || '') < (existing.created_at || '')) triedByKey.set(key, row);
            }
        });

        const loginByKey = new Map();
        filteredRangeData.forEach((row) => {
            if (!isLoginAttempt(row) || !isSuccessfulLoginText(row)) return;
            const key = idOrEmailKey(row);
            if (!key) return;
            const existing = loginByKey.get(key);
            if (!existing || (row.created_at || '') < (existing.created_at || '')) loginByKey.set(key, row);
        });

        const matched = [];
        const notMatched = [];
        // notMatched holds every non-paid logged-in user (used as the "% of logins"
        // denominator); tried/failed below are the strict exact-text subsets of it — a
        // user lands in at most one of the two, and neither if their Billing activity
        // (or lack of it) matches none of the three known messages.
        const tried = [];
        const failed = [];
        loginByKey.forEach((loginRow, key) => {
            const paidRow = paidByKey.get(key);
            if (paidRow) {
                matched.push({
                    key, username: loginRow.username || loginRow.email || paidRow.username,
                    user_id: loginRow.user_id ?? paidRow.user_id,
                    ip: paidRow.ip || loginRow.ip, message: paidRow.message,
                    prevAt: loginRow.created_at, at: paidRow.created_at,
                    login_at: loginRow.created_at, paid_at: paidRow.created_at,
                });
                return;
            }
            const failedRow = failedByKey.get(key);
            const triedRow = triedByKey.get(key);
            const notMatchedEntry = {
                key, username: loginRow.username || loginRow.email, email: loginRow.email || loginRow.username,
                user_id: loginRow.user_id, ip: (failedRow || triedRow)?.ip || loginRow.ip, message: (failedRow || triedRow)?.message,
                at: loginRow.created_at, login_at: loginRow.created_at,
            };
            notMatched.push(notMatchedEntry);
            if (failedRow) failed.push(notMatchedEntry);
            else if (triedRow) tried.push(notMatchedEntry);
        });
        matched.sort((a, b) => (b.paid_at || '').localeCompare(a.paid_at || ''));
        notMatched.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        tried.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        failed.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        return { matched, notMatched, tried, failed };
    }, [filteredRangeData]);

    // Every successful login in range against that same user's EARLIEST ever WhatsApp
    // onboarding-flavored event in range (event text containing "onboard") — i.e. did they
    // go on to actually onboard on WhatsApp. Independent of the sequential
    // funnelPath.onboarding stage above (which only counts a login if funnelRowKey — user_id,
    // falling back to `username` only — matches); this re-keys the same Login → Onboarding
    // pair by user_id/email via idOrEmailKey, same fix applied to Login → First Payment.
    const loginOnboardingMatch = useMemo(() => {
        const onboardedByKey = new Map();
        filteredRangeData.forEach((row) => {
            if (getModuleStyle(row.module).queryValue !== 'whatsapp') return;
            if (!isCompletedWhatsappOnboardingText(row)) return;
            const key = idOrEmailKey(row);
            if (!key) return;
            const existing = onboardedByKey.get(key);
            if (!existing || (row.created_at || '') < (existing.created_at || '')) onboardedByKey.set(key, row);
        });

        const loginByKey = new Map();
        filteredRangeData.forEach((row) => {
            if (!isLoginAttempt(row) || !isSuccessfulLoginText(row)) return;
            const key = idOrEmailKey(row);
            if (!key) return;
            const existing = loginByKey.get(key);
            if (!existing || (row.created_at || '') < (existing.created_at || '')) loginByKey.set(key, row);
        });

        const matched = [];
        const notMatched = [];
        loginByKey.forEach((loginRow, key) => {
            const onboardedRow = onboardedByKey.get(key);
            if (onboardedRow) {
                matched.push({
                    key, username: loginRow.username || loginRow.email || onboardedRow.username,
                    user_id: loginRow.user_id ?? onboardedRow.user_id,
                    ip: onboardedRow.ip || loginRow.ip, message: onboardedRow.message,
                    prevAt: loginRow.created_at, at: onboardedRow.created_at,
                    login_at: loginRow.created_at, onboarded_at: onboardedRow.created_at,
                });
            } else {
                notMatched.push({
                    key, username: loginRow.username || loginRow.email, email: loginRow.email || loginRow.username,
                    user_id: loginRow.user_id, ip: loginRow.ip,
                    at: loginRow.created_at, login_at: loginRow.created_at,
                });
            }
        });
        matched.sort((a, b) => (b.onboarded_at || '').localeCompare(a.onboarded_at || ''));
        notMatched.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        return { matched, notMatched };
    }, [filteredRangeData]);

    // Same Login → WhatsApp Onboarding pairing as loginOnboardingMatch above, but scoped
    // to only NEW users — this range's completed signups who also logged in, the exact
    // cohort signupLoginEmailMatch already establishes — instead of every login in range
    // regardless of when that user originally signed up. loginOnboardingMatch's count is
    // dominated by existing accounts just logging back in, which is the wrong universe for
    // the "New Users" tab's version of this stage; this one keys by email like
    // signupLoginEmailMatch does, for the same reason (a fresh signup often has no
    // user_id yet at the moment it's logged).
    const newUserOnboardingMatch = useMemo(() => {
        const onboardedByEmail = new Map();
        filteredRangeData.forEach((row) => {
            if (getModuleStyle(row.module).queryValue !== 'whatsapp') return;
            if (!isCompletedWhatsappOnboardingText(row)) return;
            const email = (row.email || row.username || '').toString().trim().toLowerCase();
            if (!email) return;
            const existing = onboardedByEmail.get(email);
            if (!existing || (row.created_at || '') < (existing.created_at || '')) onboardedByEmail.set(email, row);
        });

        const matched = [];
        const notMatched = [];
        signupLoginEmailMatch.matched.forEach((entry) => {
            const onboardedRow = onboardedByEmail.get(entry.username);
            if (onboardedRow) {
                matched.push({
                    key: entry.key, username: entry.username,
                    user_id: entry.user_id ?? onboardedRow.user_id, ip: onboardedRow.ip || entry.ip, message: onboardedRow.message,
                    prevAt: entry.login_at, at: onboardedRow.created_at,
                    login_at: entry.login_at, onboarded_at: onboardedRow.created_at,
                });
            } else {
                notMatched.push({
                    key: entry.key, username: entry.username, email: entry.username,
                    user_id: entry.user_id, ip: entry.ip,
                    at: entry.login_at, login_at: entry.login_at,
                });
            }
        });
        matched.sort((a, b) => (b.onboarded_at || '').localeCompare(a.onboarded_at || ''));
        notMatched.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        return { matched, notMatched };
    }, [filteredRangeData, signupLoginEmailMatch]);

    // Every raw "message" module row in range that actually WENT OUT (isMessageSentText
    // — excludes rows like send_email's "Nothing To Do", which log under the same module
    // but never actually sent anything), each tagged with the channel `page` points to —
    // the shared data source behind both tabs' Messaging stage and its Detailed Preview
    // channel breakdown.
    const messagingRows = useMemo(() => (
        filteredRangeData
            .filter((row) => isMessagingRow(row) && isMessageSentText(row))
            .map((row) => {
                const channel = getMessageChannel(row.page);
                return {
                    username: row.username, email: row.email || row.username, user_id: row.user_id, ip: row.ip, at: row.created_at,
                    channel, message: row.message, reason: row.message,
                };
            })
    ), [filteredRangeData]);

    // Login → Messaging, scoped to only NEW users (same "signed up & logged in" cohort as
    // newUserOnboardingMatch above) — did that user send at least one message on any
    // channel, keyed by email for the same reason (fresh signups often have no user_id yet).
    const newUserMessagingMatch = useMemo(() => {
        const messagedByEmail = new Map();
        messagingRows.forEach((row) => {
            const email = (row.email || '').toString().trim().toLowerCase();
            if (!email) return;
            const existing = messagedByEmail.get(email);
            if (!existing || (row.at || '') < (existing.at || '')) messagedByEmail.set(email, row);
        });

        const matched = [];
        const notMatched = [];
        signupLoginEmailMatch.matched.forEach((entry) => {
            const messagedRow = messagedByEmail.get(entry.username);
            if (messagedRow) {
                matched.push({
                    key: entry.key, username: entry.username,
                    user_id: entry.user_id ?? messagedRow.user_id, ip: messagedRow.ip || entry.ip, message: messagedRow.message,
                    prevAt: entry.login_at, at: messagedRow.at,
                    login_at: entry.login_at, messaged_at: messagedRow.at, channel: messagedRow.channel,
                });
            } else {
                notMatched.push({
                    key: entry.key, username: entry.username, email: entry.username,
                    user_id: entry.user_id, ip: entry.ip,
                    at: entry.login_at, login_at: entry.login_at,
                });
            }
        });
        matched.sort((a, b) => (b.messaged_at || '').localeCompare(a.messaged_at || ''));
        notMatched.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        return { matched, notMatched };
    }, [messagingRows, signupLoginEmailMatch]);

    // Only the message-send rows belonging to users in the "signed up & logged in" cohort
    // (signupLoginEmailMatch.matched) — the New Users tab's "Total Messages Sent" count
    // and channel breakdown are scoped to this, NOT messagingRows' system-wide total, so
    // they always add up against the "Sent a Message" + "No Message Sent" split right
    // below them (which is scoped to the same cohort via newUserMessagingMatch) instead of
    // showing an unrelated bigger number that includes messages from every other user.
    const newUserMessagingRows = useMemo(() => {
        const newUserEmails = new Set(signupLoginEmailMatch.matched.map((e) => e.username));
        return messagingRows.filter((row) => newUserEmails.has((row.email || '').toString().trim().toLowerCase()));
    }, [messagingRows, signupLoginEmailMatch]);

    // Login → Payment, scoped to only NEW users (same "signed up & logged in" cohort as
    // newUserOnboardingMatch/newUserMessagingMatch above) — the "New Users" tab's Payment
    // stage counterpart to funnelPath.payment, which instead pulls "Logged In" from EVERY
    // login in range (not just this range's new signups) and narrows through the sequential
    // Onboarding→Messaging chain via funnelRowKey. That mismatch is what made the New Users
    // tab's Payment numbers include payments from existing accounts just logging back in,
    // instead of only the users this range's Login-from-Signup cohort actually covers. Keyed
    // by email like the other newUser*Match memos, with the same strict exact-text split
    // loginFirstPaymentMatch uses for the All Users tab — "Payment Success"/"Payment
    // Try"/"Payment Failed" are each their own bucket; a Billing row (or user with none
    // at all) matching none of the three isn't counted in any of them.
    const newUserPaymentMatch = useMemo(() => {
        const paidByEmail = new Map();
        const triedByEmail = new Map();
        const failedByEmail = new Map();
        filteredRangeData.forEach((row) => {
            if (getModuleStyle(row.module).queryValue !== 'billing') return;
            const email = (row.email || row.username || '').toString().trim().toLowerCase();
            if (!email) return;
            if (isPaymentSuccessText(row)) {
                const existing = paidByEmail.get(email);
                if (!existing || (row.created_at || '') < (existing.created_at || '')) paidByEmail.set(email, row);
            } else if (isPaymentFailedText(row)) {
                const existing = failedByEmail.get(email);
                if (!existing || (row.created_at || '') < (existing.created_at || '')) failedByEmail.set(email, row);
            } else if (isPaymentTriedText(row)) {
                const existing = triedByEmail.get(email);
                if (!existing || (row.created_at || '') < (existing.created_at || '')) triedByEmail.set(email, row);
            }
        });

        const matched = [];
        const tried = [];
        const failed = [];
        signupLoginEmailMatch.matched.forEach((entry) => {
            const paidRow = paidByEmail.get(entry.username);
            if (paidRow) {
                matched.push({
                    key: entry.key, username: entry.username,
                    user_id: entry.user_id ?? paidRow.user_id, ip: paidRow.ip || entry.ip, message: paidRow.message,
                    prevAt: entry.login_at, at: paidRow.created_at,
                    login_at: entry.login_at, paid_at: paidRow.created_at,
                });
                return;
            }
            const failedRow = failedByEmail.get(entry.username);
            const triedRow = triedByEmail.get(entry.username);
            const notMatchedEntry = {
                key: entry.key, username: entry.username, email: entry.username,
                user_id: entry.user_id, ip: (failedRow || triedRow)?.ip || entry.ip, message: (failedRow || triedRow)?.message,
                at: entry.login_at, login_at: entry.login_at,
            };
            if (failedRow) failed.push(notMatchedEntry);
            else if (triedRow) tried.push(notMatchedEntry);
        });
        matched.sort((a, b) => (b.paid_at || '').localeCompare(a.paid_at || ''));
        tried.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        failed.sort((a, b) => (b.login_at || '').localeCompare(a.login_at || ''));
        return { matched, tried, failed };
    }, [filteredRangeData, signupLoginEmailMatch]);

    // WhatsApp Onboarding → Recharge, scoped to only NEW users who onboarded (i.e. narrows
    // newUserOnboardingMatch.matched above), the "New Users" counterpart to rechargeStats
    // further down, which instead measures every WhatsApp onboarding in range regardless of
    // whether that user is a new signup. Keyed by email like the other newUser*Match memos.
    const newUserRechargeMatch = useMemo(() => {
        const paidByEmail = new Map();
        filteredRangeData.forEach((row) => {
            if (getModuleStyle(row.module).queryValue !== 'billing') return;
            if (!/transaction_payment/i.test(row.event || '')) return;
            const email = (row.email || row.username || '').toString().trim().toLowerCase();
            if (!email) return;
            const existing = paidByEmail.get(email);
            if (!existing || (row.created_at || '') < (existing.created_at || '')) paidByEmail.set(email, row);
        });

        const matched = [];
        const notMatched = [];
        newUserOnboardingMatch.matched.forEach((entry) => {
            const paidRow = paidByEmail.get(entry.username);
            if (paidRow) {
                matched.push({
                    key: entry.key, username: entry.username,
                    user_id: entry.user_id ?? paidRow.user_id, ip: paidRow.ip || entry.ip,
                    prevAt: entry.at, at: paidRow.created_at,
                    onboarded_at: entry.at, recharged_at: paidRow.created_at,
                });
            } else {
                notMatched.push({
                    key: entry.key, username: entry.username, email: entry.username,
                    user_id: entry.user_id, ip: entry.ip,
                    at: entry.at, onboarded_at: entry.at,
                });
            }
        });
        matched.sort((a, b) => (b.recharged_at || '').localeCompare(a.recharged_at || ''));
        notMatched.sort((a, b) => (b.onboarded_at || '').localeCompare(a.onboarded_at || ''));
        return { matched, notMatched };
    }, [filteredRangeData, newUserOnboardingMatch]);

    // Re-shapes newUserOnboardingMatch/newUserRechargeMatch into the exact field names
    // whatsappOnboardingStats/rechargeStats already use (login_user_id/onboarding_user_id,
    // onboarding_ip/payment_ip, etc.) so the bottom "Login → WhatsApp Onboarding Conversion"
    // and "WhatsApp Onboarding → Recharge Conversion" tables can render either the New Users
    // or All Users numbers through the same renderX functions, just swapping which stats
    // object gets passed in based on the Funnel Stage Breakdown tab.
    const newUserOnboardingStats = useMemo(() => {
        const totalLogins = signupLoginEmailMatch.matched.length;
        const totalOnboarded = newUserOnboardingMatch.matched.length;
        return {
            totalLogins,
            totalOnboarded,
            onboardingRate: totalLogins > 0 ? (totalOnboarded / totalLogins) * 100 : 0,
            onboarded: newUserOnboardingMatch.matched.map((r) => ({
                key: r.key, username: r.username,
                login_user_id: r.user_id, onboarding_user_id: r.user_id,
                login_at: r.login_at, onboarded_at: r.onboarded_at,
                login_ip: r.ip, onboarding_ip: r.ip,
            })),
            notOnboarded: newUserOnboardingMatch.notMatched.map((r) => ({
                key: r.key, username: r.username,
                login_user_id: r.user_id, login_at: r.login_at, login_ip: r.ip,
            })),
        };
    }, [signupLoginEmailMatch, newUserOnboardingMatch]);

    const newUserRechargeStats = useMemo(() => {
        const totalOnboarded = newUserOnboardingMatch.matched.length;
        const totalRecharged = newUserRechargeMatch.matched.length;
        return {
            totalOnboarded,
            totalRecharged,
            rechargeRate: totalOnboarded > 0 ? (totalRecharged / totalOnboarded) * 100 : 0,
            recharged: newUserRechargeMatch.matched.map((r) => ({
                key: r.key, username: r.username,
                onboarding_user_id: r.user_id, payment_user_id: r.user_id,
                onboarded_at: r.onboarded_at, recharged_at: r.recharged_at,
                onboarding_ip: r.ip, payment_ip: r.ip,
            })),
            notRecharged: newUserRechargeMatch.notMatched.map((r) => ({
                key: r.key, username: r.username,
                onboarding_user_id: r.user_id, onboarded_at: r.onboarded_at, onboarding_ip: r.ip,
            })),
        };
    }, [newUserOnboardingMatch, newUserRechargeMatch]);

    // Per-stage rows for the "Funnel Stage Breakdown" table — percentages are
    // relative to the total signup cohort (the funnel's own 100% baseline),
    // same convention as the KPI cards and the happy-path chart below.
    const funnelStages = useMemo(() => {
        const totalSignups = signupCompletionSplit.total;
        const pct = (n) => (totalSignups > 0 ? (n / totalSignups) * 100 : 0);

        // Login → WhatsApp Onboarding and Login → Messaging (the "Sent a Message" /
        // "No Message Sent" split, not the raw total) compare NEW-user logins against
        // that step, not signups against it — dividing by totalSignups (like every other
        // stage here) produced nonsense over 100% (e.g. 720%). Their shared baseline is
        // the same "signed up & logged in" cohort signupLoginEmailMatch establishes.
        const totalNewUserLogins = signupLoginEmailMatch.matched.length;
        const pctOfLogins = (n) => (totalNewUserLogins > 0 ? (n / totalNewUserLogins) * 100 : 0);

        // Short "5 Aug – 11 Aug" / "11 Aug" label for the selected date range — the
        // Login-from-Signup subrow labels used to hardcode the word "Today", which was
        // wrong for any range wider than a single day (e.g. "Last 7 Days").
        const rangeLabelShort = (() => {
            if (!startDate || !endDate) return 'in range';
            const opts = { day: 'numeric', month: 'short' };
            return formatDateParam(startDate) === formatDateParam(endDate)
                ? startDate.toLocaleDateString(undefined, opts)
                : `${startDate.toLocaleDateString(undefined, opts)} – ${endDate.toLocaleDateString(undefined, opts)}`;
        })();

        return [
            {
                key: 'signup', label: 'Signup', icon: faUserPlus,
                conversionPct: pct(signupCompletionSplit.completed),
                detailColumns: [
                    { key: 'reason', label: 'Reason' }, { key: 'source', label: 'Source' }, { key: 'verified', label: 'Verified' },
                    { key: 'name', label: 'Name' }, { key: 'mobile', label: 'Mobile' }, { key: 'plan', label: 'Plan' },
                    { key: 'location', label: 'Location' }, { key: 'referredBy', label: 'Assigned Agent' },
                ],
                subrows: [
                    {
                        // Raw count of every Signup-module row in range (Completed + Not
                        // Completed combined) — the "how many signup attempts total"
                        // baseline, shown alongside the split instead of making the admin
                        // add the other two rows together themselves.
                        label: 'Total Signup Attempts', status: 'total', count: signupCompletionSplit.total, pct: pct(signupCompletionSplit.total), duration: '—',
                        rows: [...signupCompletionSplit.completedRows, ...signupCompletionSplit.notCompletedRows].sort((a, b) => (b.at || '').localeCompare(a.at || '')),
                    },
                    { label: 'Signup Completed', status: 'success', count: signupCompletionSplit.completed, pct: pct(signupCompletionSplit.completed), duration: '0 Hrs', rows: signupCompletionSplit.completedRows },
                    { label: 'Signup Not Completed', status: 'dropoff', count: signupCompletionSplit.notCompleted, pct: pct(signupCompletionSplit.notCompleted), duration: '—', rows: signupCompletionSplit.notCompletedRows },
                ],
            },
            
            {
                // New-user cross-check, not a step of the sequential happy path — same
                // "% of total signups" baseline as every other stage here. Like WhatsApp
                // Onboarding/Messaging/Payment below, "Not Logged In Yet" has no attempted-
                // but-failed log row to quote (the user just hasn't logged in at all yet),
                // so its reason is a plain restatement of the outcome, not a quoted message.
                key: 'signup_login_match', label: 'Login from Signup', icon: faSyncAlt,
                conversionPct: pct(signupLoginEmailMatch.matched.length + dataMismatchStats.mismatched.length),
                detailColumns: [{ key: 'signup_at', label: 'Signup At' }, { key: 'reason', label: 'Reason' }],
                subrows: [
                    {
                        // Same users as the "Error in Recording Data" card above (Signup + WhatsApp
                        // Onboarding activity with no Login row at all) are folded directly into this
                        // row's own count/rows — their downstream onboarding activity proves a login
                        // did happen, it just never got logged — the row-level "(Missing Record)"
                        // reason tag (below) marks which specific records these are; the stage
                        // label itself only carries the selected date range, not a backfilled count.
                        label: `Signed Up & Logged In (${rangeLabelShort})`,
                        status: 'success',
                        count: signupLoginEmailMatch.matched.length + dataMismatchStats.mismatched.length,
                        pct: pct(signupLoginEmailMatch.matched.length + dataMismatchStats.mismatched.length),
                        duration: formatDurationBucket(medianDurationHours(signupLoginEmailMatch.matched)),
                        rows: [
                            ...signupLoginEmailMatch.matched.map((r) => ({ ...r, reason: r.message })),
                            ...dataMismatchStats.mismatched.map((r) => ({ ...r, at: r.last_at, reason: '(Missing Record)' })),
                        ],
                    },
                    { label: `Signed Up (${rangeLabelShort}), Not Logged In Yet`, status: 'dropoff', count: signupLoginEmailMatch.notMatched.length, pct: pct(signupLoginEmailMatch.notMatched.length), duration: '—', rows: signupLoginEmailMatch.notMatched.map((r) => ({ ...r, reason: r.message })) },
                ],
            },
            {
                // Direct Login → Onboarding pair for NEW users specifically (newUserOnboardingMatch,
                // keyed by email like signupLoginEmailMatch above) — NOT loginOnboardingMatch, which
                // pairs against every login in range regardless of when that user signed up and so
                // counts existing accounts just logging back in, the wrong universe for this tab.
                key: 'login_onboarding_match', label: 'Login → WhatsApp Onboarding', icon: faWhatsapp,
                conversionPct: pctOfLogins(newUserOnboardingMatch.matched.length),
                detailColumns: [{ key: 'login_at', label: 'Login At' }, { key: 'reason', label: 'Reason' }],
                subrows: [
                    { label: 'Onboarded After Login', status: 'success', count: newUserOnboardingMatch.matched.length, pct: pctOfLogins(newUserOnboardingMatch.matched.length), duration: formatDurationBucket(medianDurationHours(newUserOnboardingMatch.matched)), rows: newUserOnboardingMatch.matched.map((r) => ({ ...r, reason: r.message })) },
                    { label: 'Not Onboarded Yet', status: 'dropoff', count: newUserOnboardingMatch.notMatched.length, pct: pctOfLogins(newUserOnboardingMatch.notMatched.length), duration: '—', rows: newUserOnboardingMatch.notMatched.map((r) => ({ ...r, reason: r.message })) },
                ],
            },
            {
                // "Total Messages Sent" is the raw event count across every channel (not
                // deduped per user) — but scoped to newUserMessagingRows (only messages from
                // users in the "signed up & logged in" cohort), NOT the system-wide
                // messagingRows total, so it always reconciles against "Sent a Message" +
                // "No Message Sent" right below it (newUserMessagingMatch, that same cohort)
                // instead of showing an unrelated, much larger number from every other user.
                key: 'messaging', label: 'First Message', icon: faComments,
                conversionPct: pctOfLogins(newUserMessagingMatch.matched.length),
                detailColumns: [{ key: 'channel', label: 'Channel' }, { key: 'reason', label: 'Reason' }],
                subrows: [
                    { label: 'Total Messages Sent', status: 'total', count: newUserMessagingRows.length, pct: newUserMessagingRows.length > 0 ? 100 : 0, duration: '—', rows: newUserMessagingRows },
                    { label: 'Sent a Message', status: 'success', count: newUserMessagingMatch.matched.length, pct: pctOfLogins(newUserMessagingMatch.matched.length), duration: formatDurationBucket(medianDurationHours(newUserMessagingMatch.matched)), rows: newUserMessagingMatch.matched.map((r) => ({ ...r, reason: r.message })) },
                    { label: 'No Message Sent', status: 'dropoff', count: newUserMessagingMatch.notMatched.length, pct: pctOfLogins(newUserMessagingMatch.notMatched.length), duration: '—', rows: newUserMessagingMatch.notMatched.map((r) => ({ ...r, reason: r.message })) },
                ],
                channelBreakdown: buildChannelBreakdown(newUserMessagingRows),
            },
            {
                // Direct Login → Payment pair for NEW users specifically (newUserPaymentMatch,
                // keyed by email like signupLoginEmailMatch/newUserOnboardingMatch above) — NOT
                // funnelPath.payment, which pairs against every login in range regardless of when
                // that user signed up and so counts existing accounts just logging back in, the
                // wrong universe for this tab. Uses pctOfLogins (the "signed up & logged in"
                // cohort), same baseline as the WhatsApp Onboarding/Messaging stages above.
                key: 'payment', label: 'Payment', icon: faFileInvoiceDollar,
                conversionPct: pctOfLogins(newUserPaymentMatch.matched.length),
                detailColumns: [{ key: 'reason', label: 'Reason' }],
                subrows: [
                    { label: 'Tried Payment', status: 'tried', count: newUserPaymentMatch.tried.length, pct: pctOfLogins(newUserPaymentMatch.tried.length), duration: '—', rows: newUserPaymentMatch.tried.map((r) => ({ ...r, reason: r.message })) },
                    { label: 'Payment Successful', status: 'success', count: newUserPaymentMatch.matched.length, pct: pctOfLogins(newUserPaymentMatch.matched.length), duration: formatDurationBucket(medianDurationHours(newUserPaymentMatch.matched)), rows: newUserPaymentMatch.matched.map((r) => ({ ...r, reason: r.message })) },
                    { label: 'Payment Failed', status: 'dropoff', count: newUserPaymentMatch.failed.length, pct: pctOfLogins(newUserPaymentMatch.failed.length), duration: '—', rows: newUserPaymentMatch.failed.map((r) => ({ ...r, reason: r.message })) },
                ],
            },
        ];
    }, [funnelPath, signupCompletionSplit, signupLoginEmailMatch, newUserOnboardingMatch, newUserMessagingRows, newUserMessagingMatch, newUserPaymentMatch, dataMismatchStats, startDate, endDate]);

    // "All Users" Funnel Stage Breakdown — starts with the direct Login → Payment
    // pair (matched by user_id/email via idOrEmailKey, same relationship the "New
    // Users" tab's stages measure off the signup cohort, just here scoped to every
    // login in range regardless of when that user signed up). More stages get added
    // here over time as this tab grows into its own full breakdown.
    const allUsersFunnelStages = useMemo(() => {
        // This tab's own baseline is total login USERS in range (successful + failed,
        // deduped per user), not the signup cohort — the "New Users" tab's Login stage
        // divides by total signups instead, which is why it shows nonsensical percentages
        // (e.g. 1121%) for logins from users who signed up outside this range or long ago.
        // Missing-record users (see dataMismatchStats — Signup + WhatsApp Onboarding
        // activity but no Login row at all) count toward this stage's own population
        // too, since they're login-eligible users the log just failed to capture.
        const totalLoginUsers = loginAttemptRows.successUserCount + loginAttemptRows.failedUserCount + dataMismatchStats.mismatched.length;
        const pctOfAttempts = (n) => (totalLoginUsers > 0 ? (n / totalLoginUsers) * 100 : 0);

        const totalLogins = loginFirstPaymentMatch.matched.length + loginFirstPaymentMatch.notMatched.length;
        const pct = (n) => (totalLogins > 0 ? (n / totalLogins) * 100 : 0);

        return [
            {
                key: 'login', label: 'Login', icon: faUserShield,
                conversionPct: pctOfAttempts(loginAttemptRows.successUserCount + dataMismatchStats.mismatched.length),
                detailColumns: [{ key: 'reason', label: 'Reason' }],
                subrows: [
                    {
                        // Same population as the "Error in Recording Data" card — proven active
                        // (Signup + WhatsApp Onboarding) but with no Login row on record at all —
                        // folded directly into this row's own count/rows rather than a separate
                        // row/status — the row-level "(Missing Record)" reason tag (below) marks
                        // which specific records these are, instead of a count on the stage label.
                        label: 'Login Successful',
                        status: 'success',
                        count: loginAttemptRows.successUserCount + dataMismatchStats.mismatched.length,
                        pct: pctOfAttempts(loginAttemptRows.successUserCount + dataMismatchStats.mismatched.length),
                        duration: '—',
                        rows: [
                            ...loginAttemptRows.successRows,
                            ...dataMismatchStats.mismatched.map((r) => ({ ...r, at: r.last_at, reason: '(Missing Record)' })),
                        ],
                    },
                    { label: 'Login Failed', status: 'dropoff', count: loginAttemptRows.failedUserCount, pct: pctOfAttempts(loginAttemptRows.failedUserCount), duration: '—', rows: loginAttemptRows.failedRows },
                ],
            },
            {
                // Every successful login in range matched against that same user's WhatsApp
                // onboarding event (loginOnboardingMatch, re-keyed by user_id/email) — the
                // all-users equivalent of the "New Users" tab's WhatsApp Onboarding stage,
                // which instead narrows the signup→login cohort and divides by total signups
                // (the source of that stage's inflated percentages like 707%).
                key: 'onboarding', label: 'WhatsApp Onboarding', icon: faWhatsapp,
                conversionPct: pct(loginOnboardingMatch.matched.length),
                detailColumns: [{ key: 'reason', label: 'Reason' }],
                subrows: [
                    { label: 'Onboarded', status: 'success', count: loginOnboardingMatch.matched.length, pct: pct(loginOnboardingMatch.matched.length), duration: formatDurationBucket(medianDurationHours(loginOnboardingMatch.matched)), rows: loginOnboardingMatch.matched.map((r) => ({ ...r, reason: r.message })) },
                    { label: 'Not Onboarded', status: 'dropoff', count: loginOnboardingMatch.notMatched.length, pct: pct(loginOnboardingMatch.notMatched.length), duration: '—', rows: loginOnboardingMatch.notMatched.map((r) => ({ ...r, reason: r.message })) },
                ],
            },
            {
                // Every "message" module row in range, across every channel, with no cohort
                // filter at all — unlike the "New Users" tab's Messaging stage (scoped to
                // new signups who logged in), this is simply "how much messaging happened,"
                // full stop.
                key: 'messaging', label: 'Messaging', icon: faComments,
                conversionPct: messagingRows.length > 0 ? 100 : 0,
                detailColumns: [{ key: 'channel', label: 'Channel' }, { key: 'reason', label: 'Reason' }],
                subrows: [
                    { label: 'Total Messages Sent', status: 'total', count: messagingRows.length, pct: messagingRows.length > 0 ? 100 : 0, duration: '—', rows: messagingRows },
                ],
                channelBreakdown: buildChannelBreakdown(messagingRows),
            },
            {
                key: 'login_payment_match', label: 'Login → Payment', icon: faFileInvoiceDollar,
                conversionPct: pct(loginFirstPaymentMatch.matched.length),
                detailColumns: [{ key: 'login_at', label: 'Login At' }, { key: 'reason', label: 'Reason' }],
                subrows: [
                    { label: 'Tried Payment', status: 'tried', count: loginFirstPaymentMatch.tried.length, pct: pct(loginFirstPaymentMatch.tried.length), duration: '—', rows: loginFirstPaymentMatch.tried.map((r) => ({ ...r, reason: r.message })) },
                    { label: 'Payment Successful', status: 'success', count: loginFirstPaymentMatch.matched.length, pct: pct(loginFirstPaymentMatch.matched.length), duration: formatDurationBucket(medianDurationHours(loginFirstPaymentMatch.matched)), rows: loginFirstPaymentMatch.matched.map((r) => ({ ...r, reason: r.message })) },
                    { label: 'Payment Failed', status: 'dropoff', count: loginFirstPaymentMatch.failed.length, pct: pct(loginFirstPaymentMatch.failed.length), duration: '—', rows: loginFirstPaymentMatch.failed.map((r) => ({ ...r, reason: r.message })) },
                ],
            },
        ];
    }, [loginAttemptRows, loginOnboardingMatch, loginFirstPaymentMatch, messagingRows, dataMismatchStats]);

    // Whichever tab is active drives the Funnel Stage Breakdown card's rendered stages.
    const activeFunnelStages = funnelBreakdownTab === 'new' ? funnelStages : allUsersFunnelStages;

    // Day-by-day counts for each milestone (Signup Completed / Login Successful /
    // WhatsApp Onboarded / Messaging Sent / Payment Successful) across the selected
    // date range — feeds the "Funnel Activity Trend" multi-line chart below. Each
    // series uses the SAME match condition as its corresponding funnel stage
    // elsewhere on this page, just bucketed by day instead of collapsed into one
    // range-wide total, so the trend reconciles with those totals.
    const funnelTrendData = useMemo(() => {
        const moduleOf = (row) => getModuleStyle(row.module).queryValue;
        const isSignupDone = (row) => moduleOf(row) === 'signup' && isCompletedSignup(row);
        const isLoginDone = (row) => isLoginAttempt(row) && isSuccessfulLoginText(row);
        const isOnboardDone = (row) => moduleOf(row) === 'whatsapp' && isCompletedWhatsappOnboardingText(row);
        const isMessageDone = (row) => isMessagingRow(row) && isMessageSentText(row);
        const isPaymentDone = (row) => moduleOf(row) === 'billing' && isPaymentSuccessText(row);

        const dayMap = new Map();
        const ensureDay = (day) => {
            if (!dayMap.has(day)) {
                dayMap.set(day, { date: day, Signup: 0, Login: 0, 'WA Onboarding': 0, Messaging: 0, Payment: 0 });
            }
            return dayMap.get(day);
        };

        filteredRangeData.forEach((row) => {
            const day = (row.created_at || '').split(' ')[0];
            if (!day) return;
            const entry = ensureDay(day);
            if (isSignupDone(row)) entry.Signup += 1;
            if (isLoginDone(row)) entry.Login += 1;
            if (isOnboardDone(row)) entry['WA Onboarding'] += 1;
            if (isMessageDone(row)) entry.Messaging += 1;
            if (isPaymentDone(row)) entry.Payment += 1;
        });

        return Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date));
        // isCompletedSignup only closes over registeredUsers, which is already listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredRangeData, registeredUsers]);

    // Ranks the stages with the biggest real drop-off (by absolute users lost)
    // and grades severity off what % of the total signup cohort that represents.
    const dropoffPoints = useMemo(() => {
        const totalSignups = signupCompletionSplit.total || 1;
        const points = [
            { key: 'login', label: 'No Login', count: funnelPath.login.dropped.length, moduleValue: 'login' },
            { key: 'onboarding', label: 'WhatsApp Onboarding Not Completed', count: funnelPath.onboarding.dropped.length, moduleValue: 'whatsapp' },
            { key: 'messaging', label: 'No Message Sent', count: funnelPath.messaging.dropped.length, moduleValue: null },
            { key: 'payment', label: 'No Payment Activity', count: funnelPath.payment.none.length, moduleValue: 'billing' },
        ];
        return points
            .map((p) => ({ ...p, pctOfSignups: (p.count / totalSignups) * 100 }))
            .filter((p) => p.count > 0)
            .sort((a, b) => b.count - a.count)
            .map((p) => ({ ...p, severity: p.pctOfSignups >= 40 ? 'High' : p.pctOfSignups >= 15 ? 'Medium' : 'Low' }));
    }, [funnelPath, signupCompletionSplit]);

    // Scroll target for the "View affected users" action on a drop-off point —
    // optionally applies that stage's module filter first, then scrolls the
    // raw Activity Log table into view.
    const activityTableRef = useRef(null);
    const jumpToModule = (moduleValue) => {
        if (moduleValue) {
            const option = MODULE_FILTER_OPTIONS.find((m) => m.value === moduleValue) || null;
            handleModuleChange(option);
        }
        setTimeout(() => {
            if (activityTableRef.current) {
                activityTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 50);
    };

    // Opens the detail modal for one Funnel Stage Breakdown row (e.g. "Signup",
    // "Login") — passes along all of that stage's subrows (success/tried/dropoff)
    // so the modal can show a tab per subrow with its underlying user records.
    const openStagePreview = (stage, initialTab = 0) => {
        setStagePreview({
            stageLabel: stage.label, stageIcon: stage.icon, subrows: stage.subrows,
            detailColumns: stage.detailColumns || [], channelBreakdown: stage.channelBreakdown || null,
        });
        setStagePreviewTab(initialTab);
        setStagePreviewPage(1);
        setStagePreviewChannelFilter(null);
    };

    // "Reason for Failure" opens a separate, lighter-weight modal (Username / IP / Reason
    // only — no tabs, no User ID or Date & Time columns) scoped to exactly the dropoff/tried
    // subrow whose own row button was clicked, instead of the full tabbed "Detailed Preview"
    // modal. The button lives per-subrow (not per-stage) now, so there's no need to guess
    // which of a stage's subrows to show — e.g. Payment has both a "Tried" and a "Drop-off"
    // row, and each opens this scoped to itself.
    const openStageFailurePreview = (stage, subrow) => {
        setReasonPreview({
            stageLabel: stage.label, stageIcon: stage.icon,
            subrowLabel: subrow.label, status: subrow.status, rows: subrow.rows,
        });
    };

    const stagePreviewStatusColor = { success: '#16a34a', tried: '#2563eb', dropoff: '#dc2626', total: '#64748b' };

    // Same user can log multiple raw events within one subrow (e.g. a few failed OTP
    // attempts before a successful one) — the Detailed Records table used to show one
    // row per raw event, so a single user could inflate the visible row count. Grouping
    // by user_id (falling back to username/email, then the row's own key) collapses
    // those into one row per user with an "Attempts" count, keeping the most recent
    // event's fields (rows arrive newest-first) for display and stashing every raw row
    // on the group for the per-user "Preview" button.
    const groupRowsByUser = (list) => {
        const map = new Map();
        list.forEach((r) => {
            const key = r.user_id ? `uid:${r.user_id}` : `u:${(r.username || r.email || r.key || '').toString().trim().toLowerCase()}`;
            let group = map.get(key);
            if (!group) {
                group = { ...r, attempts: 0, allRows: [] };
                map.set(key, group);
            }
            group.attempts += 1;
            group.allRows.push(r);
        });
        return Array.from(map.values());
    };

    // Modal for the "Preview" button on each Funnel Stage Breakdown section —
    // one tab per subrow (e.g. "Signup Completed" / "Signup Not Completed"),
    // each showing the underlying user-level records (username, user ID,
    // timestamp, IP) behind that subrow's count.
    const renderStagePreviewModal = () => {
        if (!stagePreview) return null;
        const activeSubrow = stagePreview.subrows[stagePreviewTab] || stagePreview.subrows[0];
        const allRows = activeSubrow?.rows || [];
        const filteredRows = stagePreviewChannelFilter
            ? allRows.filter((r) => r.channel === stagePreviewChannelFilter)
            : allRows;
        const rows = groupRowsByUser(filteredRows);
        const totalPages = Math.max(1, Math.ceil(rows.length / stagePreviewPageSize));
        const currentPage = Math.min(stagePreviewPage, totalPages);
        const pagedRows = rows.slice((currentPage - 1) * stagePreviewPageSize, currentPage * stagePreviewPageSize);

        return (
            <div
                onClick={() => setStagePreview(null)}
                style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1050, padding: '20px',
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '1100px',
                        maxHeight: '84vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        boxShadow: '0 24px 70px rgba(15,23,42,0.3)', fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                >
                    <div style={{
                        padding: '20px 24px', borderBottom: '1px solid #eef0f6',
                        display: 'flex', alignItems: 'center', gap: '14px', background: '#fafbff',
                    }}>
                        <span style={{
                            width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
                            background: 'linear-gradient(135deg, #6366f1, #4338ca)', color: '#fff', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: '15px',
                            boxShadow: '0 4px 10px rgba(67,56,202,0.3)',
                        }}>
                            <FontAwesomeIcon icon={stagePreview.stageIcon} />
                        </span>
                        <div>
                            <h6 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1a1a2e' }}>
                                {stagePreview.stageLabel} — Detailed Records
                            </h6>
                            <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}>
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '10px' }} />
                                {formatRangeLabel()}
                            </span>
                        </div>
                        <button
                            onClick={() => setStagePreview(null)}
                            title="Close"
                            style={{
                                marginLeft: 'auto', border: 'none', background: '#fff', color: '#64748b',
                                width: '34px', height: '34px', borderRadius: '10px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div style={{
                        padding: '14px 24px', display: 'flex', gap: '10px', flexWrap: 'wrap',
                        background: '#f8fafc', borderBottom: '1px solid #eef0f6',
                    }}>
                        {stagePreview.subrows.map((sub, idx) => {
                            const active = idx === stagePreviewTab;
                            const tabColor = stagePreviewStatusColor[sub.status];
                            return (
                                <button
                                    key={sub.label}
                                    onClick={() => { setStagePreviewTab(idx); setStagePreviewPage(1); setStagePreviewChannelFilter(null); }}
                                    style={{
                                        border: active ? 'none' : '1px solid #e2e8f0', cursor: 'pointer',
                                        borderRadius: '999px', padding: '8px 14px', fontSize: '12px', fontWeight: '700',
                                        display: 'flex', alignItems: 'center', gap: '7px',
                                        background: active ? tabColor : '#fff',
                                        color: active ? '#fff' : '#475569',
                                        boxShadow: active ? `0 4px 10px ${tabColor}55` : 'none',
                                    }}
                                >
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? '#fff' : tabColor }} />
                                    {sub.label}
                                    <span style={{
                                        fontSize: '10px', fontWeight: '700',
                                        color: active ? '#fff' : '#64748b',
                                        background: active ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
                                        borderRadius: '20px', padding: '1px 7px',
                                    }}>
                                        {sub.count.toLocaleString()}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {stagePreview.channelBreakdown && (
                        <div style={{
                            padding: '12px 24px', display: 'flex', gap: '8px', flexWrap: 'wrap',
                            alignItems: 'center', background: '#fff', borderBottom: '1px solid #eef0f6',
                        }}>
                            <span style={{ fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                By Channel
                            </span>
                            <button
                                type="button"
                                onClick={() => { setStagePreviewChannelFilter(null); setStagePreviewPage(1); }}
                                style={{
                                    border: !stagePreviewChannelFilter ? 'none' : '1px solid #e2e8f0', cursor: 'pointer',
                                    fontSize: '11px', fontWeight: '700',
                                    color: !stagePreviewChannelFilter ? '#fff' : '#334155',
                                    background: !stagePreviewChannelFilter ? '#4338ca' : '#fff',
                                    borderRadius: '20px', padding: '4px 12px',
                                }}
                            >
                                All
                            </button>
                            {stagePreview.channelBreakdown.map((c) => {
                                const active = stagePreviewChannelFilter === c.label;
                                return (
                                    <button
                                        key={c.label}
                                        type="button"
                                        onClick={() => { setStagePreviewChannelFilter(active ? null : c.label); setStagePreviewPage(1); }}
                                        title={`Filter to ${c.label}`}
                                        style={{
                                            border: active ? 'none' : '1px solid #e2e8f0', cursor: 'pointer',
                                            fontSize: '11px', fontWeight: '700',
                                            color: active ? '#fff' : '#334155',
                                            background: active ? '#4338ca' : '#f1f5f9',
                                            borderRadius: '20px', padding: '4px 12px',
                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        }}
                                    >
                                        {c.label}
                                        <span style={{ color: active ? 'rgba(255,255,255,0.85)' : '#64748b' }}>{c.count.toLocaleString()}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <div style={{ overflowY: 'auto', overflowX: 'auto', flex: 1 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#fafbfd' }}>
                                    {['Username', 'User ID', 'Date & Time', 'IP Address', ...stagePreview.detailColumns.map((c) => c.label), 'Attempts', 'Preview', 'Journey'].map((h) => (
                                        <th key={h} style={{
                                            padding: '12px 24px', fontSize: '10px', fontWeight: '700',
                                            color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                            letterSpacing: '0.5px', textTransform: 'uppercase',
                                            borderBottom: '1px solid #eef0f6',
                                            position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pagedRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={7 + stagePreview.detailColumns.length} style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '12px' }}>
                                            No records found.
                                        </td>
                                    </tr>
                                ) : (
                                    pagedRows.map((r, i) => (
                                        <tr key={r.key || i} style={{
                                            background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                            borderBottom: '1px solid #f0f0f8',
                                        }}>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155', fontWeight: '600' }}>
                                                {r.username || r.email || '-'}
                                            </td>
                                            <td style={{ padding: '12px 24px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                                {r.user_id ?? '-'}
                                            </td>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>
                                                {r.at || '-'}
                                            </td>
                                            <td style={{ padding: '12px 24px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                                {r.ip || '-'}
                                            </td>
                                            {stagePreview.detailColumns.map((c) => (
                                                <td key={c.key} style={{
                                                    padding: '12px 24px', fontSize: '12px', color: '#334155',
                                                    maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }} title={r[c.key] || ''}>
                                                    {r[c.key] || '-'}
                                                </td>
                                            ))}
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    minWidth: '22px', height: '22px', padding: '0 6px', borderRadius: '999px',
                                                    background: '#eef2ff', color: '#4338ca', fontSize: '11px', fontWeight: '700',
                                                }}>
                                                    {r.attempts}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 24px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setUserAttemptsPreview({
                                                        username: r.username || r.email || '-',
                                                        user_id: r.user_id,
                                                        stageLabel: stagePreview.stageLabel,
                                                        subrowLabel: activeSubrow?.label,
                                                        detailColumns: stagePreview.detailColumns,
                                                        rows: r.allRows,
                                                    })}
                                                    style={{
                                                        border: '1px solid #e2e8f0', cursor: 'pointer', background: '#fff',
                                                        color: '#4338ca', fontSize: '11px', fontWeight: '700',
                                                        borderRadius: '8px', padding: '5px 10px', whiteSpace: 'nowrap',
                                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} style={{ fontSize: '10px' }} />
                                                    Preview
                                                </button>
                                            </td>
                                            <td style={{ padding: '12px 24px' }}>
                                                {renderJourneyButton(r.user_id)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div style={{
                        padding: '14px 24px', borderTop: '1px solid #eef0f6', background: '#fafbfd',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px',
                    }}>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                            {rows.length.toLocaleString()} {rows.length === 1 ? 'user' : 'users'} ({filteredRows.length.toLocaleString()} total {filteredRows.length === 1 ? 'attempt' : 'attempts'}) in "{activeSubrow?.label}"
                        </span>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                Rows per page
                                <select
                                    value={stagePreviewPageSize}
                                    onChange={(e) => { setStagePreviewPageSize(Number(e.target.value)); setStagePreviewPage(1); }}
                                    style={{
                                        border: '1px solid #e2e8f0', borderRadius: '8px', padding: '5px 8px',
                                        fontSize: '12px', fontWeight: '600', color: '#334155', background: '#fff', cursor: 'pointer',
                                    }}
                                >
                                    {STAGE_PREVIEW_PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </label>

                            {totalPages > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <button
                                        onClick={() => setStagePreviewPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        style={{
                                            width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e2e8f0',
                                            background: currentPage === 1 ? '#f8fafc' : '#fff',
                                            color: currentPage === 1 ? '#cbd5e1' : '#334155',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    <span style={{ fontSize: '12px', color: '#334155', fontWeight: '600', padding: '0 6px', whiteSpace: 'nowrap' }}>
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setStagePreviewPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e2e8f0',
                                            background: currentPage === totalPages ? '#f8fafc' : '#fff',
                                            color: currentPage === totalPages ? '#cbd5e1' : '#334155',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const reasonPreviewStatusColor = { success: '#16a34a', tried: '#2563eb', dropoff: '#dc2626', total: '#64748b' };

    // Lighter-weight companion to renderStagePreviewModal() — no tabs, no User ID or
    // Date & Time columns, just the three fields the "Reason for Failure" button exists
    // to surface: who, from where, and why they landed in this stage's failed/dropoff bucket.
    const renderReasonPreviewModal = () => {
        if (!reasonPreview) return null;
        const allRows = reasonPreview.rows || [];
        // Same dedup as the Detailed Records modal — one row per user (most recent
        // reason shown) with an Attempts count and a Preview button for the full list of
        // reasons/timestamps behind it, instead of one row per raw failed attempt.
        const rows = groupRowsByUser(allRows);

        return (
            <div
                onClick={() => setReasonPreview(null)}
                style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1060, padding: '20px',
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '900px',
                        maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        boxShadow: '0 24px 70px rgba(15,23,42,0.3)', fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                >
                    <div style={{
                        padding: '20px 24px', borderBottom: '1px solid #eef0f6',
                        display: 'flex', alignItems: 'center', gap: '14px', background: '#fef7f7',
                    }}>
                        <span style={{
                            width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
                            background: 'linear-gradient(135deg, #f87171, #b91c1c)', color: '#fff', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: '15px',
                            boxShadow: '0 4px 10px rgba(185,28,28,0.3)',
                        }}>
                            <FontAwesomeIcon icon={reasonPreview.stageIcon} />
                        </span>
                        <div>
                            <h6 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1a1a2e' }}>
                                {reasonPreview.stageLabel} — Reason for Failure
                            </h6>
                            <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: reasonPreviewStatusColor[reasonPreview.status] }} />
                                {reasonPreview.subrowLabel} · {formatRangeLabel()}
                            </span>
                        </div>
                        <button
                            onClick={() => setReasonPreview(null)}
                            title="Close"
                            style={{
                                marginLeft: 'auto', border: 'none', background: '#fff', color: '#64748b',
                                width: '34px', height: '34px', borderRadius: '10px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#fafbfd' }}>
                                    {['Email', 'IP Address', 'Reason', 'Attempts', 'Preview', 'Journey'].map((h) => (
                                        <th key={h} style={{
                                            padding: '12px 24px', fontSize: '10px', fontWeight: '700',
                                            color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                            letterSpacing: '0.5px', textTransform: 'uppercase',
                                            borderBottom: '1px solid #eef0f6',
                                            position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '12px' }}>
                                            No records found.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((r, i) => (
                                        <tr key={r.key || i} style={{
                                            background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                            borderBottom: '1px solid #f0f0f8',
                                        }}>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155', fontWeight: '600' }}>
                                                {r.email || r.username || '-'}
                                            </td>
                                            <td style={{ padding: '12px 24px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                                {r.ip || '-'}
                                            </td>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>
                                                {r.reason || '-'}
                                            </td>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    minWidth: '22px', height: '22px', padding: '0 6px', borderRadius: '999px',
                                                    background: '#fee2e2', color: '#b91c1c', fontSize: '11px', fontWeight: '700',
                                                }}>
                                                    {r.attempts}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 24px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setUserAttemptsPreview({
                                                        username: r.email || r.username || '-',
                                                        user_id: r.user_id,
                                                        stageLabel: reasonPreview.stageLabel,
                                                        subrowLabel: reasonPreview.subrowLabel,
                                                        detailColumns: [{ key: 'reason', label: 'Reason' }],
                                                        rows: r.allRows,
                                                    })}
                                                    style={{
                                                        border: '1px solid #e2e8f0', cursor: 'pointer', background: '#fff',
                                                        color: '#b91c1c', fontSize: '11px', fontWeight: '700',
                                                        borderRadius: '8px', padding: '5px 10px', whiteSpace: 'nowrap',
                                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} style={{ fontSize: '10px' }} />
                                                    Preview
                                                </button>
                                            </td>
                                            <td style={{ padding: '12px 24px' }}>
                                                {renderJourneyButton(r.user_id)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div style={{
                        padding: '14px 24px', borderTop: '1px solid #eef0f6', background: '#fafbfd',
                    }}>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                            {rows.length.toLocaleString()} {rows.length === 1 ? 'user' : 'users'} ({allRows.length.toLocaleString()} total {allRows.length === 1 ? 'attempt' : 'attempts'}) in "{reasonPreview.subrowLabel}"
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // "Preview" button on a Detailed Records row — shows every raw attempt behind that
    // one grouped user row (e.g. all 3 failed OTP/signup events collapsed into "Attempts: 3"),
    // instead of a single collapsed line hiding what actually happened.
    const renderUserAttemptsPreviewModal = () => {
        if (!userAttemptsPreview) return null;
        const rows = userAttemptsPreview.rows || [];
        const detailColumns = userAttemptsPreview.detailColumns || [];

        return (
            <div
                onClick={() => setUserAttemptsPreview(null)}
                style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1070, padding: '20px',
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '1100px',
                        maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        boxShadow: '0 24px 70px rgba(15,23,42,0.3)', fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                >
                    <div style={{
                        padding: '20px 24px', borderBottom: '1px solid #eef0f6',
                        display: 'flex', alignItems: 'center', gap: '14px', background: '#fafbff',
                    }}>
                        <span style={{
                            width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
                            background: 'linear-gradient(135deg, #6366f1, #4338ca)', color: '#fff', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: '15px',
                            boxShadow: '0 4px 10px rgba(67,56,202,0.3)',
                        }}>
                            <FontAwesomeIcon icon={faEye} />
                        </span>
                        <div>
                            <h6 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1a1a2e' }}>
                                {userAttemptsPreview.username}
                            </h6>
                            <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}>
                                {userAttemptsPreview.stageLabel} · {userAttemptsPreview.subrowLabel} · {rows.length} {rows.length === 1 ? 'attempt' : 'attempts'}
                            </span>
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {renderJourneyButton(userAttemptsPreview.user_id)}
                            <button
                                onClick={() => setUserAttemptsPreview(null)}
                                title="Close"
                                style={{
                                    border: 'none', background: '#fff', color: '#64748b',
                                    width: '34px', height: '34px', borderRadius: '10px', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    </div>

                    <div style={{ overflowY: 'auto', overflowX: 'auto', flex: 1 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#fafbfd' }}>
                                    {['Date & Time', 'IP Address', ...detailColumns.map((c) => c.label)].map((h) => (
                                        <th key={h} style={{
                                            padding: '12px 24px', fontSize: '10px', fontWeight: '700',
                                            color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                            letterSpacing: '0.5px', textTransform: 'uppercase',
                                            borderBottom: '1px solid #eef0f6',
                                            position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={2 + detailColumns.length} style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '12px' }}>
                                            No records found.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((r, i) => (
                                        <tr key={r.key || i} style={{
                                            background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                            borderBottom: '1px solid #f0f0f8',
                                        }}>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>
                                                {r.at || '-'}
                                            </td>
                                            <td style={{ padding: '12px 24px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                                {r.ip || '-'}
                                            </td>
                                            {detailColumns.map((c) => (
                                                <td key={c.key} style={{
                                                    padding: '12px 24px', fontSize: '12px', color: '#334155',
                                                    maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }} title={r[c.key] || ''}>
                                                    {r[c.key] || '-'}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div style={{
                        padding: '14px 24px', borderTop: '1px solid #eef0f6', background: '#fafbfd',
                    }}>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                            {rows.length.toLocaleString()} {rows.length === 1 ? 'attempt' : 'attempts'} for {userAttemptsPreview.username}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // "Preview" button on any Overall Stats grid card — one shared, filterable detail
    // view over signupCompletionSplit.completedRows, with a dropdown filter per dimension
    // (Employee/Reseller/Country/Page/Plan) so any card is a valid entry point into the
    // same underlying data instead of five separately-scoped modals.
    const renderSignupDetailModal = () => {
        if (!signupDetailOpen) return null;
        const totalPages = Math.max(1, Math.ceil(signupDetailRows.length / signupDetailPageSize));
        const currentPage = Math.min(signupDetailPage, totalPages);
        const pagedRows = signupDetailRows.slice((currentPage - 1) * signupDetailPageSize, currentPage * signupDetailPageSize);
        const hasActiveFilter = Object.values(signupDetailFilters).some(Boolean);

        const filterSelects = [
            { key: 'employee', label: 'Employee', options: signupBreakdowns.employee },
            { key: 'reseller', label: 'Reseller', options: signupBreakdowns.reseller },
            { key: 'country', label: 'Country', options: signupBreakdowns.location },
            { key: 'page', label: 'Page', options: signupBreakdowns.page },
            { key: 'plan', label: 'Plan', options: signupBreakdowns.plan },
        ];

        return (
            <div
                onClick={() => setSignupDetailOpen(false)}
                style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1050, padding: '20px',
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '1100px',
                        maxHeight: '84vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        boxShadow: '0 24px 70px rgba(15,23,42,0.3)', fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                >
                    <div style={{
                        padding: '20px 24px', borderBottom: '1px solid #eef0f6',
                        display: 'flex', alignItems: 'center', gap: '14px', background: '#fafbff',
                    }}>
                        <span style={{
                            width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
                            background: 'linear-gradient(135deg, #6366f1, #4338ca)', color: '#fff', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: '15px',
                            boxShadow: '0 4px 10px rgba(67,56,202,0.3)',
                        }}>
                            <FontAwesomeIcon icon={faUserPlus} />
                        </span>
                        <div>
                            <h6 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1a1a2e' }}>
                                Signup — Detailed Records
                            </h6>
                            <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}>
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '10px' }} />
                                {formatRangeLabel()}
                            </span>
                        </div>
                        <button
                            onClick={() => setSignupDetailOpen(false)}
                            title="Close"
                            style={{
                                marginLeft: 'auto', border: 'none', background: '#fff', color: '#64748b',
                                width: '34px', height: '34px', borderRadius: '10px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div style={{
                        padding: '14px 24px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end',
                        background: '#f8fafc', borderBottom: '1px solid #eef0f6',
                    }}>
                        {filterSelects.map((f) => (
                            <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                                {f.label}
                                <select
                                    value={signupDetailFilters[f.key]}
                                    onChange={(e) => {
                                        // Read e.target.value into a local BEFORE the functional
                                        // setState updater runs — React 16 pools synthetic events
                                        // and nulls out e.target once this handler returns, so
                                        // referencing `e` lazily inside the updater below throws.
                                        const value = e.target.value;
                                        setSignupDetailFilters((prev) => ({ ...prev, [f.key]: value }));
                                        setSignupDetailPage(1);
                                    }}
                                    style={{
                                        border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 8px',
                                        fontSize: '12px', fontWeight: '600', color: '#334155', background: '#fff', cursor: 'pointer', minWidth: '140px',
                                    }}
                                >
                                    <option value="">All</option>
                                    {f.options.map((o) => (
                                        <option key={o.label} value={o.label}>{o.label} ({o.count})</option>
                                    ))}
                                </select>
                            </label>
                        ))}
                        {hasActiveFilter && (
                            <button
                                type="button"
                                onClick={() => { setSignupDetailFilters({ employee: '', reseller: '', country: '', page: '', plan: '' }); setSignupDetailPage(1); }}
                                style={{
                                    border: '1px solid #e2e8f0', cursor: 'pointer', background: '#fff',
                                    color: '#dc2626', fontSize: '11px', fontWeight: '700',
                                    borderRadius: '8px', padding: '7px 12px', whiteSpace: 'nowrap', height: '34px',
                                }}
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>

                    <div style={{ overflowY: 'auto', overflowX: 'auto', flex: 1 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#fafbfd' }}>
                                    {['Username', 'Name', 'Mobile', 'Assigned Agent', 'Country', 'Page', 'Plan', 'Date & Time', 'Journey'].map((h) => (
                                        <th key={h} style={{
                                            padding: '12px 24px', fontSize: '10px', fontWeight: '700',
                                            color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                            letterSpacing: '0.5px', textTransform: 'uppercase',
                                            borderBottom: '1px solid #eef0f6',
                                            position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pagedRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '12px' }}>
                                            No records match these filters.
                                        </td>
                                    </tr>
                                ) : (
                                    pagedRows.map((r, i) => (
                                        <tr key={r.user_id ?? r.email ?? i} style={{
                                            background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                            borderBottom: '1px solid #f0f0f8',
                                        }}>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155', fontWeight: '600' }}>{r.username || r.email || '-'}</td>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>{r.name}</td>
                                            <td style={{ padding: '12px 24px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>{r.mobile}</td>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>{r.referredBy}</td>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>{r.country || '-'}</td>
                                            <td style={{
                                                padding: '12px 24px', fontSize: '12px', color: '#334155',
                                                maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }} title={r.page || ''}>
                                                {r.page || '(direct)'}
                                            </td>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>{r.plan}</td>
                                            <td style={{ padding: '12px 24px', fontSize: '12px', color: '#334155' }}>{r.at || '-'}</td>
                                            <td style={{ padding: '12px 24px' }}>{renderJourneyButton(r.user_id)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div style={{
                        padding: '14px 24px', borderTop: '1px solid #eef0f6', background: '#fafbfd',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px',
                    }}>
                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                            {signupDetailRows.length.toLocaleString()} {signupDetailRows.length === 1 ? 'signup' : 'signups'}
                        </span>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                Rows per page
                                <select
                                    value={signupDetailPageSize}
                                    onChange={(e) => { setSignupDetailPageSize(Number(e.target.value)); setSignupDetailPage(1); }}
                                    style={{
                                        border: '1px solid #e2e8f0', borderRadius: '8px', padding: '5px 8px',
                                        fontSize: '12px', fontWeight: '600', color: '#334155', background: '#fff', cursor: 'pointer',
                                    }}
                                >
                                    {STAGE_PREVIEW_PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </label>

                            {totalPages > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <button
                                        onClick={() => setSignupDetailPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        style={{
                                            width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e2e8f0',
                                            background: currentPage === 1 ? '#f8fafc' : '#fff',
                                            color: currentPage === 1 ? '#cbd5e1' : '#334155',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    <span style={{ fontSize: '12px', color: '#334155', fontWeight: '600', padding: '0 6px', whiteSpace: 'nowrap' }}>
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setSignupDetailPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e2e8f0',
                                            background: currentPage === totalPages ? '#f8fafc' : '#fff',
                                            color: currentPage === totalPages ? '#cbd5e1' : '#334155',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const statCardStyle = {
        flex: '1 1 200px', background: '#fff', borderRadius: '12px',
        border: '1px solid #e8e8f0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '12px',
    };

    const statIconStyle = (bg, color) => ({
        width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
        background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '15px',
    });

    const dashboardCardStyle = {
        flex: '1 1 380px', background: '#fff', borderRadius: '12px',
        border: '1px solid #e8e8f0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        padding: '18px 20px',
    };

    // Shared header row (icon + title + total badge + "Preview" button) for every card
    // in the Overall Stats grid — Overall Stats itself and each of the Employee/Reseller/
    // Location/Page/Plan breakdown cards. Every card's Preview button opens the SAME
    // filterable detail modal (openSignupDetail) — it's a shared entry point, not a
    // per-card pre-scoped view.
    const renderSignupStatCardHeader = (title, icon, color, bg, badge, onPreview) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={statIconStyle(bg, color)}><FontAwesomeIcon icon={icon} /></span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>{title}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {badge != null && (
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color, background: bg,
                        borderRadius: '20px', padding: '2px 10px', whiteSpace: 'nowrap',
                    }}>
                        {badge.toLocaleString()}
                    </span>
                )}
                {onPreview && (
                    <button
                        type="button"
                        onClick={onPreview}
                        title="Preview detailed records"
                        style={{
                            border: '1px solid #e2e8f0', cursor: 'pointer', background: '#fff',
                            color: '#4338ca', width: '26px', height: '26px', borderRadius: '8px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', flexShrink: 0,
                        }}
                    >
                        <FontAwesomeIcon icon={faEye} />
                    </button>
                )}
            </div>
        </div>
    );

    // Employee/Reseller/Location/Page/Plan card — a ranked, scrollable list of every
    // distinct value seen among completed signups this range, each with its own count.
    // `filterKey` is the signupDetailFilters key this breakdown corresponds to (e.g.
    // 'employee', 'country'), so each row's count can drill straight into the shared
    // Signup Detail modal pre-filtered to that one value via openSignupDetailFiltered.
    const renderSignupBreakdownCard = (title, icon, breakdown, color, bg, onPreview, filterKey) => {
        const total = breakdown.reduce((sum, b) => sum + b.count, 0);
        return (
            <div style={{ ...dashboardCardStyle, flex: '1 1 260px' }}>
                {renderSignupStatCardHeader(title, icon, color, bg, total, onPreview)}
                {breakdown.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: '12px' }}>
                        No data in this range.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto', paddingRight: '10px' }}>
                        {breakdown.map((b) => (
                            <div key={b.label} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                                fontSize: '12px', padding: '4px 0', borderBottom: '1px solid #f4f4f8',
                            }}>
                                <span style={{ color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.label}>
                                    {b.label}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => openSignupDetailFiltered(filterKey, b.label)}
                                    title={`View ${title.toLowerCase()} "${b.label}" records`}
                                    style={{
                                        border: 'none', background: 'none', padding: 0, flexShrink: 0,
                                        fontWeight: '700', color: '#1a1a2e', fontSize: '12px', cursor: 'pointer',
                                        textDecoration: 'underline', textDecorationColor: '#c7d2fe', textUnderlineOffset: '2px',
                                    }}
                                >
                                    {b.count.toLocaleString()}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const filterChipStyle = {
        display: 'inline-flex', alignItems: 'center', fontSize: '11px', fontWeight: '600',
        color: '#4f46e5', background: '#eef2ff', border: '1px solid #c7d2fe',
        borderRadius: '20px', padding: '4px 10px', whiteSpace: 'nowrap',
    };

    const formatLastActivity = (raw) => {
        if (!raw) return '—';
        const [datePart, timePart] = raw.split(' ');
        return timePart ? `${datePart} ${timePart}` : datePart;
    };

    const formatRangeLabel = () => {
        if (!startDate || !endDate) return 'All time';
        const opts = { day: 'numeric', month: 'short', year: 'numeric' };
        if (formatDateParam(startDate) === formatDateParam(endDate)) {
            return startDate.toLocaleDateString(undefined, opts);
        }
        return `${startDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${endDate.toLocaleDateString(undefined, opts)}`;
    };

    // Dedicated table for users who both signed up and logged in within the current
    // filtered range — separate from the per-module activity tables below, since it's
    // a cross-module (signup + login) view rather than a single module's raw events.
    const renderConversionTable = () => {
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #bbf7d0',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: '1px solid #bbf7d0',
                    background: '#f0fdf4', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: '#16a34a', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: '1px solid #bbf7d0',
                    }}>
                        <FontAwesomeIcon icon={faUserPlus} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#16a34a' }}>
                        Signup → Login Conversion
                    </h6>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#16a34a',
                        background: '#fff', border: '1px solid #bbf7d0',
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {conversionStats.totalConverted} of {conversionStats.totalSignups} signups converted ({conversionStats.conversionRate.toFixed(1)}%)
                    </span>
                </div>

                <div style={{ maxHeight: '360px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Username', 'User ID', 'Signup Date & Time', 'First Login Date & Time', 'Signup IP', 'Login IP', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {conversionStats.conversions.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No records found.
                                    </td>
                                </tr>
                            ) : (
                                conversionStats.conversions.map((c, i) => (
                                    <tr key={c.key} style={{
                                        background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                        borderBottom: '1px solid #f0f0f8',
                                    }}>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                            {c.username || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                            {c.signup_user_id === c.login_user_id
                                                ? (c.login_user_id ?? '-')
                                                : `${c.signup_user_id ?? '-'} → ${c.login_user_id ?? '-'}`}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {c.signup_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {c.login_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {c.signup_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {c.login_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {renderJourneyButton(c.login_user_id || c.signup_user_id)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Dedicated table for users who signed up but have no matching login row yet
    // within the current filtered range — the complement of renderConversionTable().
    const renderNotConvertedTable = () => {
        const rows = conversionStats.notConverted;
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #fde68a',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: '1px solid #fde68a',
                    background: '#fffbeb', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: '#d97706', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: '1px solid #fde68a',
                    }}>
                        <FontAwesomeIcon icon={faUserPlus} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#d97706' }}>
                        Signed Up, Not Logged In Yet
                    </h6>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#d97706',
                        background: '#fff', border: '1px solid #fde68a',
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {rows.length} of {conversionStats.totalSignups} signups pending ({(100 - conversionStats.conversionRate).toFixed(1)}%)
                    </span>
                </div>

                <div style={{ maxHeight: '360px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Username', 'User ID', 'Signup Date & Time', 'Signup IP', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No records found.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r, i) => (
                                    <tr key={r.key} style={{
                                        background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                        borderBottom: '1px solid #f0f0f8',
                                    }}>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                            {r.username || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                            {r.signup_user_id ?? '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {r.signup_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {r.signup_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {renderJourneyButton(r.signup_user_id)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Flags customers who show up with real Signup or WhatsApp Onboarding activity but
    // no successful Login row on record — reads from dataMismatchStats above.
    const renderDataMismatchCard = () => {
        const rows = dataMismatchStats.mismatched;
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #fecaca',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: '1px solid #fecaca',
                    background: '#fef2f2', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: '#dc2626', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: '1px solid #fecaca',
                    }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#b91c1c' }}>
                        Error in Recording Data
                    </h6>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#b91c1c',
                        background: '#fff', border: '1px solid #fecaca',
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {rows.length} of {dataMismatchStats.totalActivity} customers missing a login record
                    </span>
                </div>
                <div style={{ padding: '10px 20px 0', fontSize: '11px', color: '#94a3b8' }}>
                    Customers with both Signup and WhatsApp Onboarding activity but no matching Login entry — a likely gap in login logging, not necessarily a user who never logged in.
                </div>

                <div style={{ maxHeight: '360px', overflowY: 'auto', overflowX: 'auto', marginTop: '12px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Username', 'User ID', 'Activity Source', 'First Activity', 'Last Activity', 'IP', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No mismatches found — every Signup/Onboarding activity has a matching login.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((m, i) => (
                                    <tr key={m.key} style={{
                                        background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                        borderBottom: '1px solid #f0f0f8',
                                    }}>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                            {m.username || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                            {m.user_id ?? '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', fontWeight: '700', color: '#b91c1c' }}>
                                            {m.source}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {m.first_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {m.last_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {m.ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {renderJourneyButton(m.user_id)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Signup → Login conversions filtered down to only those that happened within a
    // chosen time-to-convert threshold (5 min / 15 min / .../ 4 days, see
    // SIGNUP_LOGIN_TIME_BUCKETS) — same underlying rows as renderConversionTable, just
    // sliced by speed instead of shown in full. The bucket picker re-filters
    // signupLoginTimeFiltered client-side, so switching buckets never refetches; the
    // date-range filter above the page is what actually re-scopes the row set, and since
    // this reads from conversionsWithDuration (derived from conversionStats, which is
    // derived from filteredRangeData) a new date range flows through automatically.
    const renderSignupLoginTimeCard = () => {
        const rows = signupLoginTimeFiltered;
        const activeBucket = SIGNUP_LOGIN_TIME_BUCKETS.find((b) => b.key === signupLoginTimeBucket) || SIGNUP_LOGIN_TIME_BUCKETS[0];
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #c7d2fe',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: '1px solid #c7d2fe',
                    background: '#eef2ff', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: '#4f46e5', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: '1px solid #c7d2fe',
                    }}>
                        <FontAwesomeIcon icon={faClock} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#4338ca' }}>
                        Signup → Login — Time to Convert
                    </h6>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#4338ca',
                        background: '#fff', border: '1px solid #c7d2fe',
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {rows.length} of {conversionStats.totalSignups} signups converted {activeBucket.key === 'all' ? '' : activeBucket.label.toLowerCase()} ({conversionStats.totalSignups > 0 ? ((rows.length / conversionStats.totalSignups) * 100).toFixed(1) : '0.0'}%)
                    </span>
                </div>

                <div style={{ padding: '12px 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label htmlFor="signupLoginTimeBucket" style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>
                        Converted within:
                    </label>
                    <select
                        id="signupLoginTimeBucket"
                        value={signupLoginTimeBucket}
                        onChange={(e) => setSignupLoginTimeBucket(e.target.value)}
                        style={{
                            border: '1px solid #c7d2fe', cursor: 'pointer', borderRadius: '8px',
                            padding: '6px 12px', fontSize: '12px', fontWeight: '700',
                            background: '#fff', color: '#4338ca', outline: 'none',
                        }}
                    >
                        {SIGNUP_LOGIN_TIME_BUCKETS.map((b) => (
                            <option key={b.key} value={b.key}>{b.label}</option>
                        ))}
                    </select>
                </div>

                <div style={{ maxHeight: '360px', overflowY: 'auto', overflowX: 'auto', marginTop: '12px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Username', 'User ID', 'Signup Date & Time', 'First Login Date & Time', 'Time to Convert', 'Signup IP', 'Login IP', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No signups converted {activeBucket.key === 'all' ? '' : activeBucket.label.toLowerCase()} in this range.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((c, i) => (
                                    <tr key={c.key} style={{
                                        background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                        borderBottom: '1px solid #f0f0f8',
                                    }}>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                            {c.username || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                            {c.signup_user_id === c.login_user_id
                                                ? (c.login_user_id ?? '-')
                                                : `${c.signup_user_id ?? '-'} → ${c.login_user_id ?? '-'}`}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {c.signup_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {c.login_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', fontWeight: '700', color: '#4338ca' }}>
                                            {formatExactDuration(c.durationMs)}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {c.signup_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {c.login_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {renderJourneyButton(c.login_user_id || c.signup_user_id)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Dedicated table for users who logged in and then have a WhatsApp onboarding row
    // within the current filtered range. Accepts a `stats` object shaped like
    // whatsappOnboardingStats (all users) or newUserOnboardingStats (new users only) —
    // the caller picks which based on the Funnel Stage Breakdown tab, so this one table
    // renders either scope's data.
    const renderWhatsappOnboardedTable = (stats, scopeLabel) => {
        const rows = stats.onboarded;
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #99f6e4',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: '1px solid #99f6e4',
                    background: '#ecfdf5', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: '#0d9488', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: '1px solid #99f6e4',
                    }}>
                        <FontAwesomeIcon icon={faWhatsapp} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#0d9488' }}>
                        Login → WhatsApp Onboarding Conversion
                    </h6>
                    <span style={{
                        fontSize: '10px', fontWeight: '700', color: '#0d9488',
                        background: '#ecfdf5', border: '1px solid #99f6e4',
                        borderRadius: '20px', padding: '2px 9px',
                    }}>
                        {scopeLabel}
                    </span>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#0d9488',
                        background: '#fff', border: '1px solid #99f6e4',
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {stats.totalOnboarded} of {stats.totalLogins} logins onboarded ({stats.onboardingRate.toFixed(1)}%)
                    </span>
                </div>

                <div style={{ maxHeight: '360px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Username', 'User ID', 'Login Date & Time', 'Onboarded Date & Time', 'Login IP', 'Onboarding IP', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No records found.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((c, i) => (
                                    <tr key={c.key} style={{
                                        background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                        borderBottom: '1px solid #f0f0f8',
                                    }}>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                            {c.username || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                            {c.login_user_id === c.onboarding_user_id
                                                ? (c.onboarding_user_id ?? '-')
                                                : `${c.login_user_id ?? '-'} → ${c.onboarding_user_id ?? '-'}`}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {c.login_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {c.onboarded_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {c.login_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {c.onboarding_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {renderJourneyButton(c.onboarding_user_id || c.login_user_id)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Dedicated table for users who logged in but have no WhatsApp onboarding row
    // yet — the complement of renderWhatsappOnboardedTable(). Same `stats`/`scopeLabel`
    // pass-through as that function.
    const renderWhatsappNotOnboardedTable = (stats, scopeLabel) => {
        const rows = stats.notOnboarded;
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #fecaca',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: '1px solid #fecaca',
                    background: '#fef2f2', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: '#dc2626', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: '1px solid #fecaca',
                    }}>
                        <FontAwesomeIcon icon={faWhatsapp} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#dc2626' }}>
                        Logged In, Not Onboarded on WhatsApp Yet
                    </h6>
                    <span style={{
                        fontSize: '10px', fontWeight: '700', color: '#dc2626',
                        background: '#fef2f2', border: '1px solid #fecaca',
                        borderRadius: '20px', padding: '2px 9px',
                    }}>
                        {scopeLabel}
                    </span>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#dc2626',
                        background: '#fff', border: '1px solid #fecaca',
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {rows.length} of {stats.totalLogins} logins pending ({(100 - stats.onboardingRate).toFixed(1)}%)
                    </span>
                </div>

                <div style={{ maxHeight: '360px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Username', 'User ID', 'Login Date & Time', 'Login IP', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No records found.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r, i) => (
                                    <tr key={r.key} style={{
                                        background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                        borderBottom: '1px solid #f0f0f8',
                                    }}>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                            {r.username || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                            {r.login_user_id ?? '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {r.login_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {r.login_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {renderJourneyButton(r.login_user_id)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Dedicated table for users who onboarded on WhatsApp and then have a completed
    // payment (transaction_payment) row within the current filtered range. Accepts a
    // `stats` object shaped like rechargeStats (all users) or newUserRechargeStats
    // (new users only) — same pattern as renderWhatsappOnboardedTable above.
    const renderRechargedTable = (stats, scopeLabel) => {
        const rows = stats.recharged;
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #fde68a',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: '1px solid #fde68a',
                    background: '#fffbeb', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: '#d97706', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: '1px solid #fde68a',
                    }}>
                        <FontAwesomeIcon icon={faFileInvoiceDollar} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#d97706' }}>
                        WhatsApp Onboarding → Recharge Conversion
                    </h6>
                    <span style={{
                        fontSize: '10px', fontWeight: '700', color: '#d97706',
                        background: '#fffbeb', border: '1px solid #fde68a',
                        borderRadius: '20px', padding: '2px 9px',
                    }}>
                        {scopeLabel}
                    </span>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#d97706',
                        background: '#fff', border: '1px solid #fde68a',
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {stats.totalRecharged} of {stats.totalOnboarded} onboarded users recharged ({stats.rechargeRate.toFixed(1)}%)
                    </span>
                </div>

                <div style={{ maxHeight: '360px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Username', 'User ID', 'Onboarded Date & Time', 'Recharged Date & Time', 'Onboarding IP', 'Payment IP', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No records found.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((c, i) => (
                                    <tr key={c.key} style={{
                                        background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                        borderBottom: '1px solid #f0f0f8',
                                    }}>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                            {c.username || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                            {c.onboarding_user_id === c.payment_user_id
                                                ? (c.payment_user_id ?? '-')
                                                : `${c.onboarding_user_id ?? '-'} → ${c.payment_user_id ?? '-'}`}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {c.onboarded_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {c.recharged_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {c.onboarding_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {c.payment_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {renderJourneyButton(c.payment_user_id || c.onboarding_user_id)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Dedicated table for users who onboarded on WhatsApp but have no completed
    // payment row yet — the complement of renderRechargedTable(). Same `stats`/
    // `scopeLabel` pass-through as that function.
    const renderNotRechargedTable = (stats, scopeLabel) => {
        const rows = stats.notRecharged;
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #fecaca',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: '1px solid #fecaca',
                    background: '#fef2f2', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: '#dc2626', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: '1px solid #fecaca',
                    }}>
                        <FontAwesomeIcon icon={faFileInvoiceDollar} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#dc2626' }}>
                        Onboarded on WhatsApp, Not Recharged Yet
                    </h6>
                    <span style={{
                        fontSize: '10px', fontWeight: '700', color: '#dc2626',
                        background: '#fef2f2', border: '1px solid #fecaca',
                        borderRadius: '20px', padding: '2px 9px',
                    }}>
                        {scopeLabel}
                    </span>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#dc2626',
                        background: '#fff', border: '1px solid #fecaca',
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {rows.length} of {stats.totalOnboarded} onboarded users pending ({(100 - stats.rechargeRate).toFixed(1)}%)
                    </span>
                </div>

                <div style={{ maxHeight: '360px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Username', 'User ID', 'Onboarded Date & Time', 'Onboarding IP', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No records found.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r, i) => (
                                    <tr key={r.key} style={{
                                        background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                        borderBottom: '1px solid #f0f0f8',
                                    }}>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                            {r.username || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                            {r.onboarding_user_id ?? '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                            {r.onboarded_at || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                            {r.onboarding_ip || '-'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {renderJourneyButton(r.onboarding_user_id)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderDashboard = () => {
        if (!searched || loading) return null;
        if (logState.data.length === 0) return null;

        return (
            <>
                {/* ── Conversion Funnel hero: KPI row + Happy-Path chart + Drop-off points ── */}
                <div style={{ marginTop: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                            <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1a1a2e' }}>
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px', color: '#4f46e5' }} />
                                Conversion Funnel — {formatRangeLabel()}
                            </h6>
                            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#94a3b8' }}>
                                Signup → Login → WhatsApp Onboarding → Messaging → Payment
                            </p>
                        </div>
                        {rangeStats.truncated && (
                            <span style={{ fontSize: '11px', color: '#d97706', fontWeight: '600' }}>
                                Showing stats from the first {MAX_STAT_PAGES} pages loaded for this range — narrow the date range for exact totals.
                            </span>
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '14px' }}>
                        {/* Overall Stats — each metric's value comes from the SAME underlying
                            cohort/computation already used elsewhere on this page, so this list
                            always reconciles with the Funnel Stage Breakdown and happy-path chart
                            below rather than introducing a second, drifting source of truth:
                            - Signup: signupCompletionSplit.total (the funnel's 100% baseline).
                            - Login / WA Onboarding: the "new signup who also did X" cohorts
                              (signupLoginEmailMatch / newUserOnboardingMatch), matching the
                              "Login from Signup" / "Login → WhatsApp Onboarding" stages.
                            - Payment Success / Failed: funnelPath.payment's paid vs tried-but-
                              didn't-pay split (same source as the old "Payments Successful" card).
                            - First Message: newUserMessagingMatch, matching the "First Message"
                              stage's own "Sent a Message" subrow. */}
                        <div style={dashboardCardStyle}>
                            {renderSignupStatCardHeader('Overall Stats', faUserPlus, '#4f46e5', '#eef2ff', null, openSignupDetail)}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {[
                                    {
                                        label: 'Signup', value: signupCompletionSplit.completed, icon: faUserPlus, status: 'success',
                                        rows: signupCompletionSplit.completedRows,
                                        detailColumns: [
                                            { key: 'reason', label: 'Reason' }, { key: 'source', label: 'Source' }, { key: 'verified', label: 'Verified' },
                                            { key: 'name', label: 'Name' }, { key: 'mobile', label: 'Mobile' }, { key: 'plan', label: 'Plan' },
                                            { key: 'location', label: 'Location' }, { key: 'referredBy', label: 'Assigned Agent' },
                                        ],
                                    },
                                    {
                                        label: 'Login', value: signupLoginEmailMatch.matched.length, icon: faSyncAlt, status: 'success',
                                        rows: signupLoginEmailMatch.matched.map((r) => ({ ...r, reason: r.message })),
                                        detailColumns: [{ key: 'signup_at', label: 'Signup At' }, { key: 'reason', label: 'Reason' }],
                                    },
                                    {
                                        label: 'WhatsApp Onboarding', value: newUserOnboardingMatch.matched.length, icon: faWhatsapp, status: 'success',
                                        rows: newUserOnboardingMatch.matched.map((r) => ({ ...r, reason: r.message })),
                                        detailColumns: [{ key: 'login_at', label: 'Login At' }, { key: 'reason', label: 'Reason' }],
                                    },
                                    {
                                        label: 'Payment Success', value: funnelPath.payment.paid.length, icon: faFileInvoiceDollar, status: 'success',
                                        rows: funnelPath.payment.paid, detailColumns: [],
                                    },
                                    {
                                        label: 'Payment Failed', value: funnelPath.payment.tried.length, icon: faFileInvoiceDollar, status: 'dropoff',
                                        rows: funnelPath.payment.tried, detailColumns: [],
                                    },
                                    {
                                        label: 'First Message', value: newUserMessagingMatch.matched.length, icon: faComments, status: 'success',
                                        rows: newUserMessagingMatch.matched.map((r) => ({ ...r, reason: r.message })),
                                        detailColumns: [{ key: 'channel', label: 'Channel' }, { key: 'reason', label: 'Reason' }],
                                    },
                                ].map((m) => (
                                    <div key={m.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                                        <span style={{ color: '#64748b', fontWeight: '600' }}>{m.label}</span>
                                        <button
                                            type="button"
                                            onClick={() => m.value > 0 && openStagePreview({
                                                label: `Overall Stats — ${m.label}`, icon: m.icon,
                                                detailColumns: m.detailColumns,
                                                subrows: [{ label: m.label, status: m.status, count: m.value, pct: m.value > 0 ? 100 : 0, duration: '—', rows: m.rows }],
                                            })}
                                            disabled={m.value === 0}
                                            title={m.value > 0 ? `View ${m.label} records` : undefined}
                                            style={{
                                                border: 'none', background: 'none', padding: 0,
                                                color: '#1a1a2e', fontWeight: '800', fontSize: '14px',
                                                cursor: m.value > 0 ? 'pointer' : 'default',
                                                textDecoration: m.value > 0 ? 'underline' : 'none',
                                                textDecorationColor: '#c7d2fe', textUnderlineOffset: '2px',
                                            }}
                                        >
                                            {m.value.toLocaleString()}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {renderSignupBreakdownCard('Employee', faUserShield, signupBreakdowns.employee, '#16a34a', '#f0fdf4', openSignupDetail, 'employee')}
                        {renderSignupBreakdownCard('Reseller', faHandshake, signupBreakdowns.reseller, '#0891b2', '#ecfeff', openSignupDetail, 'reseller')}
                        {renderSignupBreakdownCard('Location', faMapMarkerAlt, signupBreakdowns.location, '#0d9488', '#ecfdf5', openSignupDetail, 'country')}
                        {renderSignupBreakdownCard('Page', faFileAlt, signupBreakdowns.page, '#d97706', '#fffbeb', openSignupDetail, 'page')}
                        {renderSignupBreakdownCard('Plan', faTags, signupBreakdowns.plan, '#7c3aed', '#f5f3ff', openSignupDetail, 'plan')}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '16px', alignItems: 'stretch' }}>
                        <div style={{ ...dashboardCardStyle, flex: '2 1 480px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c7d2fe', display: 'inline-block' }} />
                                <span style={{ fontSize: '10px', fontWeight: '700', color: '#a0a7c4', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                    Hover graph points to inspect daily totals
                                </span>
                            </div>
                            <h6 style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '13px', marginBottom: '2px' }}>Funnel Activity Trend</h6>
                            <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px' }}>Daily counts for each milestone, across the selected range</p>
                            {funnelTrendData.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: '12px' }}>
                                    No activity in this range to build a trend from.
                                </div>
                            ) : funnelTrendData.length === 1 ? (
                                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', fontSize: '12px' }}>
                                    Only one day of data in the "{activePresetKey === 'custom' ? 'selected' : DATE_PRESETS.find((p) => p.key === activePresetKey)?.label.toLowerCase()}" range —
                                    pick a wider range (e.g. "Last 7 Days") to see the daily trend line.
                                </div>
                            ) : (
                                <>
                                    <ResponsiveContainer width="100%" height={280}>
                                        <LineChart data={funnelTrendData} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
                                            <CartesianGrid stroke="#eef0f6" strokeDasharray="4 4" vertical={false} />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 11, fill: '#a0a7c4' }}
                                                axisLine={false}
                                                tickLine={false}
                                                tickFormatter={(d) => {
                                                    const dt = parseLogDate(d);
                                                    if (!dt) return d;
                                                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                                    return `${months[dt.getMonth()]} ${String(dt.getDate()).padStart(2, '0')}`;
                                                }}
                                            />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#a0a7c4' }} axisLine={false} tickLine={false} width={40} />
                                            <Tooltip content={<FunnelTrendTooltip />} />
                                            {FUNNEL_TREND_SERIES.map((s) => (
                                                <Line
                                                    key={s.key} type="natural" dataKey={s.key} name={s.key}
                                                    stroke={s.color} strokeWidth={2.5}
                                                    dot={false}
                                                    activeDot={{ r: 4, strokeWidth: 0 }}
                                                />
                                            ))}
                                        </LineChart>
                                    </ResponsiveContainer>

                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
                                        gap: '10px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f8',
                                    }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                            {FUNNEL_TREND_SERIES.map((s) => (
                                                <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b', fontWeight: '600' }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                                                    {s.key}
                                                </span>
                                            ))}
                                        </div>
                                        <span style={{
                                            fontSize: '11px', fontWeight: '700', color: '#4338ca', background: '#eef2ff',
                                            borderRadius: '20px', padding: '4px 12px', whiteSpace: 'nowrap',
                                        }}>
                                            Daily aggregates
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={{ ...dashboardCardStyle, flex: '1 1 280px' }}>
                            <h6 style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '13px', marginBottom: '2px' }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#d97706', marginRight: '6px' }} />
                                Drop-off Points
                            </h6>
                            <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px' }}>Where the most users are lost, this range</p>
                            {dropoffPoints.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                    No drop-off recorded in this range.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {dropoffPoints.map((p) => (
                                        <div key={p.key} style={{ border: '1px solid #f0f0f8', borderRadius: '10px', padding: '10px 12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e' }}>{p.label}</span>
                                                <span style={{
                                                    fontSize: '10px', fontWeight: '700', borderRadius: '20px', padding: '2px 9px', whiteSpace: 'nowrap',
                                                    color: p.severity === 'High' ? '#dc2626' : p.severity === 'Medium' ? '#d97706' : '#64748b',
                                                    background: p.severity === 'High' ? '#fef2f2' : p.severity === 'Medium' ? '#fffbeb' : '#f1f5f9',
                                                }}>
                                                    {p.severity}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                                {p.count.toLocaleString()} users lost ({p.pctOfSignups.toFixed(1)}% of signups)
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => jumpToModule(p.moduleValue)}
                                                style={{
                                                    marginTop: '6px', border: 'none', background: 'none', padding: 0,
                                                    fontSize: '11px', fontWeight: '600', color: '#4f46e5', cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                }}
                                            >
                                                View affected users <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '9px' }} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Potential Customers Lost: users who COMPLETED signup but haven't
                        logged in yet (signupLoginEmailMatch.notMatched) — not signup failures —
                        split by email domain type — Personal Domain vs Gmail — each its own
                        scrollable list. Sits right before the Funnel Stage Breakdown card below. ── */}
                    <div style={{ ...dashboardCardStyle, flex: 'none', marginTop: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                            <div>
                                <h6 style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '13px', margin: 0 }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#dc2626', marginRight: '6px' }} />
                                    Potential Customers Lost
                                </h6>
                                <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 0' }}>
                                    Signed up but not logged in yet, grouped by email domain type
                                </p>
                            </div>
                            <span style={{
                                fontSize: '11px', fontWeight: '700', color: '#dc2626', background: '#fef2f2',
                                borderRadius: '20px', padding: '3px 10px', whiteSpace: 'nowrap',
                            }}>
                                {(lostSignupsByDomain.personalDomain.length + lostSignupsByDomain.gmail.length).toLocaleString()} lost
                            </span>
                        </div>

                        {(() => {
                            const lostCustomerGroups = [
                                {
                                    key: 'personal', label: 'High Potential Customer Who Lost', color: '#d97706', bg: '#fffbeb',
                                    // subtitle: 'All non-Gmail email domains — personal providers and company/business domains alike.',
                                    rows: lostSignupsByDomain.personalDomain,
                                },
                                {
                                    key: 'gmail', label: 'Low Potential Customer Who Lost', color: '#dc2626', bg: '#fef2f2',
                                    // subtitle: '@gmail.com addresses',
                                    rows: lostSignupsByDomain.gmail,
                                },
                            ];
                            const activeGroup = lostCustomerGroups.find((g) => g.key === lostCustomersTab) || lostCustomerGroups[0];
                            return (
                                <>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                                        {lostCustomerGroups.map((group) => {
                                            const active = lostCustomersTab === group.key;
                                            return (
                                                <button
                                                    key={group.key}
                                                    type="button"
                                                    onClick={() => setLostCustomersTab(group.key)}
                                                    style={{
                                                        border: active ? 'none' : '1px solid #e2e8f0', cursor: 'pointer',
                                                        borderRadius: '999px', padding: '7px 16px', fontSize: '12px', fontWeight: '700',
                                                        background: active ? group.color : '#fff',
                                                        color: active ? '#fff' : '#475569',
                                                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                                                    }}
                                                >
                                                    {group.label}
                                                    <span style={{
                                                        fontSize: '10px', fontWeight: '700', borderRadius: '20px', padding: '1px 8px',
                                                        background: active ? 'rgba(255,255,255,0.25)' : group.bg,
                                                        color: active ? '#fff' : group.color,
                                                    }}>
                                                        {group.rows.length.toLocaleString()}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div style={{ marginTop: '12px', border: '1px solid #f0f0f8', borderRadius: '10px', overflow: 'hidden' }}>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', padding: '12px 14px 0' }}>{activeGroup.subtitle}</div>
                                        {activeGroup.rows.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: '12px' }}>
                                                No pending logins in this range.
                                            </div>
                                        ) : (
                                            <div style={{ maxHeight: '260px', overflow: 'auto', margin: '10px 0 0' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ background: '#fafbfd' }}>
                                                            {['Email', 'IP Address', 'Attempts', 'Preview', 'Journey'].map((h) => (
                                                                <th key={h} style={{
                                                                    padding: '8px 14px', fontSize: '10px', fontWeight: '700',
                                                                    color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                                                    letterSpacing: '0.5px', textTransform: 'uppercase',
                                                                    borderBottom: '1px solid #eef0f6',
                                                                    position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                                                }}>
                                                                    {h}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {groupRowsByUser(activeGroup.rows).map((r, i) => (
                                                            <tr key={`${activeGroup.key}-${r.user_id || r.email || i}`} style={{
                                                                background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                                                borderBottom: '1px solid #f0f0f8',
                                                            }}>
                                                                <td style={{ padding: '10px 14px', fontSize: '12px', color: '#334155', fontWeight: '600' }}>
                                                                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', color: '#94a3b8', fontSize: '10px' }} />
                                                                    {r.email || r.username || '-'}
                                                                </td>
                                                                <td style={{ padding: '10px 14px', fontSize: '11px', color: '#334155', fontFamily: 'Consolas, monospace' }}>
                                                                    {r.ip || '-'}
                                                                </td>
                                                                {/* <td style={{ padding: '10px 14px', fontSize: '12px', color: '#334155' }}>
                                                                    {r.reason || '-'}
                                                                </td> */}
                                                                <td style={{ padding: '10px 14px', fontSize: '12px', color: '#334155' }}>
                                                                    <span style={{
                                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                        minWidth: '22px', height: '22px', padding: '0 6px', borderRadius: '999px',
                                                                        background: activeGroup.bg, color: activeGroup.color, fontSize: '11px', fontWeight: '700',
                                                                    }}>
                                                                        {r.attempts}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '10px 14px' }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setUserAttemptsPreview({
                                                                            username: r.email || r.username || '-',
                                                                            user_id: r.user_id,
                                                                            stageLabel: 'Potential Customers Lost',
                                                                            subrowLabel: activeGroup.label,
                                                                            detailColumns: [{ key: 'reason', label: 'Reason' }],
                                                                            rows: r.allRows,
                                                                        })}
                                                                        style={{
                                                                            border: '1px solid #e2e8f0', cursor: 'pointer', background: '#fff',
                                                                            color: activeGroup.color, fontSize: '11px', fontWeight: '700',
                                                                            borderRadius: '8px', padding: '5px 10px', whiteSpace: 'nowrap',
                                                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEye} style={{ fontSize: '10px' }} />
                                                                        Preview
                                                                    </button>
                                                                </td>
                                                                <td style={{ padding: '10px 14px' }}>
                                                                    {renderJourneyButton(r.user_id)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </>
                            );
                        })()}
                    </div>

                    <div style={{ ...dashboardCardStyle, flex: 'none', marginTop: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                            <div>
                                <h6 style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '13px', margin: 0 }}>Funnel Stage Breakdown</h6>
                                <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 0' }}>
                                    All {activeFunnelStages.reduce((s, st) => s + st.subrows.length, 0)} states across {activeFunnelStages.length} milestone groups
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', fontSize: '11px', fontWeight: '600', flexWrap: 'wrap' }}>
                                {[
                                    { label: 'Total', color: STAGE_STATUS_COLOR.total, bg: STAGE_STATUS_BG.total },
                                    { label: 'Success', color: STAGE_STATUS_COLOR.success, bg: '#f0fdf4' },
                                    { label: 'Tried', color: STAGE_STATUS_COLOR.tried, bg: '#eff6ff' },
                                    { label: 'Drop-off', color: STAGE_STATUS_COLOR.dropoff, bg: '#fef2f2' },
                                ].map((legend) => (
                                    <span key={legend.label} style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                                        color: legend.color, background: legend.bg,
                                        borderRadius: '20px', padding: '3px 10px',
                                    }}>
                                        <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: legend.color }} />
                                        {legend.label}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                            {[
                                { key: 'new', label: 'New Users' },
                                { key: 'all', label: 'All Users' },
                            ].map((tab) => {
                                const active = funnelBreakdownTab === tab.key;
                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => setFunnelBreakdownTab(tab.key)}
                                        style={{
                                            border: active ? 'none' : '1px solid #e2e8f0', cursor: 'pointer',
                                            borderRadius: '999px', padding: '7px 16px', fontSize: '12px', fontWeight: '700',
                                            background: active ? '#4338ca' : '#fff',
                                            color: active ? '#fff' : '#475569',
                                            boxShadow: active ? '0 4px 10px rgba(67,56,202,0.35)' : 'none',
                                        }}
                                    >
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={{ marginTop: '14px' }}>
                            {activeFunnelStages.map((stage, idx) => (
                                <div key={stage.key} style={{ borderTop: idx === 0 ? 'none' : '1px solid #eef0f6' }}>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px',
                                        background: '#f8fafc', margin: '0 -20px', padding: '8px 20px',
                                    }}>
                                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FontAwesomeIcon icon={stage.icon} style={{ color: '#64748b', fontSize: '13px' }} />
                                            {stage.label}
                                        </span>
                                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>{stage.conversionPct.toFixed(0)}% conversion</span>
                                            <button
                                                type="button"
                                                onClick={() => openStagePreview(stage)}
                                                title={`Preview ${stage.label} records`}
                                                style={{
                                                    border: 'none', background: '#eef2ff', color: '#4338ca',
                                                    fontSize: '12px', fontWeight: '700', borderRadius: '8px',
                                                    padding: '7px 14px', cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                    boxShadow: '0 1px 2px rgba(67,56,202,0.15)',
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} style={{ fontSize: '11px' }} />
                                                Detailed Preview
                                            </button>
                                        </span>
                                    </div>
                                    {stage.subrows.map((row, rowIdx) => (
                                        // Each event/subrow renders as its own self-contained row —
                                        // status carries both its color AND label into a standalone
                                        // pill (not just colored text), so a new status (e.g. 'total')
                                        // only needs an entry in STAGE_STATUS_COLOR/BG/LABEL to render
                                        // correctly here, no per-row special-casing. The "Reason for
                                        // Failure" action lives in its own dedicated last column (not
                                        // squeezed in next to duration) so it always fits, and only
                                        // 'dropoff' rows populate it — 'tried' (e.g. Payment's "Tried
                                        // Payment" row) isn't a failure, just an in-progress attempt,
                                        // so it has nothing to show a "reason" for.
                                        <div key={row.label} style={{
                                            display: 'grid', gridTemplateColumns: '1.5fr 0.4fr 0.5fr 0.6fr 0.6fr 1fr',
                                            alignItems: 'center', gap: '8px', padding: '7px 4px 7px 20px', fontSize: '12px',
                                            borderTop: rowIdx === 0 ? 'none' : '1px solid #eef0f6',
                                        }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#334155', fontWeight: '500' }}>
                                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: STAGE_STATUS_COLOR[row.status], flexShrink: 0 }} />
                                                {row.label}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => row.count > 0 && openStagePreview(stage, rowIdx)}
                                                disabled={row.count === 0}
                                                title={row.count > 0 ? `View ${row.label} records` : undefined}
                                                style={{
                                                    border: 'none', background: 'none', padding: 0, textAlign: 'left',
                                                    color: '#1a1a2e', fontWeight: '700', fontSize: '12px',
                                                    cursor: row.count > 0 ? 'pointer' : 'default',
                                                    textDecoration: row.count > 0 ? 'underline' : 'none',
                                                    textDecorationColor: '#c7d2fe', textUnderlineOffset: '2px',
                                                }}
                                            >
                                                {row.count.toLocaleString()}
                                            </button>
                                            <span style={{
                                                fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', width: 'fit-content',
                                                color: '#475569', background: '#f1f5f9',
                                            }}>
                                                {row.pct.toFixed(0)}%
                                            </span>
                                            <span style={{
                                                fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', width: 'fit-content',
                                                color: STAGE_STATUS_COLOR[row.status], background: STAGE_STATUS_BG[row.status],
                                            }}>
                                                {STAGE_STATUS_LABEL[row.status]}
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textAlign: 'right' }}>
                                                {row.duration}
                                            </span>
                                            <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                {row.status === 'dropoff' && row.count > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openStageFailurePreview(stage, row)}
                                                        title={`Preview ${row.label} reasons`}
                                                        style={{
                                                            border: 'none', background: '#fef2f2', color: '#b91c1c',
                                                            fontSize: '11px', fontWeight: '700', borderRadius: '8px',
                                                            padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                                                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                            boxShadow: '0 1px 2px rgba(185,28,28,0.12)',
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '10px' }} />
                                                        Reason for Failure
                                                    </button>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginTop: '28px' }}>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>
                        Activity Overview
                    </h6>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '10px' }}>
                    <div style={statCardStyle}>
                        <span style={statIconStyle('#eef2ff', '#4f46e5')}>
                            <FontAwesomeIcon icon={faListUl} />
                        </span>
                        <div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a2e' }}>
                                {dashboardStats.totalInRange.toLocaleString()}{rangeStats.truncated ? '+' : ''}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Total in Range</div>
                        </div>
                    </div>
                    <div style={statCardStyle}>
                        <span style={statIconStyle('#f5f3ff', '#7c3aed')}>
                            <FontAwesomeIcon icon={faLayerGroup} />
                        </span>
                        <div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a2e' }}>
                                {dashboardStats.uniqueModules}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Modules Active (in range)</div>
                        </div>
                    </div>
                    <div style={statCardStyle}>
                        <span style={statIconStyle('#eff6ff', '#2563eb')}>
                            <FontAwesomeIcon icon={faGlobe} />
                        </span>
                        <div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a2e' }}>
                                {dashboardStats.uniqueIps}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Unique IP Addresses</div>
                        </div>
                    </div>
                    <div style={statCardStyle}>
                        <span style={statIconStyle('#fff7ed', '#ea580c')}>
                            <FontAwesomeIcon icon={faClock} />
                        </span>
                        <div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a2e' }}>
                                {formatLastActivity(dashboardStats.lastActivity)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Last Activity</div>
                        </div>
                    </div>
                    <div style={statCardStyle}>
                        <span style={statIconStyle('#f0fdf4', '#16a34a')}>
                            <FontAwesomeIcon icon={faUserPlus} />
                        </span>
                        <div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a2e' }}>
                                {conversionStats.totalSignups > 0 ? `${conversionStats.conversionRate.toFixed(1)}%` : '—'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                Signup → Login Conversion ({conversionStats.totalConverted}/{conversionStats.totalSignups})
                            </div>
                        </div>
                    </div>
                    <div style={statCardStyle}>
                        <span style={statIconStyle('#fffbeb', '#d97706')}>
                            <FontAwesomeIcon icon={faFileInvoiceDollar} />
                        </span>
                        <div>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a2e' }}>
                                {rechargeStats.totalOnboarded > 0 ? `${rechargeStats.rechargeRate.toFixed(1)}%` : '—'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                Onboarding → Recharge Conversion ({rechargeStats.totalRecharged}/{rechargeStats.totalOnboarded})
                            </div>
                        </div>
                    </div>
                </div> */}

                {/* Signup → Login only exists for new signups — there's no "All Users"
                    equivalent of a signup event, so this pair only shows on that tab.
                    The Time to Convert card reads from the same conversionStats data
                    (which was never actually scoped by this tab to begin with), so it's
                    shown on both tabs instead of being hidden on "All Users". */}
                {renderDataMismatchCard()}
                {renderSignupLoginTimeCard()}
                {funnelBreakdownTab === 'new' && renderConversionTable()}
                {funnelBreakdownTab === 'new' && renderNotConvertedTable()}
                {renderWhatsappOnboardedTable(
                    funnelBreakdownTab === 'new' ? newUserOnboardingStats : whatsappOnboardingStats,
                    funnelBreakdownTab === 'new' ? 'New Users' : 'All Users'
                )}
                {renderWhatsappNotOnboardedTable(
                    funnelBreakdownTab === 'new' ? newUserOnboardingStats : whatsappOnboardingStats,
                    funnelBreakdownTab === 'new' ? 'New Users' : 'All Users'
                )}
                {renderRechargedTable(
                    funnelBreakdownTab === 'new' ? newUserRechargeStats : rechargeStats,
                    funnelBreakdownTab === 'new' ? 'New Users' : 'All Users'
                )}
                {renderNotRechargedTable(
                    funnelBreakdownTab === 'new' ? newUserRechargeStats : rechargeStats,
                    funnelBreakdownTab === 'new' ? 'New Users' : 'All Users'
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '16px' }}>
                    <div style={dashboardCardStyle}>
                        <h6 style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '13px', marginBottom: '2px' }}>
                            Activity by Module
                        </h6>
                        <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px' }}>
                            Top modules by event count, for the selected date range
                        </p>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart
                                data={dashboardStats.moduleBreakdown}
                                margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid stroke="#e1e0d9" vertical={false} />
                                <XAxis
                                    type="category" dataKey="name"
                                    tick={{ fontSize: 11, fill: '#334155' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false}
                                />
                                <YAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={30} />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                                    formatter={(value) => [value, 'Events']}
                                />
                                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={36}>
                                    {dashboardStats.moduleBreakdown.map((entry, idx) => (
                                        <Cell key={entry.name || idx} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>

                        <div style={{
                            display: 'flex', flexWrap: 'wrap', gap: '8px',
                            marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f8',
                        }}>
                            {dashboardStats.moduleBreakdown.map((entry) => (
                                <span key={entry.name} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    fontSize: '11px', fontWeight: '600', color: entry.color,
                                    background: entry.bg, border: `1px solid ${entry.border}`,
                                    borderRadius: '20px', padding: '4px 10px 4px 6px',
                                }}>
                                    <span style={{
                                        width: '18px', height: '18px', borderRadius: '50%',
                                        background: '#fff', display: 'inline-flex',
                                        alignItems: 'center', justifyContent: 'center', fontSize: '9px',
                                    }}>
                                        <FontAwesomeIcon icon={entry.icon} />
                                    </span>
                                    {entry.name}
                                    <span style={{ color: '#94a3b8', fontWeight: '700' }}>{entry.count}</span>
                                </span>
                            ))}
                        </div>
                    </div>

                    <div style={dashboardCardStyle}>
                        <h6 style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '13px', marginBottom: '2px' }}>
                            Event Breakdown
                        </h6>
                        <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px' }}>
                            Exact counts per tracked event for the selected date range — e.g. how many logins or WhatsApp onboardings happened
                        </p>
                        <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                    {dashboardStats.rawModuleBreakdown.map(({ rawModule, count, style }) => (
                                        <tr key={rawModule} style={{ borderBottom: '1px solid #f0f0f8' }}>
                                            <td style={{ padding: '6px 4px', width: '24px' }}>
                                                <span style={{
                                                    width: '20px', height: '20px', borderRadius: '6px',
                                                    background: style.bg, color: style.color,
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '9px', border: `1px solid ${style.border}`,
                                                }}>
                                                    <FontAwesomeIcon icon={style.icon} />
                                                </span>
                                            </td>
                                            <td style={{ padding: '6px 8px', fontSize: '12px', color: '#334155' }}>
                                                {rawModule}
                                            </td>
                                            <td style={{ padding: '6px 4px', fontSize: '11px', color: '#94a3b8', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {count.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const navButtonStyle = (disabled) => ({
        height: '34px', padding: '0 14px', borderRadius: '8px',
        border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: '600',
        background: disabled ? '#f8fafc' : '#fff',
        color: disabled ? '#cbd5e1' : '#334155',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', gap: '6px',
        transition: 'background 0.15s, border-color 0.15s',
    });

    const iconButtonStyle = (disabled) => ({
        width: '34px', height: '34px', borderRadius: '8px',
        border: '1px solid #e2e8f0', fontSize: '11px',
        background: disabled ? '#f8fafc' : '#fff',
        color: disabled ? '#cbd5e1' : '#334155',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    });

    const renderPagination = () => {
        if (!logState.totalRecords) return null;
        const { currentPage, totalPages, totalRecords, pageSize } = logState;

        const pages = buildPageList(currentPage, totalPages);

        const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
        const rangeEnd = Math.min(currentPage * pageSize, totalRecords);

        return (
            <div style={{
                padding: '16px 24px', borderTop: '1px solid #eef0f6',
                background: '#fafbfd',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
            }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                    Showing <span style={{ color: '#1e293b', fontWeight: '700' }}>{rangeStart.toLocaleString()}</span>
                    {'–'}
                    <span style={{ color: '#1e293b', fontWeight: '700' }}>{rangeEnd.toLocaleString()}</span>
                    {' of '}
                    <span style={{ color: '#1e293b', fontWeight: '700' }}>{totalRecords.toLocaleString()}</span>
                    {' records'}
                </span>

                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <button
                        title="First page"
                        onClick={() => handlePageChange(1)}
                        disabled={!logState.hasPrevPage}
                        style={iconButtonStyle(!logState.hasPrevPage)}
                    >
                        <FontAwesomeIcon icon={faAngleDoubleLeft} />
                    </button>
                    <button
                        onClick={() => handlePageChange(logState.currentPage - 1)}
                        disabled={!logState.hasPrevPage}
                        style={navButtonStyle(!logState.hasPrevPage)}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                        Prev
                    </button>

                    <div style={{ display: 'flex', gap: '4px', margin: '0 2px' }}>
                        {pages.map((p, idx) =>
                            p === '...' ? (
                                <span key={'e' + idx} style={{ padding: '0 4px', color: '#94a3b8', fontSize: '13px', alignSelf: 'center' }}>…</span>
                            ) : (
                                <button
                                    key={p}
                                    onClick={() => handlePageChange(p)}
                                    aria-current={currentPage === p ? 'page' : undefined}
                                    style={{
                                        minWidth: '34px', height: '34px', borderRadius: '8px', fontSize: '12px',
                                        border: '1px solid', fontWeight: currentPage === p ? '700' : '500',
                                        borderColor: currentPage === p ? '#4f46e5' : '#e2e8f0',
                                        background: currentPage === p ? '#4f46e5' : '#fff',
                                        color: currentPage === p ? '#fff' : '#475569',
                                        boxShadow: currentPage === p ? '0 2px 6px rgba(79,70,229,0.35)' : 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {p}
                                </button>
                            )
                        )}
                    </div>

                    <button
                        onClick={() => handlePageChange(logState.currentPage + 1)}
                        disabled={!logState.hasNextPage}
                        style={navButtonStyle(!logState.hasNextPage)}
                    >
                        Next
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>

                    <button
                        title="Last page"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={!logState.hasNextPage}
                        style={iconButtonStyle(!logState.hasNextPage)}
                    >
                        <FontAwesomeIcon icon={faAngleDoubleRight} />
                    </button>
                </div>
            </div>
        );
    };

    // Pagination + rows-per-page control for the per-module cards grid — separate from
    // renderPagination() above since the cards grid is fed by its own fetchModuleCards
    // call/page state, not logState.
    const renderModuleCardsPagination = () => {
        if (!moduleCardsState.totalRecords) return null;
        const { currentPage, totalPages, totalRecords } = moduleCardsState;

        const pages = buildPageList(currentPage, totalPages);
        const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * moduleCardsLimit + 1;
        const rangeEnd = Math.min(currentPage * moduleCardsLimit, totalRecords);

        return (
            <div style={{
                padding: '16px 20px', borderTop: '1px solid #eef0f6',
                background: '#fafbfd', marginTop: '4px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
            }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                    Showing <span style={{ color: '#1e293b', fontWeight: '700' }}>{rangeStart.toLocaleString()}</span>
                    {'–'}
                    <span style={{ color: '#1e293b', fontWeight: '700' }}>{rangeEnd.toLocaleString()}</span>
                    {' of '}
                    <span style={{ color: '#1e293b', fontWeight: '700' }}>{totalRecords.toLocaleString()}</span>
                    {' module records'}
                </span>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <label htmlFor="moduleCardsLimit" style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                            Rows per module
                        </label>
                        <select
                            id="moduleCardsLimit"
                            value={moduleCardsLimit}
                            onChange={(e) => handleModuleCardsLimitChange(Number(e.target.value))}
                            className="form-control"
                            style={{ width: '76px', height: '32px', fontSize: '12px', padding: '2px 6px' }}
                        >
                            {MODULE_CARDS_LIMIT_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button
                            title="First page"
                            onClick={() => handleModuleCardsPageChange(1)}
                            disabled={currentPage <= 1}
                            style={iconButtonStyle(currentPage <= 1)}
                        >
                            <FontAwesomeIcon icon={faAngleDoubleLeft} />
                        </button>
                        <button
                            onClick={() => handleModuleCardsPageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                            style={navButtonStyle(currentPage <= 1)}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                            Prev
                        </button>

                        <div style={{ display: 'flex', gap: '4px', margin: '0 2px' }}>
                            {pages.map((p, idx) =>
                                p === '...' ? (
                                    <span key={'e' + idx} style={{ padding: '0 4px', color: '#94a3b8', fontSize: '13px', alignSelf: 'center' }}>…</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => handleModuleCardsPageChange(p)}
                                        aria-current={currentPage === p ? 'page' : undefined}
                                        style={{
                                            minWidth: '34px', height: '34px', borderRadius: '8px', fontSize: '12px',
                                            border: '1px solid', fontWeight: currentPage === p ? '700' : '500',
                                            borderColor: currentPage === p ? '#4f46e5' : '#e2e8f0',
                                            background: currentPage === p ? '#4f46e5' : '#fff',
                                            color: currentPage === p ? '#fff' : '#475569',
                                            boxShadow: currentPage === p ? '0 2px 6px rgba(79,70,229,0.35)' : 'none',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {p}
                                    </button>
                                )
                            )}
                        </div>

                        <button
                            onClick={() => handleModuleCardsPageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                            style={navButtonStyle(currentPage >= totalPages)}
                        >
                            Next
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>

                        <button
                            title="Last page"
                            onClick={() => handleModuleCardsPageChange(totalPages)}
                            disabled={currentPage >= totalPages}
                            style={iconButtonStyle(currentPage >= totalPages)}
                        >
                            <FontAwesomeIcon icon={faAngleDoubleRight} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Every raw Billing & Payments row (billing_history, transaction_history,
    // transaction_payment, ...) across the FULL selected date range — unlike
    // groupedByModule below (which only covers the current table page), this
    // pulls from filteredRangeData so nothing gets clipped by pagination.
    // Most recent first, and carries every field the API sends for a row
    // (including user_type/email, which the compact per-module table doesn't show).
    const transactionPaymentRows = useMemo(() => {
        return filteredRangeData
            .filter((row) => getModuleStyle(row.module).queryValue === 'billing')
            .slice()
            .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }, [filteredRangeData]);

    const renderTransactionPaymentTable = () => {
        const rows = transactionPaymentRows;
        const style = MODULE_STYLES.find((m) => m.queryValue === 'billing');
        return (
            <div style={{
                background: '#fff', borderRadius: '12px', border: `1px solid ${style.border}`,
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden', marginTop: '16px',
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: `1px solid ${style.border}`,
                    background: style.bg, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: style.color, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: `1px solid ${style.border}`,
                    }}>
                        <FontAwesomeIcon icon={style.icon} />
                    </span>
                    <div>
                        <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: style.color }}>
                            Transaction & Payment Records
                        </h6>
                        <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#94a3b8' }}>
                            Every Billing & Payments event across {formatRangeLabel()} — recharges, transaction history views, and completed payments
                        </p>
                    </div>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: style.color,
                        background: '#fff', border: `1px solid ${style.border}`,
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto', whiteSpace: 'nowrap',
                    }}>
                        {rows.length} {rows.length === 1 ? 'record' : 'records'}
                    </span>
                </div>

                <div style={{ maxHeight: '480px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {['Date & Time', 'Username / Email', 'User ID', 'User Type', 'Added By', 'Event', 'Message', 'Page', 'IP Address', 'Journey'].map((h) => (
                                    <th key={h} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={10} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: '12px' }}>
                                        No transaction or payment activity found for the selected filters.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row, i) => {
                                    const [datePart, timePart] = (row.created_at || '').split(' ');
                                    return (
                                        <tr key={`${row.user_id || row.username || 'row'}-${row.created_at}-${i}`} style={{
                                            background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                            borderBottom: '1px solid #f0f0f8',
                                        }}>
                                            <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontSize: '12px', color: '#1e293b', fontWeight: '600' }}>{datePart || '-'}</div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{timePart || ''}</div>
                                            </td>
                                            <td style={{
                                                padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500',
                                                maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }} title={row.email || row.username || ''}>
                                                {row.email || row.username || '-'}
                                            </td>
                                            <td style={{ padding: '11px 14px', fontSize: '11px', color: '#64748b', fontFamily: 'Consolas, monospace' }}>
                                                {row.user_id ?? '-'}
                                            </td>
                                            <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155' }}>
                                                {row.user_type || '-'}
                                            </td>
                                            <td style={{
                                                padding: '11px 14px', fontSize: '12px', color: '#334155',
                                                maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }} title={row.added_by || ''}>
                                                {row.added_by || '-'}
                                            </td>
                                            <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500', whiteSpace: 'nowrap' }}>
                                                {row.event || '-'}
                                            </td>
                                            <td style={{
                                                padding: '11px 14px', fontSize: '12px', color: '#64748b',
                                                maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }} title={row.message || ''}>
                                                {row.message || '-'}
                                            </td>
                                            <td style={{
                                                padding: '11px 14px', fontSize: '12px', color: '#64748b',
                                                maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }} title={row.page || ''}>
                                                {row.page || '-'}
                                            </td>
                                            <td style={{ padding: '11px 14px' }}>
                                                <span style={{
                                                    fontSize: '11px', color: '#334155', fontWeight: '600',
                                                    fontFamily: 'Consolas, monospace', display: 'inline-flex',
                                                    alignItems: 'center', gap: '5px',
                                                    background: '#f0f0f8', padding: '3px 8px', borderRadius: '6px',
                                                }}>
                                                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '9px', color: '#94a3b8' }} />
                                                    {row.ip || '-'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '11px 14px' }}>
                                                {renderJourneyButton(row.user_id)}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Cards grid data comes from the dedicated fetchModuleCards call (module: 'all',
    // event: 'all', its own page/limit), not the main table's single-page fetchLog
    // data — searchText still narrows it client-side, same convention as filteredData.
    const moduleCardsFilteredData = useMemo(() => {
        if (!searchText) return moduleCardsState.data;
        const needle = searchText.toLowerCase();
        return moduleCardsState.data.filter((row) =>
            ['module', 'event', 'message', 'page', 'user_id', 'username', 'ip', 'added_by']
                .some((key) => (row[key] || '').toString().toLowerCase().includes(needle))
        );
    }, [moduleCardsState.data, searchText]);

    // Rows that don't match any known MODULE_STYLES pattern used to all get merged into
    // one shared "Other" table. Instead, key each unmatched raw module on its own so it
    // renders as its own separate table, labeled with its raw module string as-is.
    const groupedByModule = useMemo(() => {
        const groups = new Map();
        const otherKeys = [];
        moduleCardsFilteredData.forEach((row) => {
            const style = getModuleStyle(row.module);
            const isOther = style === DEFAULT_MODULE_STYLE;
            // Case-insensitive key so raw casing variants of the same unmatched module
            // (the API logs e.g. "signup" and "Signup" separately) share one table.
            const key = isOther ? `other:${(row.module || 'Unknown').toLowerCase()}` : style.label;
            if (!groups.has(key)) {
                groups.set(key, {
                    style: isOther ? { ...DEFAULT_MODULE_STYLE, label: row.module || 'Unknown' } : style,
                    rows: [],
                });
                if (isOther) otherKeys.push(key);
            }
            groups.get(key).rows.push(row);
        });

        const orderedKeys = [...MODULE_STYLES.map((m) => m.label), ...otherKeys];
        return orderedKeys.map((key) => groups.get(key)).filter(Boolean);
    }, [moduleCardsFilteredData]);

    const renderModuleTable = (group) => {
        const { style, rows } = group;
        // The signup event carries a signup-source field (`added_by`) that other
        // modules don't set, so it gets its own visible column only on this table.
        const isSignupTable = style.label === 'Signup';
        const columnCount = isSignupTable ? 8 : 7;
        return (
            <div key={style.label} style={{
                background: '#fff', borderRadius: '12px', border: '1px solid #e8e8f0',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden',
                display: 'flex', flexDirection: 'column', minWidth: 0,
            }}>
                <div style={{
                    padding: '12px 20px', borderBottom: `1px solid ${style.border}`,
                    background: style.bg, display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                        background: '#fff', color: style.color, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        border: `1px solid ${style.border}`,
                    }}>
                        <FontAwesomeIcon icon={style.icon} />
                    </span>
                    <h6 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: style.color }}>
                        {style.label}
                    </h6>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: style.color,
                        background: '#fff', border: `1px solid ${style.border}`,
                        borderRadius: '20px', padding: '2px 10px', marginLeft: 'auto',
                    }}>
                        {rows.length} {rows.length === 1 ? 'record' : 'records'}
                    </span>
                </div>

                <div style={{ maxHeight: '420px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fafbfd' }}>
                                {(isSignupTable
                                    ? ['Date & Time', 'Username', 'Event', 'Added By', 'Message', 'IP Address', 'Journey', '']
                                    : ['Date & Time', 'Username', 'Event', 'Message', 'IP Address', 'Journey', '']
                                ).map((h, i) => (
                                    <th key={i} style={{
                                        padding: '10px 14px', fontSize: '10px', fontWeight: '700',
                                        color: '#94a3b8', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                        borderBottom: '1px solid #eef0f6',
                                        position: 'sticky', top: 0, background: '#fafbfd', zIndex: 1,
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => {
                                const rowKey = `${style.label}-${i}`;
                                const isExpanded = expandedRow === rowKey;
                                const [datePart, timePart] = (row.created_at || '').split(' ');
                                return (
                                    <React.Fragment key={rowKey}>
                                        <tr style={{
                                            background: i % 2 === 0 ? '#fafbfd' : '#fff',
                                            borderBottom: '1px solid #f0f0f8',
                                        }}>
                                            <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                                                <div style={{ fontSize: '12px', color: '#1e293b', fontWeight: '600' }}>{datePart || '-'}</div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{timePart || ''}</div>
                                            </td>
                                            <td style={{
                                                padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500',
                                                maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }} title={row.username || ''}>
                                                {row.username || '-'}
                                            </td>
                                            <td style={{ padding: '11px 14px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                                                {row.event || '-'}
                                            </td>
                                            {isSignupTable && (
                                                <td style={{
                                                    padding: '11px 14px', fontSize: '12px', color: '#334155',
                                                    maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }} title={row.added_by || ''}>
                                                    {row.added_by || '-'}
                                                </td>
                                            )}
                                            <td style={{
                                                padding: '11px 14px', fontSize: '12px', color: '#64748b',
                                                maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }} title={row.message || ''}>
                                                {row.message || '-'}
                                            </td>
                                            <td style={{ padding: '11px 14px' }}>
                                                <span style={{
                                                    fontSize: '11px', color: '#334155', fontWeight: '600',
                                                    fontFamily: 'Consolas, monospace', display: 'inline-flex',
                                                    alignItems: 'center', gap: '5px',
                                                    background: '#f0f0f8', padding: '3px 8px', borderRadius: '6px',
                                                }}>
                                                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '9px', color: '#94a3b8' }} />
                                                    {row.ip || '-'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '11px 14px' }}>
                                                {renderJourneyButton(row.user_id)}
                                            </td>
                                            <td style={{ padding: '11px 14px' }}>
                                                <button
                                                    onClick={() => setExpandedRow(isExpanded ? null : rowKey)}
                                                    title="View raw details"
                                                    style={{
                                                        border: 'none', background: 'transparent', cursor: 'pointer',
                                                        color: '#94a3b8', padding: '4px',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                                </button>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr style={{ background: '#fafaff' }}>
                                                <td colSpan={columnCount} style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f8' }}>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 20px', fontSize: '12px' }}>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600' }}>Message: </span>
                                                            <span style={{ color: '#334155' }}>{row.message || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600' }}>Page: </span>
                                                            <span style={{ color: '#334155' }}>{row.page || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600' }}>Username: </span>
                                                            <span style={{ color: '#334155' }}>{row.username || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600' }}>User ID: </span>
                                                            <span style={{ fontFamily: 'Consolas, monospace', color: '#334155' }}>{row.user_id ?? '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600' }}>Module (raw): </span>
                                                            <span style={{ fontFamily: 'Consolas, monospace', color: '#334155' }}>{row.module || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#94a3b8', fontWeight: '600' }}>Added By: </span>
                                                            <span style={{ color: '#334155' }}>{row.added_by || '-'}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Cards grid (Voice & TTS / RCS / Billing & Payments / ...) — its own loading/empty
    // state, since it's fed by fetchModuleCards rather than the main fetchLog page.
    const renderModuleCardsSection = () => {
        if (moduleCardsState.loading) {
            return (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                    <span className="spinner-border spinner-border-sm mr-2" /> Loading module activity...
                </div>
            );
        }

        if (moduleCardsFilteredData.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '13px' }}>
                    {searchText ? 'No module activity matches your search.' : 'No module activity found for the selected date range.'}
                </div>
            );
        }

        return (
            <>
                <div style={{
                    padding: '16px 20px 0', display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '16px',
                }}>
                    {groupedByModule.map(renderModuleTable)}
                </div>

                {renderModuleCardsPagination()}
            </>
        );
    };

    const renderTable = () => {
        if (loading) {
            return (
                <>
                    {renderModuleCardsSection()}
                    <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                        <span className="spinner-border spinner-border-sm mr-2" /> Loading...
                    </div>
                </>
            );
        }

        if (filteredData.length === 0) {
            return (
                <>
                    {renderModuleCardsSection()}
                    <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: '14px' }}>
                        {searchText ? 'No records match your search.' : 'No activity log records found for the selected filters.'}
                    </div>
                </>
            );
        }

        return (
            <>
                {renderModuleCardsSection()}

                <div style={{ padding: '0 20px' }}>
                    {renderTransactionPaymentTable()}
                </div>

                {renderPagination()}
            </>
        );
    };

    // Single full-page overlay shown for the whole duration data is loading —
    // main table, dashboard range stats, and the module cards grid all set
    // isPageLoading, so the admin gets one obvious "the page is busy" signal
    // instead of hunting for whichever section's own small inline spinner fired.
    const renderPageLoader = () => {
        if (!isPageLoading) return null;
        return (
            <div style={{
                position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.72)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '14px', zIndex: 1040, backdropFilter: 'blur(1px)',
            }}>
                <span
                    className="spinner-border"
                    role="status"
                    style={{ width: '2.75rem', height: '2.75rem', color: '#4f46e5' }}
                />
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>
                    Loading activity data…
                </span>
            </div>
        );
    };

    return (
        <AdminLayout>
            {/* AdminLayout's own <main className="content"> already applies the shared
                side padding (2.5rem at desktop) — this used to render a second, nested
                main.content here, doubling that padding and squeezing every table/card
                on this page. Plain div keeps the same vertical rhythm without stacking
                a second side gutter on top of the layout's. */}
            <div style={{ paddingBottom: '0.75rem' }}>
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '20px' }}>
                        Activity Log
                    </h4>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
                        Track and review actions performed across the platform.
                    </p>
                </div>

                <div className="card" style={{ borderRadius: '12px', border: '1px solid #e8e8f0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                    <div className="card-body">
                        <h6 style={{ fontWeight: '700', color: '#1a1a2e', marginBottom: '16px', fontSize: '14px' }}>
                            <FontAwesomeIcon icon={faSearch} style={{ marginRight: '8px', color: '#4f46e5' }} />
                            Filters
                        </h6>

                        {error.status && (
                            <div className="alert alert-danger" role="alert" style={{ marginBottom: '12px' }}>
                                <button type="button" className="close" onClick={() => setError({ status: false })} aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                {error.message}
                            </div>
                        )}

                        <div className="row g-3 align-items-end">
                            <div className="col-6 col-lg-2">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px', color: '#6b7280' }} />
                                    From Date
                                </label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={handleFromDateChange}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    maxDate={endDate || undefined}
                                    isClearable={false}
                                    placeholderText="From date"
                                    dateFormat="dd MMM yyyy"
                                    className="form-control"
                                />
                            </div>

                            <div className="col-6 col-lg-2">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px', color: '#6b7280' }} />
                                    To Date
                                </label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={handleToDateChange}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate || undefined}
                                    isClearable={false}
                                    placeholderText="To date"
                                    dateFormat="dd MMM yyyy"
                                    className="form-control"
                                />
                            </div>

                            <div className="col-12 col-lg-3">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '5px', color: '#6b7280' }} />
                                    Module
                                </label>
                                <Select
                                    value={selectedModule || MODULE_FILTER_OPTIONS[0]}
                                    onChange={handleModuleChange}
                                    options={MODULE_FILTER_OPTIONS}
                                    styles={customStyles}
                                    isSearchable={false}
                                    menuPortalTarget={document.body}
                                />
                            </div>

                            <div className="col-12 col-lg-3">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    <FontAwesomeIcon icon={faListUl} style={{ marginRight: '5px', color: '#6b7280' }} />
                                    Event
                                </label>
                                <Select
                                    value={selectedEvent || eventOptions[0]}
                                    onChange={handleEventChange}
                                    options={eventOptions}
                                    styles={customStyles}
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    placeholder="All Events"
                                />
                            </div>

                            <div className="col-12 col-lg-2">
                                <button
                                    className="btn btn-primary w-100"
                                    onClick={() => handleSearch()}
                                    disabled={loading}
                                    style={{ fontWeight: '600', fontSize: '13px' }}
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
                                            Fetch Log
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="row g-3 align-items-end" style={{ marginTop: '4px' }}>
                            <div className="col-12 col-lg-8">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    Quick Range
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {DATE_PRESETS.map((preset) => (
                                        <button
                                            key={preset.key}
                                            type="button"
                                            onClick={() => applyDatePreset(preset)}
                                            style={{
                                                height: '32px', padding: '0 12px', borderRadius: '20px',
                                                fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                                                border: '1px solid', borderColor: activePresetKey === preset.key ? '#4f46e5' : '#e2e8f0',
                                                background: activePresetKey === preset.key ? '#4f46e5' : '#fff',
                                                color: activePresetKey === preset.key ? '#fff' : '#475569',
                                            }}
                                        >
                                            {preset.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="col-12 col-lg-4">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    Search within results
                                </label>
                                <div className="input-group">
                                    <div className="input-group-text">
                                        <FontAwesomeIcon icon={faSearch} style={{ color: '#6b7280' }} />
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Filter by module, event, message..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {rangeStats.loading && (
                            <div style={{ marginTop: '10px', fontSize: '11px', color: '#94a3b8' }}>
                                <span className="spinner-border spinner-border-sm" style={{ width: '11px', height: '11px', marginRight: '5px' }} />
                                Updating range stats...
                            </div>
                        )}
                    </div>
                </div>

                {renderDashboard()}

                <div ref={activityTableRef} style={{
                    background: '#fff', borderRadius: '12px',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                    border: '1px solid #e8e8f0',
                    marginTop: '20px', overflow: 'hidden',
                    fontFamily: 'Inter, system-ui, sans-serif',
                }}>
                    <div style={{
                        padding: '14px 24px', borderBottom: '1px solid #f0f0f8',
                        display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                    }}>
                        <span style={{
                            width: '34px', height: '34px', borderRadius: '8px',
                            background: '#eef2ff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FontAwesomeIcon icon={faHistory} style={{ color: '#4f46e5', fontSize: '14px' }} />
                        </span>
                        <div>
                            <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1a1a2e' }}>
                                Activity Log
                            </h6>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                {logState.totalRecords > 0
                                    ? `${logState.totalRecords.toLocaleString()} total records`
                                    : 'No data loaded'}
                            </span>
                        </div>

                        {searched && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginLeft: 'auto' }}>
                                <span style={filterChipStyle}>
                                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px' }} />
                                    {formatRangeLabel()}
                                </span>
                                <span style={filterChipStyle}>
                                    <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '5px' }} />
                                    {selectedModule ? selectedModule.label : 'All Modules'}
                                </span>
                                <span style={filterChipStyle}>
                                    <FontAwesomeIcon icon={faListUl} style={{ marginRight: '5px' }} />
                                    {selectedEvent ? selectedEvent.label : 'All Events'}
                                </span>
                            </div>
                        )}
                    </div>

                    {renderTable()}
                </div>
            </div>
            {renderPageLoader()}
            {renderStagePreviewModal()}
            {renderReasonPreviewModal()}
            {renderUserAttemptsPreviewModal()}
            {renderSignupDetailModal()}
        </AdminLayout>
    );
};

export default Useractivitylog;
